import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { useNavigate, useLocation, Outlet, Routes, Route, Link as Link$1 } from "react-router";
import { ReactiveCore, replaceRows, DexieDatabaseProvider, useSuccessReactor } from "@livefree/reactive";
import { MqlRequestBuilder, RequestMetadata, dexieRequestBrokerAddon, RequestBrokerDataGrid, RequestBroker, Sorting } from "@livefree/request-broker";
import { EnvironmentConfigurations } from "@livefree/runtime";
import { objectMap, isEmpty, BlobUtils, pascalCase, truncate, isNumber, parseAndFormatDate, sortBy, values, groupBy, parseAndFormatDateTime, xor, fromISO, DateTime, toISO } from "@livefree/common";
import { App, useRecentView, useConfig, useUser, RequirePolicy } from "@livefree/applications";
import { useDeepCompareEffect, Params, WithRouteParams } from "@livefree/react";
import React, { createContext, useState, useRef, createRef, useCallback, useMemo, useContext, forwardRef, useImperativeHandle, useReducer, useEffect } from "react";
import { TextField, Label, InputGroup, ListBox, Chip, Spinner, FieldError, Dropdown, Link, Button, Avatar, Breadcrumbs, BreadcrumbsItem, ProgressBar, Separator, Alert, Card, Tabs, Tooltip, Modal, Table, Select, Checkbox, CloseButton, ScrollShadow, Accordion } from "@heroui/react";
import { Uniformly, FormField, TextField as TextField$1, SelectField, AutocompleteField, ArrayField, SwitchField, UrlField, DictionaryField, ExtendedSwitchField, ImageInputField, TextAreaField, TimeCalculatorField, DateField, EmailField, PasswordConfirmField, DateTimeField } from "@livefree/uniformly";
import { CopyToClipboard, keyPressedHandler, ModifyButton, withDrawerController, ErrorListChip, SaveButton, NotFound, Loading, SplitCard, FlatCard, DeleteButton, NewButton, useDrawerController, ReloadButton, TextFieldWithCopy, MultiActionButton, DrawerActions, withModalController, ResourceActions, useModalController, Toggle, Forbidden } from "@livefree/react-ui";
import { Filters, FilterInput, FilterConstant, FilterToggle, FilterSelect, HorizontalFilters, FilterDateRange } from "@livefree/filters";
import { Reorder, useMotionValue, useDragControls } from "motion/react";
import { RiCircleFill } from "@remixicon/react";
import { Resizable } from "@heroui-pro/react/resizable";
const reactive = new ReactiveCore("auth-app");
const AuthConfig = EnvironmentConfigurations.hydrate({
  development: {
    host: "https://localhost:9002",
    api: {
      host: "https://lf-login-staging.azurewebsites.net",
      apiKey: "LoginAPI"
    }
  },
  staging: {
    host: "https://lf-artemis-staging.azurewebsites.net",
    api: {
      host: "https://lf-login-staging.azurewebsites.net",
      apiKey: "LoginAPI"
    }
  },
  production: {
    host: [
      "https://artemis.lifebeacon.com",
      "https://app.alert1.com",
      "https://app.lifebeacon.com"
    ],
    api: {
      host: "https://login.lifebeacon.com",
      apiKey: "LoginAPI"
    }
  }
});
class ApiResourcesClient extends LoginSDK.ApiResourcesClient {
  queryApiResources(metadata) {
    return metadata ? super.queryApiResources(MqlRequestBuilder.create(metadata).build("ApiResourceModel")) : super.queryApiResources("FETCH ApiResourceModel;");
  }
}
const ApiResourcesService = reactive.service("apiResources", ApiResourcesClient, {
  recursive: true,
  recurseDepth: 2
})((Service) => new Service(AuthConfig));
ApiResourcesService.queryApiResources.latest();
ApiResourcesService.reactor("apiResourcesReactor", {
  success: (response, _, db) => {
    const { source, args, payload } = response;
    switch (source) {
      case ApiResourcesService.getApiResource:
        return db.apiResources.put(payload);
      case ApiResourcesService.createApiResource:
        return db.apiResources.add(payload);
      case ApiResourcesService.updateApiResource:
        return db.apiResources.put(payload);
      case ApiResourcesService.deleteApiResource:
        return db.apiResources.delete(args[0]);
      // Its own store: `apiResourceScopesLookup` holds what ApiScopesService.getApiScopes answered
      // (the scope picker reads it whole), and this result must not replace those rows.
      case ApiResourcesService.getApiResourceScopes:
        return replaceRows(db, db.apiResourceScopes, payload);
      case ApiResourcesService.apiResourceOperation:
        if (args[0] === "unlock") db.apiResources.put(payload);
        break;
      case ApiResourcesService.queryApiResources:
        RequestMetadata.setCount(source.action, payload.totalCount);
        return replaceRows(db, db.apiResources, payload.values);
    }
  }
});
ApiResourcesService.reactor("apiResourcesMessageReactor", {
  success: (response, services) => {
    const { source } = response;
    switch (source) {
      case ApiResourcesService.createApiResource:
      case ApiResourcesService.updateApiResource:
      case ApiResourcesService.deleteApiResource:
        return appRuntime.services.alerts.success("Success");
    }
  },
  error: (response, services) => {
    return appRuntime.services.alerts.error(response.payload?.message ?? "Error");
  }
});
class IdentityResourcesClient extends LoginSDK.IdentityResourcesClient {
  queryIdentityResources(metadata) {
    return metadata ? super.queryIdentityResources(
      MqlRequestBuilder.create(metadata).build("IdentityResourceModel")
    ) : super.queryIdentityResources("FETCH IdentityResourceModel;");
  }
}
const IdentityResourcesService = reactive.service("identityResources", IdentityResourcesClient, {
  recursive: true,
  recurseDepth: 2
})((Service) => new Service(AuthConfig));
const populateModel = (db, payload) => {
  const ofResource = (table) => table.where("identityResourceId").equals(payload.id);
  return Promise.all([
    replaceRows(
      db,
      db.identityResourceUserClaims,
      payload.userClaims.map((claim) => ({ identityResourceId: payload.id, value: claim })),
      { where: ofResource }
    ),
    replaceRows(
      db,
      db.identityResourceProperties,
      [{ identityResourceId: payload.id, value: payload.properties }],
      { where: ofResource }
    )
  ]);
};
IdentityResourcesService.queryIdentityResources.latest();
IdentityResourcesService.reactor("identityResourcesReactor", {
  success: (response, _, db) => {
    const { source, args, payload } = response;
    switch (source) {
      case IdentityResourcesService.queryIdentityResources:
        RequestMetadata.setCount(source.action, payload.totalCount);
        return replaceRows(db, db.identityResources, payload.values);
      case IdentityResourcesService.getIdentityResource:
        return Promise.all([populateModel(db, payload), db.identityResources.put(payload)]);
      case IdentityResourcesService.createIdentityResource:
        return Promise.all([populateModel(db, payload), db.identityResources.add(payload)]);
      case IdentityResourcesService.updateIdentityResource:
        return Promise.all([populateModel(db, payload), db.identityResources.put(payload)]);
      case IdentityResourcesService.deleteIdentityResource:
        db.identityResourceUserClaims.where("identityResourceId").equals(args[0]).delete();
        db.identityResourceProperties.where("identityResourceId").equals(args[0]).delete();
        return db.identityResources.delete(args[0]);
      case IdentityResourcesService.identityResourceOperation:
        if (args[0] === "unlock") {
          db.identityResources.put(payload);
        }
        break;
    }
  }
});
IdentityResourcesService.reactor("identityResourcesMessageReactor", {
  success: (response, services) => {
    const { source } = response;
    switch (source) {
      case IdentityResourcesService.createIdentityResource:
      case IdentityResourcesService.updateIdentityResource:
      case IdentityResourcesService.deleteIdentityResource:
        return appRuntime.services.alerts.success("Success");
    }
  },
  error: (response, services) => {
    return appRuntime.services.alerts.error(response.payload?.message ?? "Error");
  }
});
class ClientsClient extends LoginSDK.ClientsClient {
  queryClients(metadata) {
    return metadata ? super.queryClients(MqlRequestBuilder.create(metadata).build("ClientModel")) : super.queryClients("FETCH ClientModel;");
  }
  getClientsByApplication(metadata) {
    return metadata ? super.queryClients(MqlRequestBuilder.create(metadata).build("ClientModel")) : super.queryClients("FETCH ClientModel;");
  }
}
const ClientsService = reactive.service("clients", ClientsClient, {
  recursive: true,
  recurseDepth: 2
})((Service) => new Service(AuthConfig));
ClientsService.queryClients.latest();
ClientsService.reactor("clientsReactor", {
  success: (response, _, db) => {
    const { source, args, payload } = response;
    switch (source) {
      case ClientsService.queryClients:
        RequestMetadata.setCount(source.action, payload.totalCount);
        return replaceRows(db, db.clients, payload.values);
      case ClientsService.getClientsByApplication:
        RequestMetadata.setCount(source.action, payload.totalCount);
        return db.clients.bulkPut(payload.values);
      case ClientsService.getClient:
        return Promise.all([
          replaceRows(
            db,
            db.clientSecrets,
            [...payload.clientSecrets].map((x) => ({ ...x, clientId: payload.id }))
          ),
          db.clients.put(payload)
        ]);
      case ClientsService.createClient:
        return db.clients.add(payload);
      case ClientsService.updateClient:
        return db.clients.put(payload);
      case ClientsService.deleteClient:
        return db.clients.delete(args[0]);
      case ClientsService.clientOperation:
        return db.clients.put(payload);
    }
  }
});
ClientsService.reactor("clientsMessageReactor", {
  success: (response, services) => {
    switch (response.source) {
      case ClientsService.createClient:
      case ClientsService.updateClient:
      case ClientsService.deleteClient:
        return appRuntime.services.alerts.success("Success");
    }
  },
  error: (response, services) => {
    return appRuntime.services.alerts.error(response.payload?.message ?? "Error");
  }
});
const ensureNull = (client) => {
  const nullProps = [
    "pairWiseSubjectSalt",
    "userCodeType",
    "frontChannelLogoutUri",
    "backChannelLogoutUri",
    "consentLifetime",
    "userSsoLifetime"
  ];
  return objectMap(
    client,
    (val, key) => nullProps.includes(key) && isEmpty(val) ? null : client[key]
  );
};
const transformClientLogo = async (data) => {
  if (data.logo && data.logo instanceof Blob) {
    data.logo = await BlobUtils.toBase64(data.logo);
  }
};
ClientsService.updateClient.interceptor("pre", async (args) => {
  args[1] = ensureNull(args[1]);
  await transformClientLogo(args[1]);
});
ClientsService.createClient.interceptor("pre", async (args) => {
  args[0] = ensureNull(args[0]);
  await transformClientLogo(args[0]);
});
const ClientSecretsService = reactive.service(
  "clientSecrets",
  LoginSDK.ClientSecretsClient
)((Service) => new Service(AuthConfig));
ClientSecretsService.reactor("clientSecretsReactor", {
  success: (response, services, db) => {
    const { source, args, payload } = response;
    switch (source) {
      case ClientSecretsService.createClientSecret:
        appRuntime.services.alerts.success("Success");
        return db.clientSecrets.add(payload);
      case ClientSecretsService.deleteClientSecret:
        appRuntime.services.alerts.success("Success");
        return db.clientSecrets.delete(args[1]);
    }
  },
  error: (response, services) => {
    return appRuntime.services.alerts.error(response.payload?.message ?? "Error");
  }
});
const AccessTokenType = Object.freeze({
  Jwt: 0,
  // Self-contained Json Web Token
  Reference: 1
  // Absolute token expiration
});
const AccessTokenDisplay = Object.freeze({
  [AccessTokenType.Jwt]: "Jwt",
  [AccessTokenType.Reference]: "Reference"
});
const AccessTokenTypeOptions = Object.freeze([
  {
    value: AccessTokenType.Jwt,
    label: AccessTokenDisplay[AccessTokenType.Jwt],
    description: "Self-contained Json Web Token"
  },
  {
    value: AccessTokenType.Reference,
    label: AccessTokenDisplay[AccessTokenType.Reference],
    description: "Absolute token expiration"
  }
]);
const TokenExpirationType = Object.freeze({
  Sliding: 0,
  // When refreshing the token, the lifetime of the refresh token will be renewed (by the amount specified in SlidingRefreshTokenLifetime). The lifetime will not exceed AbsoluteRefreshTokenLifetime.
  Absolute: 1
  // The refresh token will expire on a fixed point in time (specified by the AbsoluteRefreshTokenLifetime)
});
const TokenExpirationDisplay = Object.freeze({
  [TokenExpirationType.Sliding]: "Sliding",
  [TokenExpirationType.Absolute]: "Absolute"
});
const TokenExpirationTypeOptions = Object.freeze([
  {
    value: TokenExpirationType.Sliding,
    label: TokenExpirationDisplay[TokenExpirationType.Sliding],
    description: "When refreshing the token, the lifetime of the refresh token will be renewed (by the amount specified in SlidingRefreshTokenLifetime). The lifetime will not exceed AbsoluteRefreshTokenLifetime."
  },
  {
    value: TokenExpirationType.Absolute,
    label: TokenExpirationDisplay[TokenExpirationType.Absolute],
    description: "The refresh token will expire on a fixed point in time (specified by the AbsoluteRefreshTokenLifetime)"
  }
]);
const TokenUsageType = Object.freeze({
  ReUse: 0,
  // The refresh token handle will stay the same when refreshing tokens
  OneTimeOnly: 1
  // The refresh token handle will be updated when refreshing tokens
});
const TokenUsageTypeDisplay = Object.freeze({
  [TokenUsageType.ReUse]: "ReUse",
  [TokenUsageType.OneTimeOnly]: "OneTimeOnly"
});
const TokenUsageTypeOptions = Object.freeze([
  {
    value: TokenUsageType.ReUse,
    label: TokenUsageTypeDisplay[TokenUsageType.ReUse],
    description: "The refresh token handle will stay the same when refreshing tokens"
  },
  {
    value: TokenUsageType.OneTimeOnly,
    label: TokenUsageTypeDisplay[TokenUsageType.OneTimeOnly],
    description: "The refresh token handle will be updated when refreshing tokens"
  }
]);
const ClientSecretType = Object.freeze({
  SharedSecret: "SharedSecret",
  X509Thumbprint: "X509Thumbprint"
});
const ClientSecretTypeOptions = Object.freeze([
  { value: ClientSecretType.SharedSecret, label: "Shared Secret" },
  { value: ClientSecretType.X509Thumbprint, label: "X509 Thumbprint" }
]);
const EnvironmentType = Object.freeze({
  Development: "development",
  Integration: "integration",
  Staging: "staging",
  Production: "production"
});
const EnvironmentTypeDisplay = Object.freeze({
  [EnvironmentType.Development]: "Development",
  [EnvironmentType.Integration]: "Integration",
  [EnvironmentType.Staging]: "Staging",
  [EnvironmentType.Production]: "Production"
});
const EnvironmentTypeOptions = Object.freeze([
  {
    value: EnvironmentType.Development,
    label: EnvironmentTypeDisplay[EnvironmentType.Development]
  },
  {
    value: EnvironmentType.Integration,
    label: EnvironmentTypeDisplay[EnvironmentType.Integration]
  },
  { value: EnvironmentType.Staging, label: EnvironmentTypeDisplay[EnvironmentType.Staging] },
  { value: EnvironmentType.Production, label: EnvironmentTypeDisplay[EnvironmentType.Production] }
]);
const CallbackType = Object.freeze({
  UserAuthorized: 1,
  UserRevoked: 2,
  UserToggleEnabled: 3,
  UserRegistrationComplete: 4,
  PostRegistrationRedirect: 5,
  UserUpdated: 6
});
const CallbackTypeDisplay = Object.freeze({
  [CallbackType.UserAuthorized]: "User Application Access Authorized",
  [CallbackType.UserRevoked]: "User Application Access Revoked",
  [CallbackType.UserToggleEnabled]: "User Enabled Toggled",
  [CallbackType.UserRegistrationComplete]: "User Registration Completed",
  [CallbackType.PostRegistrationRedirect]: "Post Registration Redirect URL",
  [CallbackType.UserUpdated]: "User Updates"
});
const CallbackTypeOptions = Object.freeze([
  {
    id: CallbackType.UserAuthorized,
    value: CallbackType.UserAuthorized,
    type: "UserAuthorized",
    label: CallbackTypeDisplay[CallbackType.UserAuthorized],
    description: CallbackTypeDisplay[CallbackType.UserAuthorized]
  },
  {
    id: CallbackType.UserRevoked,
    value: CallbackType.UserRevoked,
    type: "UserRevoked",
    label: CallbackTypeDisplay[CallbackType.UserRevoked],
    description: CallbackTypeDisplay[CallbackType.UserRevoked]
  },
  {
    id: CallbackType.UserToggleEnabled,
    value: CallbackType.UserToggleEnabled,
    type: "UserToggleEnabled",
    label: CallbackTypeDisplay[CallbackType.UserToggleEnabled],
    description: CallbackTypeDisplay[CallbackType.UserToggleEnabled]
  },
  {
    id: CallbackType.UserRegistrationComplete,
    value: CallbackType.UserRegistrationComplete,
    type: "UserRegistrationComplete",
    label: CallbackTypeDisplay[CallbackType.UserRegistrationComplete],
    description: CallbackTypeDisplay[CallbackType.UserRegistrationComplete]
  },
  {
    id: CallbackType.PostRegistrationRedirect,
    value: CallbackType.PostRegistrationRedirect,
    type: "PostRegistrationRedirect",
    label: CallbackTypeDisplay[CallbackType.PostRegistrationRedirect],
    description: CallbackTypeDisplay[CallbackType.PostRegistrationRedirect]
  },
  {
    id: CallbackType.UserUpdated,
    value: CallbackType.UserUpdated,
    type: "UserUpdated",
    label: CallbackTypeDisplay[CallbackType.UserUpdated],
    description: CallbackTypeDisplay[CallbackType.UserUpdated]
  }
]);
class ApplicationsClient extends LoginSDK.ApplicationsClient {
  queryApplications(metadata) {
    return metadata ? super.queryApplications(MqlRequestBuilder.create(metadata).build("ApplicationModel")) : super.queryApplications("FETCH ApplicationModel;");
  }
  getDevelopmentApplications(metadata) {
    return this.#getApplicationsByEnvironment(EnvironmentType.Development, metadata);
  }
  getIntegrationApplications(metadata) {
    return this.#getApplicationsByEnvironment(EnvironmentType.Integration, metadata);
  }
  getStagingApplications(metadata) {
    return this.#getApplicationsByEnvironment(EnvironmentType.Staging, metadata);
  }
  getProductionApplications(metadata) {
    return this.#getApplicationsByEnvironment(EnvironmentType.Production, metadata);
  }
  #getApplicationsByEnvironment(environment, metadata = { filters: [] }) {
    metadata.filters.setValue("Environment", [environment]);
    return super.queryApplications(MqlRequestBuilder.create(metadata).build("ApplicationModel"));
  }
}
const ApplicationsService = reactive.service("applications", ApplicationsClient, {
  recursive: true,
  recurseDepth: 2
})((Service) => new Service(AuthConfig));
const replaceEnvironment = (db, environment, rows) => replaceRows(db, db.applications, rows, {
  where: (applications) => applications.filter((x) => x.environment === environment)
});
ApplicationsService.getDevelopmentApplications.latest();
ApplicationsService.getIntegrationApplications.latest();
ApplicationsService.getStagingApplications.latest();
ApplicationsService.getProductionApplications.latest();
ApplicationsService.reactor("applicationsReactor", {
  success: (response, _, db) => {
    const { source, args, payload } = response;
    switch (source) {
      case ApplicationsService.queryApplications:
        RequestMetadata.setCount(source.action, payload.totalCount);
        return replaceRows(db, db.applications, payload.values);
      case ApplicationsService.getDevelopmentApplications:
        RequestMetadata.setCount(source.action, payload.totalCount);
        return replaceEnvironment(db, EnvironmentType.Development, payload.values);
      case ApplicationsService.getIntegrationApplications:
        RequestMetadata.setCount(source.action, payload.totalCount);
        return replaceEnvironment(db, EnvironmentType.Integration, payload.values);
      case ApplicationsService.getStagingApplications:
        RequestMetadata.setCount(source.action, payload.totalCount);
        return replaceEnvironment(db, EnvironmentType.Staging, payload.values);
      case ApplicationsService.getProductionApplications:
        RequestMetadata.setCount(source.action, payload.totalCount);
        return replaceEnvironment(db, EnvironmentType.Production, payload.values);
      case ApplicationsService.getApplication:
        return db.applications.put(payload);
      case ApplicationsService.createApplication:
        return db.applications.add(payload);
      case ApplicationsService.updateApplication:
        return db.applications.put(payload);
      case ApplicationsService.deleteApplication:
        return db.applications.delete(args[0]);
      case ApplicationsService.getApplicationRoles:
        return replaceRows(
          db,
          db.applicationRoles,
          payload.map((x) => ({ ...x, applicationId: args[0] }))
        );
      case ApplicationsService.getApplicationsRoles:
        const roles = (payload.values || []).flatMap(
          (app) => (app.roles || []).map((r) => ({ ...r, applicationId: app.id }))
        );
        return db.applicationRoles.bulkPut(roles);
    }
  }
});
ApplicationsService.reactor("applicationsServiceMessageReactor", {
  success: (response, services) => {
    switch (response.source) {
      case ApplicationsService.createApplication:
      case ApplicationsService.updateApplication:
      case ApplicationsService.deleteApplication:
        return appRuntime.services.alerts.success("Success");
    }
  },
  error: (response, services) => {
    appRuntime.services.alerts.error(response.payload?.message ?? "Error");
  }
});
const transformApplicationData = async (data) => {
  if (data.logo instanceof Blob) {
    data.logo = await BlobUtils.toBase64(data.logo);
  }
  if (data.apiRegistrations) {
    data.apiRegistrations = data.apiRegistrations.map(
      (x) => typeof x === "object" ? x : { id: x }
    );
  }
};
ApplicationsService.createApplication.interceptor("pre", async (args) => {
  await transformApplicationData(args[0]);
});
ApplicationsService.updateApplication.interceptor("pre", async (args) => {
  await transformApplicationData(args[1]);
});
class ApiRegistrationsClient extends LoginSDK.ApiRegistrationsClient {
  queryApiRegistrations(metadata) {
    return metadata ? super.queryApiRegistrations(
      MqlRequestBuilder.create(metadata).build("ApiRegistrationModel")
    ) : super.queryApiRegistrations("FETCH ApiRegistrationModel;");
  }
}
const ApiRegistrationsService = reactive.service("apiRegistrations", ApiRegistrationsClient, {
  recursive: true,
  recurseDepth: 2
})((Service) => new Service(AuthConfig));
ApiRegistrationsService.queryApiRegistrations.latest();
ApiRegistrationsService.reactor("apiRegistrationsReactor", {
  success: (response, _, db) => {
    const { source, args, payload } = response;
    switch (source) {
      case ApiRegistrationsService.queryApiRegistrations:
        RequestMetadata.setCount(source.action, payload.totalCount);
        return replaceRows(db, db.apiRegistrations, payload.values);
      case ApiRegistrationsService.getApiRegistration:
        return db.apiRegistrations.put(payload);
      case ApiRegistrationsService.createApiRegistration:
        return db.apiRegistrations.add(payload);
      case ApiRegistrationsService.updateApiRegistration:
        return db.apiRegistrations.put(payload);
      case ApiRegistrationsService.deleteApiRegistration:
        return db.apiRegistrations.delete(args[0]);
      case ApiRegistrationsService.getApiRegistrationResources:
      case ApiRegistrationsService.deleteApiRegistrationResource:
      case ApiRegistrationsService.bulkUpdateApiRegistrationResources:
        return replaceRows(
          db,
          db.resources,
          payload.map((item) => {
            const { id, ...rest } = item;
            return { _id: id, ...rest };
          })
        );
      case ApiRegistrationsService.getRoles:
        return replaceRows(db, db.roles, payload);
      case ApiRegistrationsService.createRole:
        return db.roles.put(payload);
      case ApiRegistrationsService.getApiRegistrationCallbacks:
        return db.callbacks.bulkAdd(payload);
      case ApiRegistrationsService.updateApiRegistrationCallbacks:
        return replaceRows(db, db.callbacks, payload);
    }
  }
});
ApiRegistrationsService.reactor("apiRegistrationsMessageReactor", {
  success: (response, services) => {
    switch (response.source) {
      case ApiRegistrationsService.createApiRegistration:
      case ApiRegistrationsService.updateApiRegistration:
      case ApiRegistrationsService.deleteApiRegistration:
      case ApiRegistrationsService.bulkUpdateApiRegistrationResources:
      case ApiRegistrationsService.deleteApiRegistrationResource:
      case ApiRegistrationsService.updateApiRegistrationCallbacks:
      case ApiRegistrationsService.createRole:
        return appRuntime.services.alerts.success("Success");
    }
  },
  error: (response, services) => {
    return appRuntime.services.alerts.error(response.payload?.message ?? "Error");
  }
});
const ApiScopeService = reactive.service(
  "apiScopes",
  LoginSDK.ApiScopesClient
)((Service) => new Service(AuthConfig));
ApiScopeService.reactor("apiScopesReactor", {
  success: (response, _, db) => {
    const { source, payload } = response;
    switch (source) {
      case ApiScopeService.getApiScopes:
        return replaceRows(
          db,
          db.apiResourceScopesLookup,
          payload.map((x) => ({ value: x }))
        );
    }
  },
  error: (response, services) => {
    return appRuntime.services.alerts.error(response.payload?.message ?? "Error");
  }
});
const OperationsService = reactive.service(
  "operations",
  LoginSDK.OperationsClient
)((Service) => new Service(AuthConfig));
OperationsService.reactor("operationsReactor", {
  success: (response, services, db) => {
    const { source, payload } = response;
    switch (source) {
      case OperationsService.getResourceOperations:
        return replaceRows(
          db,
          db.operations,
          payload.map((item) => {
            const { id, ...rest } = item;
            return { _id: id, ...rest };
          })
        );
      case OperationsService.bulkUpdateResourceOperations:
        appRuntime.services.alerts.success("Success");
        return replaceRows(
          db,
          db.operations,
          payload.map((item) => {
            const { id, ...rest } = item;
            return { _id: id, ...rest };
          })
        );
    }
  },
  error: (response, services) => {
    return appRuntime.services.alerts.error(response.payload?.message ?? "Error");
  }
});
class RolesClient extends LoginSDK.RolesClient {
  queryRoles(metadata) {
    return metadata ? super.queryRoles(MqlRequestBuilder.create(metadata).build("RoleModel")) : super.queryRoles("FETCH RoleModel;");
  }
}
const RolesService = reactive.service("roles", RolesClient, {
  recursive: true,
  recurseDepth: 2
})((Service) => new Service(AuthConfig));
RolesService.reactor("rolesReactor", {
  success: (response, services, db) => {
    const { source, args, payload } = response;
    switch (source) {
      case RolesService.queryRoles:
        RequestMetadata.setCount(source.action, payload.totalCount);
        return replaceRows(db, db.roles, payload.values);
      case RolesService.getRolePolicies:
        return replaceRows(db, db.rolePolicies, payload);
      case RolesService.updateRole:
        appRuntime.services.alerts.success("Success");
        return db.roles.put(payload);
      case RolesService.deleteRole:
        appRuntime.services.alerts.success("Success");
        return db.roles.delete(args[0]);
      case RolesService.setRolePolicies:
        appRuntime.services.alerts.success("Success");
        return replaceRows(db, db.rolePolicies, payload);
    }
  },
  error: (response, services) => {
    return appRuntime.services.alerts.error(response.payload?.message ?? "Error");
  }
});
class UsersClient extends LoginSDK.UsersClient {
  queryUsers(metadata) {
    return metadata ? super.queryUsers(MqlRequestBuilder.create(metadata).build("UserModel")) : super.queryUsers("FETCH UserModel;");
  }
  getFilteredUsersLookup(userId, filter = "") {
    return super.queryUsers(
      userId ? `FETCH UserModel MATCH [Id != ${userId} AND (DisplayName ? '${filter}' OR Email ? '${filter}' OR Username ? '${filter}')] SORTBY DisplayName;` : `FETCH UserModel MATCH [DisplayName ? '${filter}' OR Email ? '${filter}' OR Username ? '${filter}'] SORTBY DisplayName;`
    );
  }
}
const UsersService = reactive.service("users", UsersClient, {
  recursive: true,
  recurseDepth: 2
})((Service) => new Service(AuthConfig));
UsersService.queryUsers.latest();
UsersService.reactor("usersReactor", {
  success: (response, _, db) => {
    const { source, args, payload } = response;
    switch (source) {
      case UsersService.queryUsers:
        RequestMetadata.setCount(source.action, payload.totalCount);
        return replaceRows(db, db.users, payload.values);
      case UsersService.getUser:
        return Promise.all([
          replaceRows(db, db.credentials, [{ userId: payload.user.id, ...payload.credentials }]),
          db.users.put(payload.user)
        ]);
      case UsersService.createUser:
        return db.users.add(payload);
      case UsersService.updateUser:
        return db.users.put(payload);
      case UsersService.deleteUser:
        return db.users.delete(args[0]);
      case UsersService.getUserApplications:
        return replaceRows(
          db,
          db.userApplications,
          payload.map((app) => ({ ...app, userId: args[0] }))
        );
      case UsersService.addUserApplication:
        return db.userApplications.put({ ...payload, userId: args[0] });
      case UsersService.removeUserApplication:
        return db.userApplications.delete(args[1]);
      case UsersService.getUserEffectivePolicies:
        return replaceRows(db, db.userEffectivePolicies, payload);
      case UsersService.getUserRoles:
        return replaceRows(
          db,
          db.userRoles,
          payload.map((x) => ({ ...x, userId: args[0] }))
        );
      case UsersService.addUserRole:
        return db.userRoles.put({ ...payload, userId: args[0] });
      case UsersService.removeUserRole:
        return db.userRoles.delete([args[1], args[0]]);
      case UsersService.getUserPolicyOverrides:
        return replaceRows(db, db.userOverridePolicies, payload);
      case UsersService.createUserPolicyOverride:
        return db.userOverridePolicies.put(payload);
      case UsersService.deleteUserPolicyOverride:
        return db.userOverridePolicies.delete(args[2]);
      case UsersService.getApiTokens:
        return replaceRows(db, db.apiTokens, payload, {
          where: (tokens) => tokens.where({ userId: args[0] })
        });
      case UsersService.createApiToken:
        return db.apiTokens.put(payload);
      case UsersService.updateApiToken:
        return db.apiTokens.put(payload);
      case UsersService.deleteApiToken:
        return db.apiTokens.delete(args[1]);
    }
  }
});
UsersService.reactor("usersMessageReactor", {
  success: (response, services) => {
    switch (response.source) {
      case UsersService.createUser:
      case UsersService.updateUser:
      case UsersService.deleteUser:
      case UsersService.updateCredentials:
      case UsersService.addUserApplication:
      case UsersService.removeUserApplication:
      case UsersService.addUserRole:
      case UsersService.removeUserRole:
      case UsersService.createUserPolicyOverride:
      case UsersService.deleteUserPolicyOverride:
        return appRuntime.services.alerts.success("Success");
    }
  },
  error: (response, services) => {
    return appRuntime.services.alerts.error(response.payload?.message ?? "Error");
  }
});
class ClientTemplatesClient extends LoginSDK.ClientTemplatesClient {
  queryClientTemplates(metadata) {
    return metadata ? super.queryClientTemplates(MqlRequestBuilder.create(metadata).build("ClientTemplateModel")) : super.queryClientTemplates("FETCH ClientTemplateModel;");
  }
}
const ClientTemplatesService = reactive.service("clientTemplates", ClientTemplatesClient, {
  recursive: true,
  recurseDepth: 2
})((Service) => new Service(AuthConfig));
ClientTemplatesService.reactor("clientTemplatesReactor", {
  success: (response, services, db) => {
    const { source, args, payload } = response;
    switch (source) {
      case ClientTemplatesService.queryClientTemplates:
        RequestMetadata.setCount(source.action, payload.totalCount);
        return replaceRows(
          db,
          db.clientTemplates,
          payload.values.map((x) => ({ id: x.id, name: x.name, ...JSON.parse(x.value) }))
        );
      case ClientTemplatesService.getClientTemplate: {
        const { id, name, value } = payload;
        return db.clientTemplates.put({ id, name, ...JSON.parse(value) });
      }
      case ClientTemplatesService.createClientTemplate:
        appRuntime.services.alerts.success("Success");
        return db.clientTemplates.add(payload);
      case ClientTemplatesService.updateClientTemplate:
        appRuntime.services.alerts.success("Success");
        return db.clientTemplates.put(payload);
      case ClientTemplatesService.deleteClientTemplate:
        appRuntime.services.alerts.success("Success");
        return db.clientTemplates.delete(args[0]);
    }
  },
  error: (response, services) => {
    return appRuntime.services.alerts.error(response.payload?.message ?? "Error");
  }
});
class ContextsClient extends LoginSDK.ContextsClient {
  queryContexts(metadata) {
    return metadata ? super.queryContexts(MqlRequestBuilder.create(metadata).build("ContextModel")) : super.queryContexts("FETCH ContextModel;");
  }
}
const ContextsService = reactive.service("contexts", ContextsClient, {
  recursive: true,
  recurseDepth: 2
})((Service) => new Service(AuthConfig));
ContextsService.reactor("contextsReactor", {
  success: (response, _, db) => {
    const { source, payload } = response;
    switch (source) {
      case ContextsService.queryContexts:
        RequestMetadata.setCount(source.action, payload.totalCount);
        return replaceRows(db, db.contexts, payload.values);
    }
  },
  error: (response, services) => {
    return services.alerts.ActionError("Error", "contexts", response);
  }
});
class TemplatesClient extends LoginSDK.TemplatesClient {
  queryTemplates(metadata) {
    return metadata ? super.queryTemplates(MqlRequestBuilder.create(metadata).build("TemplateModel")) : super.queryTemplates("FETCH TemplateModel;");
  }
  applyTemplate(userId, templateId) {
    return super.applyTemplate(userId, templateId);
  }
}
const TemplatesService = reactive.service("templates", TemplatesClient, {
  recursive: true,
  recurseDepth: 2
})((Service) => new Service(AuthConfig));
TemplatesService.queryTemplates.latest();
TemplatesService.reactor("templatesReactor", {
  success: (response, services, db) => {
    const { payload, source, args } = response;
    switch (source) {
      case TemplatesService.queryTemplates:
        RequestMetadata.setCount(source.action, payload.totalCount);
        return replaceRows(db, db.templates, payload.values);
      case TemplatesService.getTemplate:
        return db.templates.update(payload.id, payload);
      case TemplatesService.createTemplate:
        appRuntime.services.alerts.success("Success");
        return db.templates.add(payload);
      case TemplatesService.updateTemplate:
        appRuntime.services.alerts.success("Success");
        return db.templates.put(payload);
      case TemplatesService.deleteTemplate:
        appRuntime.services.alerts.success("Success");
        return db.templates.delete(args[0]);
      case TemplatesService.applyTemplate:
        appRuntime.services.alerts.success("Success");
        break;
    }
  }
});
TemplatesService.reactor("templateMessagesReactor", {
  error: (response, services) => {
    return appRuntime.services.alerts.error(response.payload?.message ?? "Error");
  }
});
class LoginHistoryClient extends LoginSDK.LoginHistoryClient {
  queryAuthenticationHistory(metadata) {
    return metadata ? super.queryAuthenticationHistory(
      MqlRequestBuilder.create(metadata).build("AuthenticationHistoryModel")
    ) : super.queryAuthenticationHistory("FETCH AuthenticationHistoryModel;");
  }
}
const LoginHistoryService = reactive.service("loginHistory", LoginHistoryClient, {
  recursive: true,
  recurseDepth: 2
})((Service) => new Service(AuthConfig));
LoginHistoryService.queryAuthenticationHistory.latest();
LoginHistoryService.reactor("loginHistoryReactor", {
  success: (response, _, db) => {
    const { source, payload } = response;
    switch (source) {
      case LoginHistoryService.queryAuthenticationHistory:
        const filter = (RequestMetadata.get(source.action).filters ?? []).find(
          (x) => x.name === "UserId"
        );
        RequestMetadata.setCount(source.action, payload.totalCount);
        return replaceRows(db, db.userAuthHistory, payload.values, {
          where: filter ? (history) => history.where({ userId: filter.value }) : void 0
        });
    }
  }
});
LoginHistoryService.reactor(
  "LoginHistoryMessageReactor",
  {
    error: (response, services) => {
      return appRuntime.services.alerts.error(response.payload?.message ?? "Error");
    }
  }
);
const storeFormat = (store) => Object.entries(store).reduce((result, [key, value]) => {
  result[key] = value.join(",");
  return result;
}, {});
const ReactiveConfig = {
  db: {
    name: "livefree.auth.db",
    provider: new DexieDatabaseProvider([dexieRequestBrokerAddon]),
    init: (db) => {
      db.version(14).stores(storeFormat(_store));
      db.version(15).stores({ apiResourceScopes: "++id" });
    }
  }
};
const _store = {
  apiResources: ["&id"],
  // What ApiScopesService.getApiScopes answers, read whole by the scope picker.
  apiResourceScopesLookup: ["++id"],
  identityResources: ["&id"],
  identityResourceUserClaims: ["++id", "identityResourceId"],
  identityResourceProperties: ["++id", "identityResourceId"],
  clients: ["&id", "applicationId"],
  clientTemplates: ["&id"],
  clientSecrets: ["&id", "clientId"],
  applications: ["&id"],
  applicationRoles: ["&id", "applicationId"],
  apiRegistrations: ["&id"],
  resources: ["++id", "_id", "apiRegistrationId"],
  operations: ["++id", "_id", "resourceId"],
  contexts: ["&id"],
  callbacks: ["&id", "apiRegistrationId"],
  roles: ["&id", "apiRegistrationId"],
  rolePolicies: ["&policyId", "roleId"],
  users: ["&id"],
  usersLookup: ["&id"],
  userApplications: ["&id", "userId"],
  userRoles: ["[&id+userId]", "userId", "apiRegistrationId"],
  userEffectivePolicies: [
    "&id",
    "userId",
    "apiRegistrationId",
    "resourceId",
    "operationId",
    "contextId"
  ],
  userOverridePolicies: ["&id", "userId", "apiRegistrationId"],
  credentials: ["&userId"],
  apiTokens: ["&id", "userId"],
  templates: ["&id", "applicationId"],
  userAuthHistory: ["&id", "userId"]
};
const Shell = ({ children }) => /* @__PURE__ */ jsx("div", { className: "p-4 h-full flex flex-col", children });
let Bootstrapped;
const Layout$1 = ({ children }) => {
  Bootstrapped ??= App.bootstrap(Shell, { runtime: appRuntime });
  return /* @__PURE__ */ jsx(Bootstrapped, { children });
};
const BaseLayout = reactive.configure(ReactiveConfig).render((props) => /* @__PURE__ */ jsx(Layout$1, { ...props }));
const FilteredFormsContext = createContext();
const FilteredFormsProvider = ({ forms = [], children }) => {
  const [filtered, setFiltered] = useState(forms);
  const sections = useRef(
    forms.reduce((acc, form) => {
      acc[form.name] = createRef();
      return acc;
    }, {})
  );
  const filter = useCallback(
    (search) => {
      if (search) {
        const result = forms.reduce((acc, form) => {
          const filteredFields = form.fields.filter(
            (field) => field?.label?.toLowerCase().includes(search.toLowerCase()) || field?.description?.toLowerCase().includes(search.toLowerCase())
          );
          if (filteredFields.length > 0) {
            acc.push({ ...form, fields: filteredFields });
          }
          return acc;
        }, []);
        setFiltered(result);
      } else {
        setFiltered(forms);
      }
    },
    [forms]
  );
  const context = useMemo(
    () => ({
      forms: filtered,
      sections,
      filter
    }),
    [filtered, sections, filter]
  );
  return /* @__PURE__ */ jsx(FilteredFormsContext.Provider, { value: context, children });
};
const useFilteredFormProvider = () => useContext(FilteredFormsContext);
const FilteredForm = ({ form, values: values2, onChange }) => {
  const { forms, sections } = useFilteredFormProvider();
  const isFormVisible = forms.some((x) => x.name === form.name);
  const fieldsFiltered = isFormVisible ? forms.find((x) => x.name === form.name)?.fields || [] : [];
  return /* @__PURE__ */ jsxs(
    Uniformly,
    {
      name: form.name,
      onChange: (x) => onChange({ name: form.name, [form.name]: x }),
      className: `p-4 flex flex-col gap-y-2 w-full ${isFormVisible ? "" : "hidden"}`,
      children: [
        /* @__PURE__ */ jsx("h5", { ref: sections.current[form.name], children: form.title }),
        form.fields.map((field) => {
          const { name, render } = field;
          const isFieldVisible = fieldsFiltered.some((x) => x.name === name);
          return /* @__PURE__ */ jsx("div", { className: `w-full ${isFieldVisible ? "" : "hidden"}`, children: render({ value: values2[name] }, values2) }, `${field.name}-field`);
        })
      ]
    }
  );
};
const FilteredForms = ({ values: values2, onChange }) => {
  const { forms } = useFilteredFormProvider();
  return forms.map((form) => /* @__PURE__ */ jsx(FilteredForm, { form, values: values2, onChange }, `form-${form.name}`));
};
const FilteredFormsNavigation = ({ iconDefinitions = {} }) => {
  const [search, setSearch] = useState("");
  const { forms, sections, filter } = useFilteredFormProvider();
  return /* @__PURE__ */ jsx("div", { className: "w-full", children: /* @__PURE__ */ jsxs("nav", { className: "flex flex-col gap-2", children: [
    /* @__PURE__ */ jsxs(
      TextField,
      {
        value: search,
        onChange: (value) => {
          setSearch(value);
          filter(value);
        },
        children: [
          /* @__PURE__ */ jsx(Label, { children: "Search" }),
          /* @__PURE__ */ jsxs(InputGroup, { children: [
            /* @__PURE__ */ jsx(InputGroup.Prefix, { children: /* @__PURE__ */ jsx("i", { className: "w-4 h-4 text-muted ri-search-line" }) }),
            /* @__PURE__ */ jsx(InputGroup.Input, { placeholder: "Settings" })
          ] })
        ]
      }
    ),
    /* @__PURE__ */ jsx(
      ListBox,
      {
        className: "w-auto shrink-0",
        "aria-label": "Listbox menu with icons",
        onAction: (key) => sections.current[key]?.current?.scrollIntoView({
          behavior: "smooth"
        }),
        children: forms.map((form) => /* @__PURE__ */ jsxs(
          ListBox.Item,
          {
            className: "justify-start",
            id: form.name,
            textValue: form.title,
            children: [
              Object.keys(iconDefinitions).includes(form.name) ? /* @__PURE__ */ jsx("i", { className: iconDefinitions[form.name] }) : null,
              /* @__PURE__ */ jsx(Label, { children: form.title })
            ]
          },
          `${form.name}-list`
        ))
      }
    )
  ] }) });
};
const useAggregateForm = () => {
  const [forms, setForms] = useState({});
  const isValid = useMemo(
    () => Object.entries(forms).map(([, form]) => form.isValid).every((x) => !!x),
    [forms]
  );
  const errors = useMemo(
    () => Object.entries(forms).reduce((prev, [, form]) => prev.concat(form.errors), []),
    [forms]
  );
  const data = useMemo(
    () => Object.entries(forms).reduce((prev, [, form]) => ({ ...prev, ...form.data }), {}),
    [forms]
  );
  const rawData = useMemo(
    () => Object.entries(forms).reduce((prev, [, form]) => ({ ...prev, ...form.rawData }), {}),
    [forms]
  );
  const setForm = useCallback((form) => {
    setForms((x) => ({ ...x, ...form }));
  }, []);
  return {
    forms,
    data,
    rawData,
    setForm,
    isValid,
    errors
  };
};
const EnvironmentChip = ({ environment, mode = "normal", ...rest }) => {
  const color = useMemo(() => {
    switch (environment) {
      case EnvironmentType.Development:
        return "success";
      case EnvironmentType.Integration:
        return "accent";
      case EnvironmentType.Staging:
        return "warning";
      case EnvironmentType.Production:
        return "danger";
    }
  }, [environment]);
  const title = useMemo(() => {
    switch (mode) {
      case "abbr":
        return pascalCase(environment).substring(0, 1);
      default:
        return pascalCase(environment);
    }
  }, [mode, environment]);
  return /* @__PURE__ */ jsx(Chip, { size: "sm", color, variant: "primary", ...rest, children: title });
};
const KeyDisplay = ({ keyValue, maxLength }) => /* @__PURE__ */ jsxs("div", { className: "flex gap-1 items-center", children: [
  /* @__PURE__ */ jsx("code", { className: "rounded-md bg-surface-secondary px-2 py-1 font-mono text-sm text-accent", children: maxLength > 0 ? truncate(keyValue, maxLength) : keyValue }),
  /* @__PURE__ */ jsx(CopyToClipboard, { text: keyValue })
] });
const NameValidationField = FormField(
  forwardRef(
    ({
      name,
      label,
      onChange,
      showError,
      errors,
      value,
      onValidate,
      isDisabled = false,
      isLoading = false,
      ...rest
    }, ref) => {
      const [status, setStatus] = useState({
        isValid: true,
        errors: []
      });
      useImperativeHandle(ref, () => ({
        setIsValid: (isValid, message) => {
          setStatus({ isValid, errors: message ? [message] : [] });
        }
      }));
      const handleValidate = useCallback((name2) => onValidate && onValidate(name2), [onValidate]);
      return /* @__PURE__ */ jsxs(
        TextField,
        {
          name,
          id: name,
          isInvalid: showError || !status.isValid,
          onChange,
          isDisabled,
          value,
          ...rest,
          children: [
            /* @__PURE__ */ jsx(Label, { children: label }),
            /* @__PURE__ */ jsxs(InputGroup, { variant: "secondary", children: [
              /* @__PURE__ */ jsx(
                InputGroup.Input,
                {
                  type: "text",
                  onBlur: ({ target }) => handleValidate(target.value),
                  autoComplete: "validate-name",
                  "data-1p-ignore": true,
                  "data-lpignore": "true",
                  "data-protonpass-ignore": "true"
                }
              ),
              isLoading && /* @__PURE__ */ jsx(InputGroup.Suffix, { children: /* @__PURE__ */ jsx(Spinner, { size: "sm", color: "current" }) })
            ] }),
            /* @__PURE__ */ jsx(FieldError, { children: (errors || []).concat(status.errors).join(",") })
          ]
        },
        name
      );
    }
  )
);
const ValidatePassword = FormField((props) => {
  const { name, value, label, onChange, ...rest } = props;
  const [isVisible, setIsVisible] = useState({
    password: false,
    confirmPassword: false
  });
  const [passwordValue, setConfirmPassword] = useState({
    password: "",
    confirmPassword: ""
  });
  return /* @__PURE__ */ jsxs(
    Uniformly,
    {
      name: "passwordForm",
      onChange: ({ data }) => {
        setConfirmPassword({
          password: data[name],
          confirmPassword: data.confirmPassword
        });
        let isValid = false;
        if (data[name] !== "" && data.confirmPassword !== "") {
          isValid = data[name] === data.confirmPassword;
        }
        onChange({
          value: data[name],
          isValid
        });
      },
      renderForm: false,
      children: [
        /* @__PURE__ */ jsx(
          TextField$1,
          {
            type: isVisible.password ? "string" : "password",
            name,
            label,
            value: value?.value,
            isInvalid: passwordValue.password !== passwordValue.confirmPassword,
            isRequired: true,
            errorMessage: passwordValue.password !== passwordValue.confirmPassword ? "Passwords do not match" : null,
            endContent: /* @__PURE__ */ jsx(
              "i",
              {
                className: `focus:outline-hidden ${isVisible.password ? "ri-eye-line" : "ri-eye-off-line"}`,
                style: { cursor: "pointer" },
                type: "button",
                onClick: () => setIsVisible((prev) => ({ ...prev, password: !prev.password }))
              }
            ),
            ...rest
          }
        ),
        /* @__PURE__ */ jsx(
          TextField$1,
          {
            type: isVisible.confirmPassword ? "string" : "password",
            name: "confirmPassword",
            label: "Confirm Password",
            isInvalid: passwordValue.password !== passwordValue.confirmPassword,
            isRequired: true,
            errorMessage: passwordValue.password !== passwordValue.confirmPassword ? "Passwords do not match" : null,
            endContent: /* @__PURE__ */ jsx(
              "i",
              {
                className: `focus:outline-hidden ${isVisible.confirmPassword ? "ri-eye-line" : "ri-eye-off-line"}`,
                style: { cursor: "pointer" },
                type: "button",
                onClick: () => setIsVisible((prev) => ({ ...prev, confirmPassword: !prev.confirmPassword }))
              }
            ),
            ...rest
          }
        )
      ]
    }
  );
});
const CopyApplicationMenu = reactive.component(
  ({ applicationId, currentEnvironment, services, monitors }) => {
    const [triggerIcon, setTriggerIcon] = useState("ri-more-2-fill");
    const items = useMemo(
      () => [
        {
          key: EnvironmentType.Development,
          title: "Copy to Development"
        },
        {
          key: EnvironmentType.Integration,
          title: "Copy to Integration"
        },
        {
          key: EnvironmentType.Staging,
          title: "Copy to Staging"
        },
        {
          key: EnvironmentType.Production,
          title: "Copy to Production"
        }
      ],
      []
    );
    const disabledKeys = useMemo(
      () => items.filter((x) => x.key === currentEnvironment).map((x) => x.key),
      [items, currentEnvironment]
    );
    const handleCopy = useCallback(
      (targetEnvironment) => services.applications.applicationOperation("copy-application", {
        applicationId,
        environment: targetEnvironment
      }),
      [services, applicationId]
    );
    useSuccessReactor(services.applications.applicationOperation, () => {
      setTriggerIcon("ri-checkbox-circle-fill text-success");
      setTimeout(() => {
        setTriggerIcon("ri-more-2-fill");
      }, 3e3);
    });
    return /* @__PURE__ */ jsxs(Dropdown, { isDisabled: monitors.applicationOperation.executing, children: [
      /* @__PURE__ */ jsx(Dropdown.Trigger, { "aria-label": "Copy application", className: "cursor-pointer", children: monitors.applicationOperation.executing ? /* @__PURE__ */ jsx(Spinner, { size: "sm" }) : /* @__PURE__ */ jsx("i", { className: triggerIcon }) }),
      /* @__PURE__ */ jsx(Dropdown.Popover, { children: /* @__PURE__ */ jsx(Dropdown.Menu, { items, disabledKeys, onAction: (key) => handleCopy(key), children: (item) => /* @__PURE__ */ jsx(Dropdown.Item, { id: item.key, textValue: item.title, children: /* @__PURE__ */ jsx(Label, { children: item.title }) }, item.key) }) })
    ] });
  },
  {
    name: "CopyApplicationMenu",
    monitors: (services) => [services.applications.applicationOperation]
  }
);
const ApplicationsColumns = [
  { propertyName: "name", name: "Name", isSortable: true, isResizable: true, minWidth: 180 },
  { propertyName: "key", name: "Key", isSortable: false, isResizable: true, minWidth: 180 },
  {
    propertyName: "description",
    name: "Description",
    isSortable: false,
    isResizable: true,
    minWidth: 220
  },
  {
    propertyName: "applicationUri",
    name: "URL",
    isSortable: false,
    isResizable: true,
    minWidth: 180
  }
];
const ApplicationsList = ({ applications = [], isDisabled = false, onSelect }) => {
  const renderCell = useCallback((row, columnKey) => {
    const cellValue = row[columnKey];
    switch (columnKey) {
      case "key":
        return cellValue ? /* @__PURE__ */ jsx(KeyDisplay, { keyValue: cellValue, maxLength: 50 }) : null;
      case "applicationUri":
        return cellValue ? /* @__PURE__ */ jsx(Link, { isExternal: true, href: cellValue, size: "sm", showAnchorIcon: true, className: "truncate", children: cellValue }) : null;
      default:
        return cellValue;
    }
  }, []);
  const columns2 = useMemo(
    () => [
      ...ApplicationsColumns.map((column) => ({
        id: column.propertyName,
        header: column.name,
        // `name` is the row's accessible identifier; sorting flows through the
        // request broker via each column's `allowsSorting` + matching `id`.
        isRowHeader: column.propertyName === "name",
        allowsSorting: column.isSortable,
        allowsResizing: column.isResizable,
        minWidth: column.minWidth,
        cell: (row) => renderCell(row, column.propertyName)
      })),
      // Pinned actions column: copy application to another environment.
      {
        id: "actions",
        header: "",
        pinned: "end",
        width: 56,
        minWidth: 56,
        cell: (row) => /* @__PURE__ */ jsx(CopyApplicationMenu, { applicationId: row.id, currentEnvironment: row.environment })
      }
    ],
    [renderCell]
  );
  return /* @__PURE__ */ jsx(
    RequestBrokerDataGrid,
    {
      "aria-label": "Applications table",
      chipScope: "global",
      columns: columns2,
      data: applications,
      getRowId: (row) => row.id,
      allowsColumnResize: true,
      onRowAction: (key) => {
        if (isDisabled) return;
        const selected = applications.find((item) => item.id === key);
        if (selected && onSelect) onSelect(selected);
      }
    }
  );
};
const filters$4 = new Filters([
  new FilterInput({
    name: "Name",
    title: "Name",
    defaultValue: ""
  }),
  new FilterInput({
    name: "Description",
    title: "Description",
    defaultValue: ""
  }),
  new FilterConstant({
    name: "Environment",
    title: "Environment",
    defaultValue: [],
    options: {
      searchType: "in",
      scope: "custom"
    },
    render: {
      scope: ""
    }
  })
]);
const serviceMap = (services) => ({
  [EnvironmentType.Staging]: services.applications.getStagingApplications,
  [EnvironmentType.Integration]: services.applications.getIntegrationApplications,
  [EnvironmentType.Development]: services.applications.getDevelopmentApplications,
  [EnvironmentType.Production]: services.applications.getProductionApplications
});
const monitorsMap = (monitors) => ({
  [EnvironmentType.Staging]: monitors.getStagingApplications,
  [EnvironmentType.Integration]: monitors.getIntegrationApplications,
  [EnvironmentType.Development]: monitors.getDevelopmentApplications,
  [EnvironmentType.Production]: monitors.getProductionApplications
});
const ApplicationsListController = reactive.component(
  ({ data, services, monitors, onSelect, db, environmentType }) => {
    const handleExecute = useCallback((action, metadata) => {
      action(metadata);
    }, []);
    return /* @__PURE__ */ jsx(
      RequestBroker,
      {
        actionKey: serviceMap(services)[environmentType].action,
        action: serviceMap(services)[environmentType],
        filters: filters$4,
        isExecuting: monitorsMap(monitors)[environmentType].executing,
        skipInitial: () => db.applications.toCollection().filter((x) => x.environment === environmentType).count(),
        onExecute: handleExecute,
        children: /* @__PURE__ */ jsx(
          ApplicationsList,
          {
            applications: data.applications,
            isDisabled: monitorsMap(monitors)[environmentType].executing,
            onSelect: (item) => onSelect && onSelect(item)
          }
        )
      }
    );
  },
  {
    name: "ApplicationsListController",
    queries: ({ environmentType }, services) => [
      {
        name: "applications",
        execute: (db) => db.withRequestBroker(
          serviceMap(services)[environmentType].action,
          () => db.applications.toCollection().filter((x) => x.environment === environmentType),
          {
            sorting: true
          }
        )
      }
    ],
    monitors: (services) => [
      services.applications.getDevelopmentApplications,
      services.applications.getIntegrationApplications,
      services.applications.getStagingApplications,
      services.applications.getProductionApplications
    ]
  }
);
const AccessTokenTypeLookup = ({ name, label, value, selectionMode, ...rest }) => {
  return /* @__PURE__ */ jsx(
    SelectField,
    {
      name,
      label,
      items: AccessTokenTypeOptions,
      value,
      selectionMode: selectionMode || "single",
      ...rest,
      children: (accessTokenType) => /* @__PURE__ */ jsxs(
        ListBox.Item,
        {
          id: accessTokenType.value,
          textValue: accessTokenType.label + " - " + accessTokenType.description,
          children: [
            accessTokenType.label + " - " + accessTokenType.description,
            /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
          ]
        },
        accessTokenType.value
      )
    }
  );
};
const TokenUsageTypeLookup = ({ name, label, value, selectionMode, ...rest }) => {
  return /* @__PURE__ */ jsx(
    SelectField,
    {
      name,
      label,
      items: TokenUsageTypeOptions,
      value,
      selectionMode: selectionMode || "single",
      ...rest,
      children: (tokenUsageType) => /* @__PURE__ */ jsxs(
        ListBox.Item,
        {
          id: tokenUsageType.value,
          textValue: tokenUsageType.label + " - " + tokenUsageType.description,
          children: [
            tokenUsageType.label + " - " + tokenUsageType.description,
            /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
          ]
        },
        tokenUsageType.value
      )
    }
  );
};
const TokenExpirationTypeLookup = ({ name, label, value, selectionMode, ...rest }) => {
  return /* @__PURE__ */ jsx(
    SelectField,
    {
      name,
      label,
      items: TokenExpirationTypeOptions,
      value,
      selectionMode: selectionMode || "single",
      emptyContent: "No options",
      variant: "secondary",
      ...rest,
      children: (tokenExpirationType) => /* @__PURE__ */ jsxs(
        ListBox.Item,
        {
          id: tokenExpirationType.value,
          textValue: tokenExpirationType.label + " - " + tokenExpirationType.description,
          children: [
            tokenExpirationType.label + " - " + tokenExpirationType.description,
            /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
          ]
        },
        tokenExpirationType.value
      )
    }
  );
};
const ClientSecretTypeLookup = ({ name, label, value, selectionMode, ...rest }) => {
  return /* @__PURE__ */ jsx(
    SelectField,
    {
      name,
      label,
      items: ClientSecretTypeOptions,
      value,
      selectionMode: selectionMode || "single",
      variant: "secondary",
      ...rest,
      children: (clientSecretType) => /* @__PURE__ */ jsxs(
        ListBox.Item,
        {
          id: clientSecretType.value,
          textValue: clientSecretType.label,
          children: [
            clientSecretType.label,
            /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
          ]
        },
        clientSecretType.value
      )
    }
  );
};
const EnvironmentTypeLookup = ({ name, label, value, selectionMode, ...rest }) => {
  return /* @__PURE__ */ jsx(
    SelectField,
    {
      name,
      label,
      items: EnvironmentTypeOptions,
      value,
      selectionMode: selectionMode || "single",
      ...rest,
      children: (environmentType) => /* @__PURE__ */ jsxs(
        ListBox.Item,
        {
          id: environmentType.value,
          textValue: environmentType.label,
          children: [
            environmentType.label,
            /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
          ]
        },
        environmentType.value
      )
    }
  );
};
const ApiRegistrationsLookup = reactive.component(
  ({
    data,
    monitors,
    name,
    label,
    value,
    selectionMode,
    services,
    isLoading,
    applicationId,
    ...rest
  }) => {
    return /* @__PURE__ */ jsx(
      SelectField,
      {
        name,
        label,
        items: data.apiRegistrations,
        value,
        selectionMode: selectionMode || "single",
        isLoading: isLoading || monitors.queryApiRegistrations.executing,
        withReload: true,
        onReload: () => services.apiRegistrations.queryApiRegistrations(),
        ...rest,
        children: (apiRegistration) => /* @__PURE__ */ jsxs(
          ListBox.Item,
          {
            id: apiRegistration.id,
            textValue: apiRegistration.name,
            children: [
              apiRegistration.name,
              /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
            ]
          },
          apiRegistration.id
        )
      }
    );
  },
  {
    name: "ApiRegistrationsLookup",
    sync: (services, { applicationId }) => [
      {
        execute: () => services.apiRegistrations.queryApiRegistrations(),
        deps: [applicationId]
      }
    ],
    queries: ({ applicationId }) => [
      {
        name: "apiRegistrations",
        execute: (db) => db.apiRegistrations.toArray(),
        deps: [applicationId]
      }
    ],
    monitors: (services) => [services.apiRegistrations.queryApiRegistrations]
  }
);
const ApiResourcesLookup = reactive.component(
  ({ data, monitors, services, name, label, value, selectionMode, ...rest }) => {
    return /* @__PURE__ */ jsx(
      SelectField,
      {
        name,
        label,
        items: data.apiResources,
        value,
        selectionMode: selectionMode || "single",
        isLoading: monitors.queryApiResources.executing,
        withReload: true,
        onReload: () => services.apiResources.queryApiResources(),
        ...rest,
        children: (apiResource) => /* @__PURE__ */ jsxs(ListBox.Item, { id: apiResource.id, textValue: apiResource.name, children: [
          apiResource.name,
          /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
        ] }, apiResource.id)
      }
    );
  },
  {
    name: "ApiResourcesLookup",
    sync: (services) => [
      {
        execute: () => services.apiResources.queryApiResources(),
        deps: []
      }
    ],
    queries: () => [
      {
        name: "apiResources",
        execute: (db) => db.apiResources.toArray()
      }
    ],
    monitors: (services) => [services.apiResources.queryApiResources]
  }
);
const _ResourcesLookup = ({
  data,
  monitors,
  name,
  label,
  value,
  selectionMode,
  ...rest
}) => {
  return /* @__PURE__ */ jsx(
    SelectField,
    {
      name,
      label,
      items: data.resources,
      value,
      selectionMode: selectionMode || "single",
      isLoading: monitors.getApiRegistrationResources.executing,
      ...rest,
      children: (resource) => /* @__PURE__ */ jsxs(ListBox.Item, { id: resource.keyCode, textValue: resource.keyCode, children: [
        resource.keyCode,
        /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
      ] }, resource.id)
    }
  );
};
const ResourcesLookup = reactive.component(_ResourcesLookup, {
  name: "ResourcesLookup",
  sync: (services, { apiRegistrationId }) => [
    {
      execute: () => services.apiRegistrations.getApiRegistrationResources(apiRegistrationId),
      deps: [apiRegistrationId]
    }
  ],
  queries: ({ apiRegistrationId }) => [
    {
      name: "resources",
      execute: (db) => db.resources.where({ apiRegistrationId }).toArray(),
      deps: [apiRegistrationId]
    }
  ],
  monitors: (services) => [services.apiRegistrations.getApiRegistrationResources]
});
const OperationsLookup = reactive.component(
  ({ data, monitors, name, label, value, selectionMode, forceRequest = false, ...rest }) => {
    return /* @__PURE__ */ jsx(
      SelectField,
      {
        name,
        label,
        items: data.operations,
        value,
        selectionMode: selectionMode || "single",
        isLoading: forceRequest && monitors.getResourceOperations.executing,
        ...rest,
        children: (operation) => /* @__PURE__ */ jsxs(ListBox.Item, { id: operation.id, textValue: operation.keyCode, children: [
          operation.keyCode,
          /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
        ] }, operation.id)
      }
    );
  },
  {
    name: "OperationsLookup",
    sync: (services, { resourceId }) => [
      {
        execute: () => resourceId && services.operations.getResourceOperations(resourceId),
        deps: [resourceId]
      }
    ],
    queries: ({ resourceId }) => [
      {
        name: "operations",
        execute: (db) => db.operations.where({ resourceId: resourceId || 0 }).toArray(),
        deps: [resourceId]
      }
    ],
    monitors: (services) => [services.operations.getResourceOperations]
  }
);
const _ContextsLookup = ({
  data,
  monitors,
  name,
  label,
  value,
  selectionMode,
  forceRequest = false,
  ...rest
}) => {
  const { contexts } = data;
  return /* @__PURE__ */ jsx(
    SelectField,
    {
      name,
      label,
      items: data.contexts,
      value,
      selectionMode: selectionMode || "single",
      isLoading: forceRequest && monitors.queryContexts.executing,
      ...rest,
      children: (context) => /* @__PURE__ */ jsxs(ListBox.Item, { id: context.id, textValue: context.name, children: [
        context.name,
        /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
      ] }, context.id)
    }
  );
};
const ContextsLookup = reactive.component(_ContextsLookup, {
  name: "ContextsLookup",
  sync: (services) => [
    {
      execute: () => services.contexts.queryContexts()
    }
  ],
  queries: () => [
    {
      name: "contexts",
      execute: (db) => db.contexts.toArray()
    }
  ],
  monitors: (services) => [services.contexts.queryContexts]
});
const _TemplatesLookup = ({
  data,
  monitors,
  name,
  label,
  value,
  selectionMode,
  ...rest
}) => {
  return /* @__PURE__ */ jsx(
    SelectField,
    {
      name,
      label,
      items: data.templates,
      value,
      selectionMode: selectionMode || "single",
      isLoading: monitors.queryTemplates.executing,
      ...rest,
      children: (template) => /* @__PURE__ */ jsxs(ListBox.Item, { id: template.id, textValue: template.name, children: [
        template.name,
        /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
      ] }, template.id)
    }
  );
};
const TemplatesLookup = reactive.component(_TemplatesLookup, {
  name: "TemplatesLookup",
  sync: (services) => [
    {
      execute: () => services.templates.queryTemplates(),
      deps: []
    }
  ],
  queries: () => [
    {
      name: "templates",
      execute: (db) => db.templates.toArray(),
      defaultValue: [],
      deps: []
    }
  ],
  monitors: (services) => [services.templates.queryTemplates]
});
const reducer = (state, action) => {
  switch (action.type) {
    case "term":
      return { ...state, searchText: action.payload };
    case "users":
      return { ...state, users: action.payload };
    case "show":
      return { ...state, showSearch: action.payload };
    case "all":
      return action.payload;
    default:
      return state;
  }
};
const useUsersLookup = (services, value) => {
  const [{ searchText, showSearch, users }, dispatch] = useReducer(reducer, {
    searchText: "",
    users: [],
    showSearch: !value
  });
  const actions = useMemo(
    () => ({
      setSearchText: (text) => dispatch({ type: "term", payload: text }),
      setUsers: (usrs) => dispatch({ type: "users", payload: usrs }),
      showSearch: (show) => dispatch({ type: "show", payload: show })
    }),
    [dispatch]
  );
  useSuccessReactor(services.users.getFilteredUsersLookup, (payload) => {
    const results = payload.values || [];
    actions.setUsers(results);
  });
  return {
    searchText,
    users,
    showSearch,
    actions
  };
};
const getInitials$1 = (name) => (name || "").split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
const UsersLookup = reactive.component(
  ({ userId, value, displayValue, label, services, monitors, ...rest }) => {
    const { searchText, users, showSearch, actions } = useUsersLookup(services, value);
    const handleSearch = useCallback(
      (x) => {
        services.users.getFilteredUsersLookup(userId, x);
      },
      [userId, services]
    );
    const transformIn = useCallback(
      (val) => val !== null && val !== void 0 ? val.toString() : val,
      []
    );
    const transformOut = useCallback(
      (val) => !isNaN(parseInt(val, 10)) ? parseInt(val, 10) : val,
      []
    );
    return showSearch ? /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
      /* @__PURE__ */ jsxs(
        TextField,
        {
          name: "userSearch",
          value: searchText,
          onChange: actions.setSearchText,
          variant: "secondary",
          children: [
            /* @__PURE__ */ jsx(Label, { children: `Search ${label}` }),
            /* @__PURE__ */ jsxs(InputGroup, { children: [
              /* @__PURE__ */ jsx(
                InputGroup.Input,
                {
                  type: "text",
                  onKeyUp: keyPressedHandler("enter", () => handleSearch(searchText))
                }
              ),
              /* @__PURE__ */ jsx(InputGroup.Suffix, { className: "pr-1", children: /* @__PURE__ */ jsx(
                Button,
                {
                  isIconOnly: true,
                  size: "sm",
                  variant: "primary",
                  onPress: () => handleSearch(searchText),
                  className: "rounded-md",
                  isDisabled: monitors.getFilteredUsersLookup.executing,
                  children: monitors.getFilteredUsersLookup.executing ? /* @__PURE__ */ jsx(Spinner, { size: "sm", color: "current" }) : /* @__PURE__ */ jsx("i", { className: "ri-search-line" })
                }
              ) })
            ] })
          ]
        }
      ),
      users.length > 0 && /* @__PURE__ */ jsx(
        AutocompleteField,
        {
          label,
          items: users,
          extractValue: (user) => user.id.toString(),
          extractDisplayValue: (user) => user.displayName,
          transformIn,
          transformOut,
          value,
          ...rest,
          children: (user) => /* @__PURE__ */ jsx(ListBox.Item, { id: user.id.toString(), textValue: user.displayName, children: /* @__PURE__ */ jsxs("div", { className: "flex gap-2 items-center", children: [
            /* @__PURE__ */ jsxs(Avatar, { children: [
              /* @__PURE__ */ jsx(Avatar.Image, { alt: user.displayName, src: user.avatarUrl }),
              /* @__PURE__ */ jsx(Avatar.Fallback, { children: getInitials$1(user.displayName) })
            ] }),
            /* @__PURE__ */ jsxs("div", { className: "flex flex-col", children: [
              /* @__PURE__ */ jsx("span", { className: "text-sm", children: user.displayName }),
              /* @__PURE__ */ jsxs("span", { className: "text-xs text-muted", children: [
                "Email: ",
                user.email
              ] }),
              /* @__PURE__ */ jsxs("span", { className: "text-xs text-muted", children: [
                "Username: ",
                user.username
              ] })
            ] })
          ] }) }, user.id)
        }
      )
    ] }) : /* @__PURE__ */ jsxs(TextField, { name: "parentUser", value: displayValue, isReadOnly: true, children: [
      /* @__PURE__ */ jsx(Label, { children: "Parent User" }),
      /* @__PURE__ */ jsxs(InputGroup, { children: [
        /* @__PURE__ */ jsx(InputGroup.Input, { type: "text" }),
        /* @__PURE__ */ jsx(InputGroup.Suffix, { children: /* @__PURE__ */ jsx(ModifyButton, { iconOnly: true, onPress: () => actions.showSearch(true) }) })
      ] })
    ] });
  },
  {
    name: "UsersLookup",
    monitors: (services) => [services.users.getFilteredUsersLookup]
  }
);
const ApplicationsLookup = reactive.component(
  ({ services, data, monitors, name, label, value, selectionMode, ...rest }) => {
    const { applications } = data;
    return /* @__PURE__ */ jsx(
      SelectField,
      {
        name,
        label,
        items: data.applications,
        value,
        selectionMode: selectionMode || "single",
        isLoading: monitors.queryApplications.executing,
        withReload: true,
        onReload: () => services.applications.queryApplications(),
        ...rest,
        children: (app) => /* @__PURE__ */ jsxs(ListBox.Item, { id: app.id, textValue: `${app.name} ${app.environment}`, children: [
          /* @__PURE__ */ jsxs("div", { className: "inline-flex items-center gap-2", children: [
            app.name,
            " ",
            /* @__PURE__ */ jsx(EnvironmentChip, { environment: app.environment })
          ] }),
          /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
        ] }, app.id)
      }
    );
  },
  {
    name: "ApplicationsLookup",
    sync: (services, { forceRequest }) => [
      {
        execute: () => forceRequest && services.applications.queryApplications(),
        deps: [forceRequest]
      }
    ],
    queries: () => [
      {
        name: "applications",
        execute: (db) => db.applications.toCollection().sortBy("name")
      }
    ],
    monitors: (services) => [services.applications.queryApplications]
  }
);
reactive.component(
  ({ data, monitors, name, label, value, selectionMode, ...rest }) => {
    return /* @__PURE__ */ jsx(
      SelectField,
      {
        name,
        label,
        "aria-label": label || "Application Roles",
        items: data.roles,
        value,
        selectionMode: selectionMode || "single",
        isLoading: monitors.getApplicationRoles.executing,
        ...rest,
        children: (role) => /* @__PURE__ */ jsxs(ListBox.Item, { id: role.id, textValue: role.name, children: [
          role.name,
          /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
        ] }, role.id)
      }
    );
  },
  {
    name: "ApplicationRolesLookup",
    sync: (services, { applicationId, apiRegistrationId }) => [
      {
        execute: () => services.applications.getApplicationRoles(applicationId),
        bypass: (db) => db.applicationRoles.where({ applicationId }).count(),
        deps: [applicationId, apiRegistrationId]
      }
    ],
    queries: ({ applicationId, apiRegistrationId }) => [
      {
        name: "roles",
        execute: (db) => apiRegistrationId ? db.applicationRoles.filter((x) => x.apiRegistrationId === Number(apiRegistrationId)).toArray() : db.applicationRoles.where({ applicationId }).toArray(),
        deps: [applicationId, apiRegistrationId]
      }
    ],
    monitors: (services) => [services.applications.getApplicationRoles]
  }
);
const ApiResourceScopeDynamicField = reactive.component(
  ({ monitors, data, applicationId, name, label, defaultItems = [], ...rest }) => {
    if (applicationId && monitors.getApiScopes.executing) return null;
    return /* @__PURE__ */ jsx(
      ArrayField,
      {
        name,
        label,
        defaultItems: isNumber(applicationId) ? data.apiScopes.map((x) => ({ value: x.value, label: x.value })) : defaultItems,
        ...rest
      }
    );
  },
  {
    name: "ApiResourceScopeDynamicField",
    sync: (services, { applicationId }) => [
      {
        execute: () => isNumber(applicationId) && services.apiScopes.getApiScopes(applicationId || 0),
        deps: [applicationId]
      }
    ],
    queries: ({ applicationId }) => [
      {
        name: "apiScopes",
        execute: (db) => db.apiResourceScopesLookup.toArray(),
        deps: [applicationId]
      }
    ],
    monitors: (services) => [services.apiScopes.getApiScopes]
  }
);
const ApiNameField = reactive.component(
  ({ apiRegistrationId, services, monitors, ...rest }) => {
    const ref = useRef();
    useSuccessReactor(services.apiRegistrations.apiRegistrationOperation, (response) => {
      if (response.exists && response.apiRegistrationId !== apiRegistrationId) {
        ref.current.setIsValid(
          false,
          `${response.apiRegistrationName} is already used, please choose a different name`
        );
      } else {
        ref.current.setIsValid(true);
      }
    });
    const handleValidate = (name) => {
      services.apiRegistrations.apiRegistrationOperation("validate-registration-name", {
        apiRegistrationId,
        name
      });
    };
    return /* @__PURE__ */ jsx(
      NameValidationField,
      {
        ref,
        onValidate: handleValidate,
        isLoading: monitors.apiRegistrationOperation.executing,
        isDisabled: monitors.apiRegistrationOperation.executing,
        ...rest
      }
    );
  },
  {
    name: "ApiNameField",
    monitors: (services) => [services.apiRegistrations.apiRegistrationOperation]
  }
);
const ApiForm = ({ apiRegistration, onChange }) => {
  return /* @__PURE__ */ jsxs(Uniformly, { className: "flex flex-col gap-2", name: "apiRegistrationsForm", onChange, children: [
    apiRegistration && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(TextField$1, { className: "hidden", hidden: true, name: "id", value: apiRegistration.id }),
      /* @__PURE__ */ jsx(TextField$1, { className: "hidden", hidden: true, name: "key", value: apiRegistration.key })
    ] }),
    /* @__PURE__ */ jsx(
      ApiNameField,
      {
        apiRegistrationId: apiRegistration?.id,
        name: "name",
        label: "Name",
        value: apiRegistration?.name,
        isRequired: true,
        validation: (x) => x.string().max(100).required("Name is required"),
        description: "Do not change the API name unless you know what you're doing, bad things will happen!"
      }
    ),
    /* @__PURE__ */ jsx(TextField$1, { name: "description", label: "Description", value: apiRegistration?.description }),
    /* @__PURE__ */ jsx(
      ApiResourcesLookup,
      {
        name: "apiResourceId",
        label: "Api Resource",
        value: apiRegistration?.apiResourceId,
        apiResourceId: apiRegistration?.apiResourceId,
        isRequired: true,
        validation: (x) => x.string().required("Required")
      }
    )
  ] });
};
const ApiCreate = ({ onChange }) => /* @__PURE__ */ jsx(ApiForm, { onChange });
ApiCreate.Drawer = withDrawerController(ApiCreate);
const ApiCreateView = reactive.component(
  ({ services, monitors }) => {
    const navigate = useNavigate();
    const [form, setForm] = useState({});
    const goToList = () => navigate(RoutePaths().apis.list());
    useSuccessReactor(services.apiRegistrations.createApiRegistration, goToList);
    return /* @__PURE__ */ jsx(
      ApiCreate.Drawer,
      {
        isOpen: true,
        size: "3xl",
        onChange: setForm,
        headerContent: () => "Create Api Registration",
        footerContent: () => /* @__PURE__ */ jsxs("div", { className: "flex w-full items-center justify-end gap-x-2", children: [
          /* @__PURE__ */ jsx(ErrorListChip, { errors: form.errors }),
          /* @__PURE__ */ jsx(
            SaveButton,
            {
              size: "sm",
              color: "primary",
              isDisabled: !form.isValid,
              onPress: () => services.apiRegistrations.createApiRegistration(form.data),
              isLoading: monitors.createApiRegistration.executing
            }
          )
        ] }),
        onClose: goToList
      }
    );
  },
  {
    name: "ApiCreateView",
    monitors: (services) => [services.apiRegistrations.createApiRegistration]
  }
);
const ActiveApiContext = createContext();
const ActiveApiProvider = reactive.component(
  ({ data, monitors, children, onNotFound }) => {
    const { apiRegistration, resources, roles, contexts } = data;
    useRecentView({
      title: apiRegistration?.name,
      route: apiRegistration?.id ? RoutePaths().apis.details(apiRegistration.id) : void 0
    });
    if (monitors.getApiRegistration.complete && isEmpty(apiRegistration))
      return /* @__PURE__ */ jsx(NotFound, { resourceType: "API", onBack: onNotFound });
    if (monitors.getApiRegistration.executing)
      return /* @__PURE__ */ jsx(Loading, { label: "Loading...", color: "primary" });
    return !isEmpty(apiRegistration) && /* @__PURE__ */ jsx(ActiveApiContext.Provider, { value: { apiRegistration, resources, roles, contexts }, children: children({ apiRegistration, resources, roles, contexts }) });
  },
  {
    name: "ActiveApiProvider",
    sync: (services, { apiRegistrationId }) => [
      {
        execute: () => services.apiRegistrations.getApiRegistration(apiRegistrationId),
        deps: [apiRegistrationId]
      },
      {
        execute: () => services.apiRegistrations.getApiRegistrationResources(apiRegistrationId),
        deps: [apiRegistrationId]
      },
      {
        execute: () => services.apiRegistrations.getRoles(apiRegistrationId),
        deps: [apiRegistrationId]
      },
      {
        execute: () => services.contexts.queryContexts(),
        deps: [apiRegistrationId]
      }
    ],
    queries: ({ apiRegistrationId }) => [
      {
        name: "apiRegistration",
        execute: (db) => db.apiRegistrations.get(apiRegistrationId),
        defaultValue: {},
        deps: [apiRegistrationId]
      },
      {
        name: "resources",
        execute: (db) => db.resources.where({ apiRegistrationId }).sortBy("order"),
        deps: [apiRegistrationId]
      },
      {
        name: "roles",
        execute: (db) => db.roles.where({ apiRegistrationId }).toArray(),
        deps: [apiRegistrationId]
      },
      {
        name: "contexts",
        execute: (db) => db.contexts.toArray(),
        deps: []
      }
    ],
    monitors: (services) => [services.apiRegistrations.getApiRegistration]
  }
);
const withActiveApi = (Wrapper) => {
  const _Wrapper = ({ ...props }) => {
    const context = useContext(ActiveApiContext);
    if (!context || !context?.apiRegistration?.id)
      return /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx("p", { children: /* @__PURE__ */ jsx("strong", { children: "Error" }) }),
        /* @__PURE__ */ jsxs("p", { children: [
          "A component using ",
          /* @__PURE__ */ jsx("strong", { children: "withApiRegistration" }),
          " must be wrapped with",
          " ",
          /* @__PURE__ */ jsx("strong", { children: "ApiRegistrationProvider" }),
          "."
        ] })
      ] });
    return /* @__PURE__ */ jsx(Wrapper, { ...props, ...context });
  };
  return _Wrapper;
};
const useActiveApi = () => useContext(ActiveApiContext);
const NAV_ITEMS = [
  {
    key: "details",
    label: "General",
    icon: "ri-settings-3-line",
    href: (id) => RoutePaths().apis.details(id)
  },
  {
    key: "resources",
    label: "Resources",
    icon: "ri-database-2-line",
    href: (id) => RoutePaths().apis.resources(id)
  },
  {
    key: "roles",
    label: "Roles",
    icon: "ri-user-settings-line",
    href: (id) => RoutePaths().apis.roles(id)
  },
  {
    key: "callbacks",
    label: "Webhooks",
    icon: "ri-links-line",
    href: (id) => RoutePaths().apis.callbacks(id)
  }
];
const ApiNavigationTabs = ({ apiRegistrationId }) => {
  const location = useLocation();
  const selectedKey = NAV_ITEMS.slice(1).map((item) => item.key).find((key) => location.pathname.includes(`/${apiRegistrationId}/${key}`)) || "details";
  return /* @__PURE__ */ jsx(
    ListBox,
    {
      "aria-label": "options",
      disallowEmptySelection: true,
      selectionMode: "single",
      selectedKeys: [selectedKey],
      children: NAV_ITEMS.map(({ key, label, icon, href }) => /* @__PURE__ */ jsxs(
        ListBox.Item,
        {
          id: key,
          textValue: label,
          href: href(apiRegistrationId),
          "aria-label": label,
          children: [
            /* @__PURE__ */ jsx("i", { className: icon }),
            /* @__PURE__ */ jsx(Label, { children: label })
          ]
        },
        key
      ))
    }
  );
};
const ApiDetailsView = ({ apiRegistrationId }) => {
  const navigate = useNavigate();
  return /* @__PURE__ */ jsx(
    ActiveApiProvider,
    {
      apiRegistrationId,
      onNotFound: () => navigate(RoutePaths().apis.list()),
      children: ({ apiRegistration }) => /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx("div", { className: "flex flex-row justify-between mb-4 items-center", children: /* @__PURE__ */ jsxs(Breadcrumbs, { children: [
          /* @__PURE__ */ jsx(BreadcrumbsItem, { isDisabled: true, children: "Auth" }),
          /* @__PURE__ */ jsx(BreadcrumbsItem, { onPress: () => navigate(RoutePaths().apis.list()), children: "APIs" }),
          /* @__PURE__ */ jsx(BreadcrumbsItem, { children: apiRegistration.name })
        ] }) }),
        /* @__PURE__ */ jsx(SplitCard, { children: /* @__PURE__ */ jsxs(SplitCard.Body, { children: [
          /* @__PURE__ */ jsx(SplitCard.Sidebar, { children: /* @__PURE__ */ jsx(ApiNavigationTabs, { apiRegistrationId: apiRegistration.id }) }),
          /* @__PURE__ */ jsx(SplitCard.Main, { children: /* @__PURE__ */ jsx(Outlet, {}) })
        ] }) })
      ] })
    }
  );
};
const ApiColumns = [
  { propertyName: "name", name: "Name", isSortable: true, isResizable: true, minWidth: 160 },
  { propertyName: "key", name: "Key", isSortable: false, isResizable: true, minWidth: 180 },
  {
    propertyName: "description",
    name: "Description",
    isSortable: false,
    isResizable: true,
    minWidth: 240
  },
  {
    propertyName: "apiResourceName",
    name: "API Resource",
    isSortable: false,
    isResizable: true,
    minWidth: 180
  }
];
const ApiList = ({ apiRegistrations = [], onSelect }) => {
  const renderCell = useCallback((row, columnKey) => {
    const cellValue = row[columnKey];
    switch (columnKey) {
      case "name":
        return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx("i", { className: "ri-database-2-line" }),
          /* @__PURE__ */ jsx("span", { className: "truncate", children: cellValue })
        ] });
      case "key":
        return cellValue ? /* @__PURE__ */ jsx(KeyDisplay, { keyValue: cellValue, maxLength: 50 }) : null;
      case "description":
        return /* @__PURE__ */ jsx("span", { className: "text-xs italic", children: cellValue });
      case "apiResourceName":
        return cellValue ? /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1", children: [
          /* @__PURE__ */ jsx("code", { className: "rounded-md bg-surface-secondary px-2 py-1 font-mono text-sm text-accent", children: cellValue }),
          /* @__PURE__ */ jsx(CopyToClipboard, { text: cellValue })
        ] }) : null;
      default:
        return cellValue;
    }
  }, []);
  const columns2 = useMemo(
    () => ApiColumns.map((column) => ({
      id: column.propertyName,
      header: column.name,
      // `name` is the row's accessible identifier; sorting flows through the
      // request broker via each column's `allowsSorting` + matching `id`.
      isRowHeader: column.propertyName === "name",
      allowsSorting: column.isSortable,
      allowsResizing: column.isResizable,
      minWidth: column.minWidth,
      cell: (row) => renderCell(row, column.propertyName)
    })),
    [renderCell]
  );
  return /* @__PURE__ */ jsx(
    RequestBrokerDataGrid,
    {
      "aria-label": "API registrations table",
      chipScope: "global",
      columns: columns2,
      data: apiRegistrations,
      getRowId: (row) => row.id,
      allowsColumnResize: true,
      onRowAction: (key) => {
        const selected = apiRegistrations.find((item) => item.id === key);
        if (selected && onSelect) onSelect(selected);
      }
    }
  );
};
const filters$3 = new Filters([
  new FilterInput({
    name: "Name",
    title: "Name",
    defaultValue: ""
  }),
  new FilterInput({
    name: "Description",
    title: "Description",
    defaultValue: ""
  })
]);
const ApiListView = reactive.component(
  ({ data, services, monitors, db }) => {
    const navigate = useNavigate();
    const handleExecute = useCallback((action, metadata) => {
      action(metadata);
    }, []);
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx("div", { className: "flex flex-row justify-between mb-4", children: /* @__PURE__ */ jsxs(Breadcrumbs, { className: "content-center", children: [
        /* @__PURE__ */ jsx(BreadcrumbsItem, { isDisabled: true, children: "Auth" }),
        /* @__PURE__ */ jsx(BreadcrumbsItem, { children: "APIs" })
      ] }) }),
      /* @__PURE__ */ jsx(
        RequestBroker,
        {
          actionKey: "queryApiRegistrations",
          action: services.apiRegistrations.queryApiRegistrations,
          filters: filters$3,
          isExecuting: monitors.queryApiRegistrations.executing,
          onExecute: handleExecute,
          skipInitial: () => db.apiRegistrations.count(),
          children: /* @__PURE__ */ jsx(
            ApiList,
            {
              apiRegistrations: data.apiRegistrations,
              onSelect: (item) => navigate(RoutePaths().apis.details(item.id))
            }
          )
        }
      ),
      /* @__PURE__ */ jsx(Outlet, {})
    ] });
  },
  {
    name: "ApiListController",
    sync: (services) => [
      {
        execute: () => services.apiRegistrations.queryApiRegistrations(),
        bypass: (db) => db.apiRegistrations.count(),
        deps: []
      }
    ],
    queries: (_, services) => [
      {
        name: "apiRegistrations",
        execute: (db) => db.withRequestBroker(
          services.apiRegistrations.queryApiRegistrations.action,
          () => db.apiRegistrations,
          {
            sorting: true
          }
        )
      }
    ],
    monitors: (services) => [services.apiRegistrations.queryApiRegistrations]
  }
);
const ApiUpdateView = reactive.component(
  ({ services, monitors, apiRegistrationId }) => {
    const navigate = useNavigate();
    const { apiRegistration } = useActiveApi();
    const [apiForm, setApiForm] = useState({});
    useSuccessReactor(services.apiRegistrations.deleteApiRegistration, () => {
      navigate(RoutePaths().apis.list());
    });
    return /* @__PURE__ */ jsxs(FlatCard, { children: [
      /* @__PURE__ */ jsx(FlatCard.Header, { children: /* @__PURE__ */ jsx(FlatCard.Title, { children: "General" }) }),
      /* @__PURE__ */ jsxs(FlatCard.Main, { className: "flex flex-col gap-2", children: [
        /* @__PURE__ */ jsx(KeyDisplay, { keyValue: apiRegistration.key }),
        /* @__PURE__ */ jsx(ApiForm, { apiRegistration, onChange: setApiForm })
      ] }),
      /* @__PURE__ */ jsxs(FlatCard.Footer, { className: "flex flex-row justify-between gap-2", children: [
        /* @__PURE__ */ jsx(
          DeleteButton,
          {
            size: "sm",
            color: "danger",
            onPress: () => services.apiRegistrations.deleteApiRegistration(apiRegistration.id),
            isLoading: monitors.deleteApiRegistration.executing
          }
        ),
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx(ErrorListChip, { errors: apiForm.errors }),
          /* @__PURE__ */ jsx(
            SaveButton,
            {
              size: "sm",
              color: "primary",
              isDisabled: !apiForm.isValid,
              onPress: () => services.apiRegistrations.updateApiRegistration(apiRegistration.id, apiForm.data),
              isLoading: monitors.updateApiRegistration.executing
            }
          )
        ] })
      ] })
    ] });
  },
  {
    name: "ApiUpdateView",
    monitors: (services) => [
      services.apiRegistrations.updateApiRegistration,
      services.apiRegistrations.deleteApiRegistration
    ]
  }
);
const ResourceCreateForm = ({ onChange }) => {
  return /* @__PURE__ */ jsxs(Uniformly, { className: "flex flex-col gap-2", name: "resource", onChange, children: [
    /* @__PURE__ */ jsx(
      TextField$1,
      {
        name: "keyCode",
        label: "Key Code",
        size: "sm",
        isRequired: true,
        validation: (x) => x.string().max(100).required("Key Code is Required")
      }
    ),
    /* @__PURE__ */ jsx(
      TextField$1,
      {
        name: "name",
        label: "Name",
        size: "sm",
        isRequired: true,
        validation: (x) => x.string().max(100).required("Name is Required")
      }
    )
  ] });
};
const ResourceCreate = ({ onChange }) => /* @__PURE__ */ jsx(ResourceCreateForm, { onChange });
ResourceCreate.Drawer = withDrawerController(ResourceCreate);
const ResourceCreateView = reactive.component(
  ({ apiRegistrationId, services, data, monitors }) => {
    const navigate = useNavigate();
    const { resources } = data;
    const [form, setForm] = useState({});
    const goToList = () => navigate(RoutePaths().apis.resources(apiRegistrationId));
    useSuccessReactor(services.apiRegistrations.bulkUpdateApiRegistrationResources, goToList);
    return /* @__PURE__ */ jsx(
      ResourceCreate.Drawer,
      {
        isOpen: true,
        size: "3xl",
        onChange: setForm,
        headerContent: () => "Create Resource",
        footerContent: () => /* @__PURE__ */ jsxs("div", { className: "flex w-full items-center justify-end gap-x-2", children: [
          /* @__PURE__ */ jsx(ErrorListChip, { errors: form.errors }),
          /* @__PURE__ */ jsx(
            SaveButton,
            {
              size: "sm",
              color: "primary",
              isDisabled: !form.isValid,
              isLoading: monitors.bulkUpdateApiRegistrationResources.executing,
              onPress: () => {
                const payload = resources.map(({ _id, ...rest }) => ({ ...rest, id: _id })).concat({
                  apiRegistrationId,
                  ...form.data,
                  id: void 0,
                  order: resources.length + 1
                });
                services.apiRegistrations.bulkUpdateApiRegistrationResources(
                  apiRegistrationId,
                  payload
                );
              }
            }
          )
        ] }),
        onClose: goToList
      }
    );
  },
  {
    name: "ResourceCreateView",
    queries: ({ apiRegistrationId }) => [
      {
        name: "resources",
        execute: (db) => db.resources.where({ apiRegistrationId }).toArray(),
        deps: [apiRegistrationId]
      }
    ],
    monitors: (services) => [services.apiRegistrations.bulkUpdateApiRegistrationResources]
  }
);
const ReorderItem$1 = ({ item, onSelect, onDelete, onDragEnd }) => {
  const y = useMotionValue(0);
  const dragControls = useDragControls();
  return /* @__PURE__ */ jsx(
    Reorder.Item,
    {
      value: item,
      style: { y },
      dragListener: false,
      dragControls,
      onDragEnd,
      children: /* @__PURE__ */ jsxs("div", { className: "flex flex-row mb-2 gap-2 items-center justify-between", children: [
        /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsx(
          "i",
          {
            className: "ri-draggable",
            style: { cursor: "pointer" },
            onPointerDown: (event) => dragControls.start(event)
          }
        ) }),
        /* @__PURE__ */ jsxs("div", { className: "basis-1/3", children: [
          item.keyCode,
          " ",
          /* @__PURE__ */ jsx(DeprecatedChip, { size: "sm", color: "warning", isDeprecated: item.isDeprecated })
        ] }),
        /* @__PURE__ */ jsx("div", { className: "basis-1/3", children: item.name }),
        /* @__PURE__ */ jsx("div", { className: "basis-1/3", children: /* @__PURE__ */ jsx("div", { className: "flex flex-row justify-end gap-2", children: /* @__PURE__ */ jsxs("div", { className: "flex gap-2", children: [
          /* @__PURE__ */ jsx(
            ModifyButton,
            {
              size: "sm",
              radius: "full",
              isIconOnly: true,
              onPress: () => onSelect(item),
              variant: "secondary",
              className: "min-w-8 max-w-8"
            }
          ),
          /* @__PURE__ */ jsx(
            DeleteButton,
            {
              size: "sm",
              radius: "full",
              isIconOnly: true,
              onPress: () => onDelete(item),
              variant: "danger-soft",
              className: "min-w-8 max-w-8"
            }
          )
        ] }) }) })
      ] })
    },
    item.id
  );
};
const ResourceList = ({ resources = [], onSelect, onDelete, onChange, isSaving }) => {
  const [items, setItems] = useState(resources);
  useDeepCompareEffect(() => {
    setItems(resources);
  }, resources);
  const handleReorder = (itemsOrdered) => {
    const ordered = itemsOrdered.map((item, index) => ({
      ...item,
      order: index + 1
    }));
    setItems(ordered);
  };
  const handleDragEnd = () => {
    onChange && onChange(items);
  };
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col w-full", children: [
    isSaving && /* @__PURE__ */ jsxs(ProgressBar, { isIndeterminate: true, size: "sm", className: "max-w-md", color: "accent", children: [
      /* @__PURE__ */ jsx(Label, { children: "Saving order..." }),
      /* @__PURE__ */ jsx(ProgressBar.Track, { children: /* @__PURE__ */ jsx(ProgressBar.Fill, {}) })
    ] }),
    /* @__PURE__ */ jsx(Reorder.Group, { axis: "y", values: items, onReorder: handleReorder, children: items.map((item) => /* @__PURE__ */ jsx(
      ReorderItem$1,
      {
        item,
        onSelect,
        onDelete,
        onDragEnd: handleDragEnd
      },
      item.id
    )) })
  ] });
};
const ResourceListController = reactive.component(
  ({ services, data, apiRegistrationId, onSelect, onAdd, monitors, isSaving }) => {
    const [, setResources] = useState(data.resources);
    useEffect(() => setResources(data.resources), [data.resources]);
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(
        ResourceList,
        {
          resources: data.resources,
          onSelect: (item) => onSelect(item),
          onDelete: (item) => services.apiRegistrations.deleteApiRegistrationResource(apiRegistrationId, item._id),
          onChange: (items) => {
            const payload = items.map(({ _id, ...rest }) => ({
              ...rest,
              id: _id
            }));
            services.apiRegistrations.bulkUpdateApiRegistrationResources(
              apiRegistrationId,
              payload
            );
          },
          isSaving: monitors.bulkUpdateApiRegistrationResources.executing
        }
      ),
      /* @__PURE__ */ jsx("div", { className: "flex items-center justify-center w-full", children: /* @__PURE__ */ jsx(NewButton, { size: "sm", variant: "bordered", isIconOnly: true, onPress: () => onAdd && onAdd() }) })
    ] });
  },
  {
    name: "ResourceListController",
    sync: (services, { apiRegistrationId, forceRemote = false }) => [
      {
        execute: () => forceRemote && services.apiRegistrations.getApiRegistrationResources(apiRegistrationId),
        deps: [apiRegistrationId]
      }
    ],
    queries: ({ apiRegistrationId }) => [
      {
        name: "resources",
        execute: (db) => db.resources.where({ apiRegistrationId }).sortBy("order"),
        deps: [apiRegistrationId]
      }
    ],
    monitors: (services) => [
      services.apiRegistrations.bulkUpdateApiRegistrationResources,
      services.apiRegistrations.deleteApiRegistrationResource
    ]
  }
);
const ResourceListView = reactive.component(
  ({ apiRegistrationId, services, monitors }) => {
    const navigate = useNavigate();
    return /* @__PURE__ */ jsxs(FlatCard, { children: [
      /* @__PURE__ */ jsx(FlatCard.Header, { children: /* @__PURE__ */ jsx(FlatCard.Title, { children: "Resources" }) }),
      /* @__PURE__ */ jsxs(FlatCard.Main, { className: "flex flex-row gap-4", children: [
        /* @__PURE__ */ jsx("div", { className: "flex-grow", children: /* @__PURE__ */ jsx(
          ResourceListController,
          {
            apiRegistrationId,
            onAdd: () => navigate(RoutePaths().apis.createResource(apiRegistrationId)),
            onSelect: (item) => navigate(RoutePaths().apis.resourceDetails(apiRegistrationId, item._id))
          }
        ) }),
        /* @__PURE__ */ jsx(Outlet, {})
      ] })
    ] });
  },
  {
    name: "ResourceListView",
    monitors: (services) => [services.apiRegistrations.getApiRegistrationResources]
  }
);
const OperationCreateForm = ({ onChange }) => {
  return /* @__PURE__ */ jsxs(
    Uniformly,
    {
      className: "flex flex-row items-center w-full gap-2",
      name: "operation",
      onChange,
      children: [
        /* @__PURE__ */ jsx(
          TextField$1,
          {
            name: "keyCode",
            label: "Key Code",
            size: "sm",
            isClearable: true,
            isRequired: true,
            className: "w-full",
            validation: (x) => x.string().max(100).required("Key Code is Required")
          }
        ),
        /* @__PURE__ */ jsx(
          TextField$1,
          {
            name: "name",
            label: "Name",
            size: "sm",
            isClearable: true,
            isRequired: true,
            className: "w-full",
            validation: (x) => x.string().max(100).required("Name is Required")
          }
        )
      ]
    }
  );
};
const OperationCreateController = reactive.component(
  ({ resourceId, services, data, monitors, onSuccess }) => {
    const [form, setForm] = useState({});
    useSuccessReactor(services.operations.bulkUpdateResourceOperations, (payload) => {
      onSuccess && onSuccess(payload);
    });
    const handleSubmit = useCallback(
      (form2) => {
        const payload = data.operations.map(({ _id, ...rest }) => ({
          ...rest,
          id: _id
        })).concat({ resourceId, ...form2.data, id: void 0 });
        services.operations.bulkUpdateResourceOperations(resourceId, payload);
      },
      [data.operations, resourceId, services]
    );
    return /* @__PURE__ */ jsxs(FlatCard, { children: [
      /* @__PURE__ */ jsx(FlatCard.Main, { children: /* @__PURE__ */ jsx(OperationCreateForm, { onChange: setForm }) }),
      /* @__PURE__ */ jsx(FlatCard.Footer, { className: "flex justify-end items-center", children: /* @__PURE__ */ jsx(
        SaveButton,
        {
          color: "primary",
          size: "sm",
          isDisabled: !form.isValid,
          isLoading: monitors.bulkUpdateResourceOperations.executing,
          isIconOnly: true,
          onPress: () => handleSubmit(form)
        }
      ) })
    ] });
  },
  {
    name: "OperationCreateController",
    queries: ({ resourceId }) => [
      {
        name: "operations",
        execute: (db) => db.operations.where({ resourceId }).toArray(),
        deps: [resourceId]
      }
    ],
    monitors: (services) => [services.operations.bulkUpdateResourceOperations]
  }
);
const OperationUpdateForm = ({ operation, onChange }) => {
  return /* @__PURE__ */ jsxs(Uniformly, { className: "flex flex-row items-center gap-2", name: "operation", onChange, children: [
    /* @__PURE__ */ jsx(
      TextField$1,
      {
        name: "keyCode",
        label: "Key Code (read-only)",
        isReadOnly: true,
        size: "sm",
        value: operation.keyCode,
        fullWidth: true,
        validation: (x) => x.string().max(100).required("Key Code is Required")
      }
    ),
    /* @__PURE__ */ jsx(
      TextField$1,
      {
        name: "name",
        label: "Name",
        size: "sm",
        isRequired: true,
        value: operation.name,
        fullWidth: true,
        validation: (x) => x.string().max(100).required("Name is Required")
      }
    ),
    /* @__PURE__ */ jsx(SwitchField, { name: "isDeprecated", label: "Deprecated", value: operation.isDeprecated, children: "Deprecated" })
  ] });
};
const ReorderItemContent = ({ item, dragControls, onDelete, onChange }) => {
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-row items-center gap-2", children: [
    /* @__PURE__ */ jsx(
      "i",
      {
        className: "text-xl ri-draggable",
        style: { cursor: "pointer" },
        onPointerDown: (event) => dragControls.start(event)
      }
    ),
    /* @__PURE__ */ jsx("div", { className: "grow", children: /* @__PURE__ */ jsx(OperationUpdateForm, { operation: item, onChange }) }),
    /* @__PURE__ */ jsx(
      DeleteButton,
      {
        size: "sm",
        radius: "full",
        variant: "danger-soft",
        isIconOnly: true,
        className: "min-w-8 max-w-8",
        onPress: () => onDelete(item)
      }
    )
  ] });
};
const ReorderItem = ({ item, onDelete, onChange }) => {
  const y = useMotionValue(0);
  const dragControls = useDragControls();
  return /* @__PURE__ */ jsx(
    Reorder.Item,
    {
      value: item,
      style: { y },
      dragListener: false,
      dragControls,
      children: /* @__PURE__ */ jsx(
        ReorderItemContent,
        {
          item,
          dragControls,
          onDelete,
          onChange
        }
      )
    },
    item.id
  );
};
const OperationList = ({ operations = [], onDelete, onChange }) => {
  const [items, setItems] = useState(operations);
  useDeepCompareEffect(() => {
    operations[0];
    setItems(operations);
  }, operations);
  const updateItems = useCallback(
    (updated) => {
      setItems(updated);
      onChange && onChange(updated);
    },
    [onChange]
  );
  return /* @__PURE__ */ jsx(
    Reorder.Group,
    {
      className: "flex flex-col gap-4",
      axis: "y",
      values: items,
      onReorder: (itemsOrdered) => updateItems(itemsOrdered.map((item, index) => ({ ...item, order: ++index }))),
      children: items.map((item) => /* @__PURE__ */ jsx(
        ReorderItem,
        {
          item,
          onDelete,
          onChange: (item2) => {
            updateItems(
              items.map((x) => x.keyCode === item2.data.keyCode ? { ...x, ...item2.data } : x)
            );
          }
        },
        item.id
      ))
    }
  );
};
const OperationListController = reactive.component(
  ({ services, data, monitors, resourceId }) => {
    const [operations, setOperations] = useState(data.operations);
    useEffect(() => setOperations(data.operations), [data.operations]);
    return /* @__PURE__ */ jsxs(FlatCard, { children: [
      /* @__PURE__ */ jsx(FlatCard.Main, { children: /* @__PURE__ */ jsx(
        OperationList,
        {
          operations: data.operations,
          onDelete: (item) => services.operations.bulkUpdateResourceOperations(
            resourceId,
            operations.filter((op) => op.keyCode !== item.keyCode)
          ),
          onChange: (items) => setOperations(items.map(({ _id, ...rest }) => ({ ...rest, id: _id }))),
          isSaveStatus: monitors.bulkUpdateResourceOperations.executing
        }
      ) }),
      /* @__PURE__ */ jsx(FlatCard.Footer, { className: "flex items-center justify-end", children: /* @__PURE__ */ jsx(
        SaveButton,
        {
          size: "sm",
          onPress: () => services.operations.bulkUpdateResourceOperations(resourceId, operations),
          isLoading: monitors.bulkUpdateResourceOperations.executing
        }
      ) })
    ] });
  },
  {
    name: "OperationListController",
    sync: (services, { resourceId }) => [
      {
        execute: () => services.operations.getResourceOperations(resourceId),
        deps: [resourceId]
      }
    ],
    queries: ({ resourceId }) => [
      {
        name: "operations",
        execute: (db) => db.operations.where({ resourceId }).sortBy("order"),
        deps: [resourceId]
      }
    ],
    monitors: (services) => [services.operations.bulkUpdateResourceOperations]
  }
);
const ResourceUpdateForm = ({ resource, onChange }) => {
  return /* @__PURE__ */ jsxs(
    Uniformly,
    {
      name: "resource",
      className: "flex flex-col items-start w-full gap-2",
      onChange,
      children: [
        /* @__PURE__ */ jsx(
          TextField$1,
          {
            name: "name",
            label: "Name",
            size: "sm",
            isRequired: true,
            className: "grow w-full",
            value: resource?.name,
            validation: (x) => x.string().max(100).required("Name is Required")
          }
        ),
        /* @__PURE__ */ jsx(SwitchField, { name: "isDeprecated", label: "Deprecated", value: resource?.isDeprecated, children: "Deprecated" })
      ]
    }
  );
};
const ResourceUpdateController = reactive.component(
  ({ apiRegistrationId, resourceId, services, data, monitors, onSuccess }) => {
    const { resources } = data;
    const [_form, setForm] = useState({});
    const resource = useMemo(
      () => resources.find((x) => x._id === resourceId),
      [resourceId, resources]
    );
    useSuccessReactor(
      services.apiRegistrations.bulkUpdateApiRegistrationResources,
      (payload) => {
        onSuccess && onSuccess(payload);
      },
      [apiRegistrationId]
    );
    return /* @__PURE__ */ jsxs(FlatCard, { children: [
      /* @__PURE__ */ jsx(FlatCard.Main, { children: /* @__PURE__ */ jsx(ResourceUpdateForm, { resource, onChange: setForm }) }),
      /* @__PURE__ */ jsx(FlatCard.Footer, { className: "flex justify-end items-center", children: /* @__PURE__ */ jsx(
        SaveButton,
        {
          size: "sm",
          isDisabled: !_form.isValid,
          isLoading: monitors.bulkUpdateApiRegistrationResources.executing,
          onPress: () => {
            const payload = resources.map(
              ({ _id, ...rest }) => _id === resourceId ? {
                ...resource,
                ..._form.data,
                apiRegistrationId,
                id: resourceId
              } : { ...rest, id: _id }
            );
            services.apiRegistrations.bulkUpdateApiRegistrationResources(
              apiRegistrationId,
              payload
            );
          }
        }
      ) })
    ] });
  },
  {
    name: "ResourceUpdateController",
    queries: ({ apiRegistrationId }) => [
      {
        name: "resources",
        execute: (db) => db.resources.where({ apiRegistrationId }).toArray(),
        deps: [apiRegistrationId]
      }
    ],
    monitors: (services) => [services.apiRegistrations.bulkUpdateApiRegistrationResources]
  }
);
const ResourceUpdateView = ({ apiRegistrationId, resourceId }) => {
  const navigate = useNavigate();
  const Wrapper = withDrawerController(({ onSuccess }) => {
    return /* @__PURE__ */ jsxs("div", { className: "flex flex-col w-full mx-auto gap-y-4", children: [
      /* @__PURE__ */ jsx(
        ResourceUpdateController,
        {
          apiRegistrationId,
          resourceId,
          onSuccess
        }
      ),
      /* @__PURE__ */ jsx(Separator, {}),
      /* @__PURE__ */ jsx("h2", { className: "text-md", children: "New Operation" }),
      /* @__PURE__ */ jsx(OperationCreateController, { resourceId }),
      /* @__PURE__ */ jsx(Separator, {}),
      /* @__PURE__ */ jsx(OperationListController, { resourceId })
    ] });
  });
  return /* @__PURE__ */ jsx(
    Wrapper,
    {
      isOpen: true,
      isDismissable: true,
      headerContent: () => "Update Resource",
      apiRegistrationId,
      resourceId,
      size: "5xl",
      onClose: () => navigate(RoutePaths().apis.resources(apiRegistrationId))
    }
  );
};
const buildPolicies$1 = (policies) => policies?.reduce((acc, { operationId, contextId }) => {
  acc[`${operationId}_selected`] = true;
  acc[`${operationId}_contextId`] = contextId;
  return acc;
}, {}) || {};
const RolePolicyForm = ({ name, resourceId, operations, policies, onChange }) => {
  const [policiesForm, setPoliciesForm] = useState(buildPolicies$1(policies));
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useDeepCompareEffect(() => {
    setPoliciesForm(buildPolicies$1(policies));
  }, [policies]);
  useEffect(() => {
    const rolePolicies = Object.keys(policiesForm).filter((key, value) => key.includes("_selected") && !!policiesForm[key]).map((key) => {
      const operationId = key.split("_")[0];
      return {
        operationId: Number(operationId),
        contextId: Number(policiesForm[`${operationId}_contextId`])
      };
    });
    onChangeRef.current(resourceId, rolePolicies);
  }, [policiesForm]);
  return /* @__PURE__ */ jsx(
    Uniformly,
    {
      name,
      onChange: (form) => {
        setPoliciesForm(form.data);
      },
      children: /* @__PURE__ */ jsx("div", { className: "flex flex-col gap-2", children: operations.map((item) => /* @__PURE__ */ jsxs("div", { className: "flex flex-row items-center justify-between", children: [
        /* @__PURE__ */ jsxs(
          SwitchField,
          {
            name: `${item.id}_selected`,
            value: !!policies.find((x) => x.operationId === item.id),
            children: [
              item.name,
              " ",
              /* @__PURE__ */ jsx(DeprecatedChip, { isDeprecated: item.isDeprecated, className: "ms-2" })
            ]
          }
        ),
        /* @__PURE__ */ jsx(
          ContextsLookup,
          {
            name: `${item.id}_contextId`,
            label: "Context",
            isDisabled: !policiesForm[`${item.id}_selected`],
            value: policies.find((x) => x.operationId === item.id)?.contextId,
            className: "min-w-30 max-w-48"
          }
        )
      ] }, item.id)) })
    }
  );
};
const buildPolicies = (policies) => policies.map(({ operationId, contextId, resourceId }) => ({
  operationId,
  contextId,
  resourceId
}));
const RolePolicyList = ({ policies, resources, onChange, isLoading }) => {
  const [rolePolicies, setRolePolicies] = useState(buildPolicies(policies));
  useEffect(() => {
    setRolePolicies(buildPolicies(policies));
  }, [policies]);
  useEffect(() => {
    onChange(rolePolicies);
  }, [rolePolicies]);
  const updateRolePolicies = (resourceId, items) => {
    setRolePolicies((prev) => {
      const filtered = prev.filter((p) => p.resourceId !== resourceId);
      return [...filtered, ...items.map((i) => ({ ...i, resourceId }))];
    });
  };
  if (isLoading) {
    return /* @__PURE__ */ jsxs(Alert, { status: "accent", children: [
      /* @__PURE__ */ jsx(Alert.Indicator, {}),
      /* @__PURE__ */ jsxs(Alert.Content, { children: [
        /* @__PURE__ */ jsx(Alert.Title, { children: "Loading policies..." }),
        /* @__PURE__ */ jsx(Alert.Description, { children: "Please wait while we load the available policies." })
      ] })
    ] });
  }
  if (isEmpty(resources)) {
    return /* @__PURE__ */ jsxs(Alert, { status: "accent", children: [
      /* @__PURE__ */ jsx(Alert.Indicator, {}),
      /* @__PURE__ */ jsxs(Alert.Content, { children: [
        /* @__PURE__ */ jsx(Alert.Title, { children: "No resources found" }),
        /* @__PURE__ */ jsx(Alert.Description, { children: "You will need to create resources and operations before configuring roles" })
      ] })
    ] });
  }
  return /* @__PURE__ */ jsx("div", { className: "flex flex-col gap-2", children: resources.filter(({ operations }) => !isEmpty(operations)).filter((x) => !x.isDeprecated).map(({ _id, name, operations, isDeprecated, ...rest }) => {
    return /* @__PURE__ */ jsxs(Card, { className: "grow-0", children: [
      /* @__PURE__ */ jsxs(Card.Header, { children: [
        name,
        " ",
        /* @__PURE__ */ jsx(DeprecatedChip, { isDeprecated, className: "ms-2" })
      ] }),
      /* @__PURE__ */ jsx(Card.Content, { children: /* @__PURE__ */ jsx(
        RolePolicyForm,
        {
          name,
          resourceId: _id,
          operations: operations.filter((x) => !x.isDeprecated).sort((a, b) => a.order - b.order),
          policies: policies.filter((p) => p.resourceId === _id),
          onChange: updateRolePolicies
        }
      ) })
    ] }, _id);
  }) });
};
const RolePermissionsView = reactive.component(
  ({ apiRegistrationId, roleId, services, data, monitors }) => {
    const { resources } = useActiveApi();
    const [policies, setPolicies] = useState([]);
    const { rolePolicies } = data;
    return /* @__PURE__ */ jsxs(FlatCard, { children: [
      /* @__PURE__ */ jsx(FlatCard.Header, { className: "flex flex-row items-center justify-between gap-2", children: /* @__PURE__ */ jsx(FlatCard.Title, { children: "Permissions" }) }),
      /* @__PURE__ */ jsx(FlatCard.Main, { children: /* @__PURE__ */ jsx(
        RolePolicyList,
        {
          resources,
          policies: data.rolePolicies,
          onChange: (settings) => setPolicies(settings),
          isLoading: monitors.getRolePolicies.executing
        }
      ) }),
      /* @__PURE__ */ jsx(FlatCard.Footer, { className: "flex items-center justify-end p-2", children: /* @__PURE__ */ jsx(
        SaveButton,
        {
          size: "sm",
          isLoading: monitors.setRolePolicies.executing,
          isDisabled: monitors.getRolePolicies.executing,
          onPress: () => services.roles.setRolePolicies(roleId, policies)
        }
      ) })
    ] });
  },
  {
    name: "RolePermissionsView",
    sync: (services, { apiRegistrationId, roleId }) => [
      {
        execute: () => services.roles.getRolePolicies(roleId),
        bypass: (db) => db.rolePolicies.where({ roleId }).count(),
        deps: [apiRegistrationId, roleId]
      }
    ],
    queries: ({ apiRegistrationId, roleId }) => [
      {
        name: "rolePolicies",
        execute: (db) => db.rolePolicies.where({ roleId }).toArray(),
        deps: [apiRegistrationId, roleId]
      }
    ],
    monitors: (services) => [
      services.roles.setRolePolicies,
      services.roles.getRolePolicies,
      services.apiRegistrations.getApiRegistrationResources
    ]
  }
);
const RoleForm = ({ role, onChange }) => /* @__PURE__ */ jsxs(Uniformly, { className: "flex flex-col flex-grow gap-2", name: "roleForm", onChange, children: [
  /* @__PURE__ */ jsx(
    TextField$1,
    {
      name: "name",
      label: "Name",
      isRequired: true,
      value: role?.name,
      validation: (x) => x.string().max(100).required("Name is Required")
    }
  ),
  /* @__PURE__ */ jsx(SwitchField, { name: "isPrivate", value: role?.isPrivate || false, children: "Private" })
] });
const RoleCreateController = reactive.component(
  ({ apiRegistrationId, services, monitors, onSuccess }) => {
    const [form, setForm] = useState({});
    useSuccessReactor(services.apiRegistrations.createRole, (payload) => {
      onSuccess && onSuccess(payload);
    });
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(RoleForm, { onChange: setForm }),
      /* @__PURE__ */ jsx("div", { className: "text-end pb-2", children: /* @__PURE__ */ jsx(
        SaveButton,
        {
          size: "sm",
          isDisabled: !form.isValid,
          isLoading: monitors.createRole.executing,
          onPress: () => services.apiRegistrations.createRole(apiRegistrationId, {
            ...form.data,
            apiRegistrationId
          })
        }
      ) })
    ] });
  },
  {
    name: "RoleCreateController",
    monitors: (services) => [services.apiRegistrations.createRole]
  }
);
RoleCreateController.Drawer = withDrawerController(({ onSuccess, ...props }) => /* @__PURE__ */ jsx(RoleCreateController, { onSuccess, ...props }));
const RoleUpdateController = reactive.component(
  ({ apiRegistrationId, item: roleId, services, data, monitors, onSuccess }) => {
    const { role } = data;
    const [form, setForm] = useState({});
    useSuccessReactor(services.roles.updateRole, (payload) => {
      onSuccess && onSuccess(payload);
    });
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(RoleForm, { role, onChange: setForm }),
      /* @__PURE__ */ jsx("div", { className: "text-end pb-2", children: /* @__PURE__ */ jsx(
        SaveButton,
        {
          size: "sm",
          isDisabled: !form.isValid,
          isLoading: monitors.updateRole.executing,
          onPress: () => services.roles.updateRole(roleId, {
            ...form.data,
            id: roleId,
            apiRegistrationId
          })
        }
      ) })
    ] });
  },
  {
    name: "RoleUpdateController",
    queries: ({ item: roleId }) => [
      {
        name: "role",
        execute: (db) => db.roles.get(roleId),
        deps: [roleId]
      }
    ],
    monitors: (services) => [services.roles.updateRole]
  }
);
RoleUpdateController.Drawer = withDrawerController(({ onSuccess, ...props }) => /* @__PURE__ */ jsx(RoleUpdateController, { onSuccess, ...props }));
const RoleList = ({ roles, selectedRoleId, onChange, onModify, onDelete, onAdd }) => {
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      ListBox,
      {
        "aria-label": "options",
        disallowEmptySelection: true,
        selectionMode: "single",
        selectedKeys: [selectedRoleId?.toString()],
        onSelectionChange: (items) => onChange && onChange(Array.from(items)[0]),
        children: roles?.map((role) => /* @__PURE__ */ jsx(ListBox.Item, { id: role.id.toString(), textValue: role.name, children: /* @__PURE__ */ jsxs("div", { className: "flex flex-row items-center justify-between w-full", children: [
          /* @__PURE__ */ jsxs("div", { className: "flex flex-row items-center justify-start gap-2", children: [
            /* @__PURE__ */ jsx("i", { className: "ri-group-line" }),
            /* @__PURE__ */ jsx(Label, { children: role.name })
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "flex flex-row items-center justify-end gap-2", children: [
            /* @__PURE__ */ jsx(
              ModifyButton,
              {
                size: "sm",
                variant: "secondary",
                radius: "full",
                className: "max-w-8 min-w-8",
                isIconOnly: true,
                onPress: () => onModify && onModify(role.id)
              }
            ),
            /* @__PURE__ */ jsx(
              DeleteButton,
              {
                size: "sm",
                variant: "danger-soft",
                radius: "full",
                isIconOnly: true,
                onPress: () => onDelete && onDelete(role.id)
              }
            )
          ] })
        ] }) }, role.id.toString()))
      }
    ),
    /* @__PURE__ */ jsx("div", { className: "flex w-full justify-center", children: /* @__PURE__ */ jsx(
      NewButton,
      {
        label: "New Role",
        size: "sm",
        isIconOnly: true,
        variant: "secondary",
        onPress: () => onAdd && onAdd()
      }
    ) })
  ] });
};
const RoleListView = reactive.component(
  ({ apiRegistrationId, roleId, services, monitors }) => {
    const navigate = useNavigate();
    const { roles } = useActiveApi();
    const [createDrawer, CreateRoleDrawer] = useDrawerController(RoleCreateController.Drawer, {
      headerContent: () => "Create Role",
      size: "md",
      isOpen: false
    });
    const [updateDrawer, UpdateRoleDrawer] = useDrawerController(RoleUpdateController.Drawer, {
      headerContent: () => "Update Role",
      size: "md",
      isOpen: false
    });
    return /* @__PURE__ */ jsxs(SplitCard, { variant: "transparent", children: [
      /* @__PURE__ */ jsx(SplitCard.Header, { children: /* @__PURE__ */ jsx(SplitCard.Title, { children: "Roles" }) }),
      /* @__PURE__ */ jsxs(SplitCard.Body, { children: [
        /* @__PURE__ */ jsxs(SplitCard.Sidebar, { children: [
          /* @__PURE__ */ jsx(CreateRoleDrawer, { apiRegistrationId }),
          /* @__PURE__ */ jsx(UpdateRoleDrawer, { apiRegistrationId }),
          /* @__PURE__ */ jsx(
            RoleList,
            {
              selectedRoleId: roleId,
              roles,
              onChange: (id) => navigate(RoutePaths().apis.roles(apiRegistrationId, id)),
              onModify: (id) => updateDrawer.open(id),
              onDelete: (id) => services.roles.deleteRole(id),
              onAdd: () => createDrawer.open()
            }
          )
        ] }),
        /* @__PURE__ */ jsx(SplitCard.Main, { children: /* @__PURE__ */ jsx(Outlet, {}) })
      ] })
    ] });
  },
  {
    name: "RoleListView",
    monitors: (services) => [services.apiRegistrations.getRoles]
  }
);
const CallbacksForm = ({
  apiRegistrationId,
  isLoading,
  environment,
  callbacks = [],
  onChange
}) => {
  const [, setCallbacks] = useState({});
  const filtered = useMemo(
    () => callbacks.filter((x) => x.environment === environment),
    [callbacks, environment]
  );
  const findExistingOrDefault = (callbackType) => filtered.find((x) => x.callbackType === callbackType) || {
    id: 0,
    apiRegistrationId,
    callbackType,
    url: ""
  };
  const handleChange = (form) => {
    setCallbacks((curr) => {
      const updated = { ...curr, [form.data.callbackType]: form.data };
      const result = Object.values(updated).filter((x) => !isEmpty(x.url)).map((x) => {
        if (x.id === 0) delete x.id;
        return x;
      });
      onChange(result);
      return updated;
    });
  };
  return /* @__PURE__ */ jsx("div", { className: "p-2 sm:p-4", children: /* @__PURE__ */ jsx("div", { className: "space-y-3 sm:space-y-4", children: CallbackTypeOptions.map((callbackType) => {
    const existing = findExistingOrDefault(callbackType.id);
    return /* @__PURE__ */ jsxs(Card, { className: "p-3 sm:p-4", children: [
      /* @__PURE__ */ jsxs("div", { className: "mb-2", children: [
        /* @__PURE__ */ jsx("h3", { className: "text-base sm:text-lg font-medium", children: callbackType.type }),
        /* @__PURE__ */ jsx("p", { className: "text-xs sm:text-sm text-gray-600 mb-2", children: callbackType.description })
      ] }),
      /* @__PURE__ */ jsx("div", { className: "flex flex-col sm:flex-row gap-2 w-full", children: /* @__PURE__ */ jsxs(Uniformly, { name: "callbacks", onChange: handleChange, className: "w-full", children: [
        /* @__PURE__ */ jsx(TextField$1, { name: "id", className: "hidden", value: existing.id, type: "number" }),
        /* @__PURE__ */ jsx(
          TextField$1,
          {
            name: "apiRegistrationId",
            className: "hidden",
            value: existing.apiRegistrationId,
            type: "number"
          }
        ),
        /* @__PURE__ */ jsx(
          TextField$1,
          {
            name: "callbackType",
            className: "flex-grow hidden",
            value: callbackType.id,
            type: "number"
          }
        ),
        /* @__PURE__ */ jsx(TextField$1, { name: "environment", className: "flex-grow hidden", value: environment }),
        /* @__PURE__ */ jsx(
          UrlField,
          {
            isDisabled: isLoading,
            name: "url",
            className: "w-full",
            isClearable: true,
            value: existing.url
          }
        )
      ] }) })
    ] }, callbackType.id);
  }) }) });
};
const CallbacksController = withActiveApi(
  reactive.component(
    ({ data, apiRegistration, services, monitors }) => {
      const [form, setForm] = useState({
        [EnvironmentType.Development]: [],
        [EnvironmentType.Integration]: [],
        [EnvironmentType.Staging]: [],
        [EnvironmentType.Production]: []
      });
      const handleSave = () => {
        const payload = Object.values(form).reduce((prev, curr) => prev.concat(curr), []);
        services.apiRegistrations.updateApiRegistrationCallbacks(apiRegistration.id, payload);
      };
      const handleChange = (environment, callbacks) => {
        setForm((curr) => ({ ...curr, [environment]: callbacks }));
      };
      const loading = monitors.getApiRegistrationCallbacks.executing || monitors.updateApiRegistrationCallbacks.executing;
      return /* @__PURE__ */ jsxs("div", { className: "flex flex-col w-full", children: [
        /* @__PURE__ */ jsxs(Tabs, { className: "w-full overflow-x-auto", children: [
          /* @__PURE__ */ jsx(Tabs.ListContainer, { children: /* @__PURE__ */ jsxs(Tabs.List, { "aria-label": "Webhooks by Environment", children: [
            /* @__PURE__ */ jsxs(Tabs.Tab, { id: EnvironmentType.Development, children: [
              "Development",
              /* @__PURE__ */ jsx(Tabs.Indicator, {})
            ] }),
            /* @__PURE__ */ jsxs(Tabs.Tab, { id: EnvironmentType.Integration, children: [
              "Integration",
              /* @__PURE__ */ jsx(Tabs.Indicator, {})
            ] }),
            /* @__PURE__ */ jsxs(Tabs.Tab, { id: EnvironmentType.Staging, children: [
              "Staging",
              /* @__PURE__ */ jsx(Tabs.Indicator, {})
            ] }),
            /* @__PURE__ */ jsxs(Tabs.Tab, { id: EnvironmentType.Production, children: [
              "Production",
              /* @__PURE__ */ jsx(Tabs.Indicator, {})
            ] })
          ] }) }),
          /* @__PURE__ */ jsx(Tabs.Panel, { id: EnvironmentType.Development, children: /* @__PURE__ */ jsx(
            CallbacksForm,
            {
              callbacks: data.callbacks,
              apiRegistrationId: apiRegistration.id,
              environment: EnvironmentType.Development,
              isLoading: loading,
              onChange: (x) => handleChange(EnvironmentType.Development, x)
            }
          ) }),
          /* @__PURE__ */ jsx(Tabs.Panel, { id: EnvironmentType.Integration, children: /* @__PURE__ */ jsx(
            CallbacksForm,
            {
              callbacks: data.callbacks,
              apiRegistrationId: apiRegistration.id,
              environment: EnvironmentType.Integration,
              isLoading: loading,
              onChange: (x) => handleChange(EnvironmentType.Integration, x)
            }
          ) }),
          /* @__PURE__ */ jsx(Tabs.Panel, { id: EnvironmentType.Staging, children: /* @__PURE__ */ jsx(
            CallbacksForm,
            {
              callbacks: data.callbacks,
              apiRegistrationId: apiRegistration.id,
              environment: EnvironmentType.Staging,
              isLoading: loading,
              onChange: (x) => handleChange(EnvironmentType.Staging, x)
            }
          ) }),
          /* @__PURE__ */ jsx(Tabs.Panel, { id: EnvironmentType.Production, children: /* @__PURE__ */ jsx(
            CallbacksForm,
            {
              callbacks: data.callbacks,
              apiRegistrationId: apiRegistration.id,
              environment: EnvironmentType.Production,
              isLoading: loading,
              onChange: (x) => handleChange(EnvironmentType.Production, x)
            }
          ) })
        ] }),
        /* @__PURE__ */ jsx("div", { className: "flex align-center justify-end", children: /* @__PURE__ */ jsx(SaveButton, { size: "sm", onPress: handleSave, isLoading: loading }) })
      ] });
    },
    {
      name: "CallbacksController",
      sync: (services, { apiRegistration }) => [
        {
          execute: () => {
            return services.apiRegistrations.getApiRegistrationCallbacks(apiRegistration.id);
          },
          bypass: (db) => db.callbacks.where({ apiRegistrationId: apiRegistration.id }).count(),
          deps: [apiRegistration.id]
        }
      ],
      queries: ({ apiRegistration }) => [
        {
          name: "callbacks",
          execute: (db) => db.callbacks.where({ apiRegistrationId: apiRegistration.id }).toArray(),
          deps: [apiRegistration.id]
        }
      ],
      monitors: (services) => [
        services.apiRegistrations.updateApiRegistrationCallbacks,
        services.apiRegistrations.getApiRegistrationCallbacks
      ]
    }
  )
);
const CallbacksView = reactive.component(
  ({ apiRegistrationId, services, monitors }) => {
    return /* @__PURE__ */ jsxs(FlatCard, { children: [
      /* @__PURE__ */ jsxs(FlatCard.Header, { className: "flex flex-row items-center gap-1", children: [
        /* @__PURE__ */ jsx("div", { className: "text-lg", children: "Webhooks" }),
        /* @__PURE__ */ jsx(
          ReloadButton,
          {
            size: "sm",
            variant: "light",
            radius: "full",
            isIconOnly: true,
            isLoading: monitors.getApiRegistrationCallbacks.executing,
            onPress: () => services.apiRegistrations.getApiRegistrationCallbacks(apiRegistrationId)
          }
        )
      ] }),
      /* @__PURE__ */ jsx(FlatCard.Main, { className: "flex flex-row gap-4", children: /* @__PURE__ */ jsx(CallbacksController, {}) })
    ] });
  },
  {
    name: "CallbacksView",
    monitors: (services) => [services.apiRegistrations.getApiRegistrationCallbacks]
  }
);
const DeprecatedChip = ({ isDeprecated, ...rest }) => isDeprecated && /* @__PURE__ */ jsx(Chip, { size: "sm", color: "warning", ...rest, children: "Deprecated" });
const params = {
  route: {
    number: ["apiRegistrationId", "roleId", "resourceId"]
  }
};
const ApiRegistrationRouter = () => /* @__PURE__ */ jsxs(Routes, { children: [
  /* @__PURE__ */ jsx(Route, { path: "/", element: /* @__PURE__ */ jsx(ApiListView, {}), children: /* @__PURE__ */ jsx(Route, { path: "create", element: /* @__PURE__ */ jsx(ApiCreateView, {}) }) }),
  /* @__PURE__ */ jsxs(
    Route,
    {
      path: ":apiRegistrationId",
      element: /* @__PURE__ */ jsx(Params, { params, component: ApiDetailsView }),
      children: [
        /* @__PURE__ */ jsx(Route, { index: true, element: /* @__PURE__ */ jsx(Params, { params, component: ApiUpdateView }) }),
        /* @__PURE__ */ jsxs(Route, { path: "resources", element: /* @__PURE__ */ jsx(Params, { params, component: ResourceListView }), children: [
          /* @__PURE__ */ jsx(Route, { path: "create", element: /* @__PURE__ */ jsx(Params, { params, component: ResourceCreateView }) }),
          /* @__PURE__ */ jsx(
            Route,
            {
              path: ":resourceId",
              element: /* @__PURE__ */ jsx(Params, { params, component: ResourceUpdateView })
            }
          )
        ] }),
        /* @__PURE__ */ jsx(Route, { path: "roles", element: /* @__PURE__ */ jsx(Params, { params, component: RoleListView }), children: /* @__PURE__ */ jsx(
          Route,
          {
            path: ":roleId",
            element: /* @__PURE__ */ jsx(Params, { params, component: RolePermissionsView })
          }
        ) }),
        /* @__PURE__ */ jsx(Route, { path: "callbacks", element: /* @__PURE__ */ jsx(Params, { params, component: CallbacksView }) })
      ]
    }
  )
] });
const ApiResourcesColumns = [
  { propertyName: "name", name: "Name", isSortable: true, isResizable: true, minWidth: 100 },
  {
    propertyName: "displayName",
    name: "Display Name",
    isSortable: true,
    isResizable: true,
    minWidth: 100
  },
  { propertyName: "nonEditable", name: "Locked", isSortable: false },
  { propertyName: "enabled", name: "Enabled", isSortable: false },
  { propertyName: "showInDiscoveryDocument", name: "Show In Discovery Document", isSortable: false }
];
const ApiResourcesList = ({ apiResources = [], onSelect }) => {
  const renderCell = useCallback((row, columnKey) => {
    const cellValue = row[columnKey];
    switch (columnKey) {
      case "nonEditable":
      case "enabled":
      case "showInDiscoveryDocument":
        return /* @__PURE__ */ jsxs(Chip, { size: "sm", color: cellValue ? "success" : "danger", children: [
          /* @__PURE__ */ jsx(RiCircleFill, { size: "8" }),
          /* @__PURE__ */ jsx(Chip.Label, { className: "capitalize", children: cellValue ? "Yes" : "No" })
        ] });
      default:
        return cellValue;
    }
  }, []);
  const columns2 = useMemo(
    () => ApiResourcesColumns.map((column) => ({
      id: column.propertyName,
      header: column.name,
      // `name` is the row's accessible identifier; sorting flows through the
      // request broker via each column's `allowsSorting` + matching `id`.
      isRowHeader: column.propertyName === "name",
      allowsSorting: column.isSortable,
      allowsResizing: column.isResizable,
      minWidth: column.minWidth,
      cell: (row) => renderCell(row, column.propertyName)
    })),
    [renderCell]
  );
  return /* @__PURE__ */ jsx(
    RequestBrokerDataGrid,
    {
      "aria-label": "Api resources table",
      chipScope: "global",
      columns: columns2,
      data: apiResources,
      getRowId: (row) => row.id,
      allowsColumnResize: true,
      onRowAction: (key) => {
        const selected = apiResources.find((item) => item.id === key);
        if (selected) onSelect(selected);
      }
    }
  );
};
const ApiResourcesFilters = new Filters([
  new FilterInput({
    name: "Name",
    title: "Name",
    defaultValue: ""
  }),
  new FilterInput({
    name: "DisplayName",
    title: "Display Name",
    defaultValue: ""
  }),
  new FilterToggle({
    name: "Enabled",
    title: "Enabled",
    defaultValue: true
  }),
  new FilterToggle({
    name: "NonEditable",
    title: "Read-Only",
    defaultValue: false
  }),
  new FilterToggle({
    name: "ShowInDiscoveryDocument",
    title: "Show In Discovery Document",
    defaultValue: true
  })
]);
const ApiResourcesListController = reactive.component(
  ({ data, services, monitors, db, onSelect }) => {
    const { apiResources } = data;
    const handleExecute = useCallback((action, metadata) => {
      action(metadata);
    }, []);
    return /* @__PURE__ */ jsx(
      RequestBroker,
      {
        actionKey: "queryApiResources",
        action: services.apiResources.queryApiResources,
        filters: ApiResourcesFilters,
        isExecuting: monitors.queryApiResources.executing,
        onExecute: handleExecute,
        skipInitial: () => db.apiResources.count(),
        children: /* @__PURE__ */ jsx(
          ApiResourcesList,
          {
            apiResources,
            onSelect: (item) => onSelect && onSelect(item)
          }
        )
      }
    );
  },
  {
    name: "ApiResourcesListController",
    queries: (_, services) => [
      {
        name: "apiResources",
        execute: (db) => db.withRequestBroker(
          services.apiResources.queryApiResources.action,
          () => db.apiResources,
          {
            sorting: true
          }
        )
      }
    ],
    monitors: (services) => [services.apiResources.queryApiResources]
  }
);
const ApiResourcesListView = () => {
  const navigate = useNavigate();
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("div", { className: "flex flex-row justify-between mb-4", children: /* @__PURE__ */ jsxs(Breadcrumbs, { className: "content-center", children: [
      /* @__PURE__ */ jsx(BreadcrumbsItem, { isDisabled: true, children: "Auth" }),
      /* @__PURE__ */ jsx(BreadcrumbsItem, { isDisabled: true, children: "Identity Server" }),
      /* @__PURE__ */ jsx(BreadcrumbsItem, { children: "Api Resources" })
    ] }) }),
    /* @__PURE__ */ jsx(
      ApiResourcesListController,
      {
        onSelect: (item) => navigate(RoutePaths().apiResources.details(item.id))
      }
    ),
    /* @__PURE__ */ jsx(Outlet, {})
  ] });
};
const ApiResourceDetailsForm = ({ apiResource, onChange, validApiResource, onValidate }) => {
  const isValidName = !apiResource?.name ? validApiResource?.status : validApiResource?.status && !!validApiResource?.apiResourceName && apiResource?.name !== validApiResource?.apiResourceName;
  const errorMessageName = !apiResource?.name ? validApiResource?.status ? "Api Resource Name already exists" : null : validApiResource?.status && !!validApiResource?.apiResourceName && apiResource?.name !== validApiResource?.apiResourceName ? "Api Resource Name already exists" : null;
  return /* @__PURE__ */ jsxs(
    Uniformly,
    {
      className: "flex flex-col gap-2",
      name: "apiResourceForm",
      onChange: (form) => {
        form.isValid = form.isValid && !isValidName;
        onChange(form);
      },
      deps: [isValidName],
      children: [
        /* @__PURE__ */ jsx(
          TextField$1,
          {
            name: "name",
            label: "Name",
            value: apiResource?.name ? apiResource?.name : "api://",
            isInvalid: isValidName,
            onBlur: ({ target }) => onValidate(target.value),
            errorMessage: errorMessageName,
            isRequired: true,
            validation: (x) => x.string().max(100).required("Name is required")
          }
        ),
        /* @__PURE__ */ jsx(
          TextField$1,
          {
            name: "description",
            label: "Description",
            value: apiResource?.description,
            validation: (x) => x.string().max(200)
          }
        ),
        /* @__PURE__ */ jsx(
          TextField$1,
          {
            name: "displayName",
            label: "Display Name",
            value: apiResource?.displayName,
            validation: (x) => x.string().max(100)
          }
        ),
        /* @__PURE__ */ jsx(SwitchField, { name: "enabled", value: apiResource?.enabled || false, children: "Enabled" }),
        /* @__PURE__ */ jsx(
          SwitchField,
          {
            name: "showInDiscoveryDocument",
            value: apiResource?.showInDiscoveryDocument || false,
            children: "Show in Document"
          }
        ),
        /* @__PURE__ */ jsx(SwitchField, { name: "nonEditable", value: apiResource?.nonEditable || false, children: "Read-Only" }),
        /* @__PURE__ */ jsx(ArrayField, { name: "scopes", label: "Scopes", value: apiResource?.scopes ?? [] }),
        /* @__PURE__ */ jsx(ArrayField, { name: "userClaims", label: "User Claims", value: apiResource?.userClaims ?? [] }),
        /* @__PURE__ */ jsx(DictionaryField, { name: "properties", label: "Properties", value: apiResource?.properties ?? {} })
      ]
    }
  );
};
const ApiResourceUpdate = ({ apiResource, onChange, validApiResource, onValidate }) => /* @__PURE__ */ jsx(
  ApiResourceDetailsForm,
  {
    apiResource,
    onChange,
    validApiResource,
    onValidate
  }
);
ApiResourceUpdate.Drawer = withDrawerController(ApiResourceUpdate);
const ApiResourceUpdateView = reactive.component(
  ({ services, monitors, data, apiResourceId }) => {
    apiResourceId = Number(apiResourceId);
    const navigate = useNavigate();
    const { apiResource } = data;
    const [form, setForm] = useState({});
    const [hasApiResource, setHasApiResource] = useState(null);
    const title = "Update API Resource";
    const goToList = () => navigate(RoutePaths().apiResources.list());
    useSuccessReactor(services.apiResources.apiResourceOperation, (payload) => {
      setHasApiResource(payload);
    });
    useSuccessReactor(services.apiResources.updateApiResource, goToList);
    useSuccessReactor(services.apiResources.deleteApiResource, goToList);
    return /* @__PURE__ */ jsx(
      ApiResourceUpdate.Drawer,
      {
        isOpen: true,
        size: "3xl",
        apiResource,
        onChange: setForm,
        validApiResource: hasApiResource,
        onValidate: (value) => services.apiResources.apiResourceOperation("validate-resource-name", value),
        headerContent: () => apiResource?.nonEditable ? /* @__PURE__ */ jsxs("div", { className: "flex flex-row items-center gap-x-2", children: [
          /* @__PURE__ */ jsx(Chip, { color: "danger", children: "Read Only" }),
          title
        ] }) : title,
        footerContent: () => /* @__PURE__ */ jsxs("div", { className: "flex w-full justify-between", children: [
          /* @__PURE__ */ jsxs("div", { children: [
            !apiResource.nonEditable && /* @__PURE__ */ jsxs(Tooltip, { delay: 0, children: [
              /* @__PURE__ */ jsx(
                DeleteButton,
                {
                  label: "",
                  size: "sm",
                  isDisabled: apiResource.nonEditable,
                  onPress: () => services.apiResources.deleteApiResource(apiResource.id),
                  isLoading: monitors.deleteApiResource.executing
                }
              ),
              /* @__PURE__ */ jsx(Tooltip.Content, { children: "Delete" })
            ] }),
            apiResource.nonEditable && /* @__PURE__ */ jsxs(Tooltip, { delay: 0, children: [
              /* @__PURE__ */ jsx(
                Button,
                {
                  isIconOnly: true,
                  size: "sm",
                  variant: "primary",
                  onPress: () => services.apiResources.apiResourceOperation("unlock", apiResource.id),
                  isPending: monitors.apiResourceOperation.executing,
                  children: ({ isPending }) => isPending ? /* @__PURE__ */ jsx(Spinner, { size: "sm", color: "current" }) : /* @__PURE__ */ jsx("i", { className: "ri-lock-unlock-fill" })
                }
              ),
              /* @__PURE__ */ jsx(Tooltip.Content, { children: "Unlock" })
            ] })
          ] }),
          /* @__PURE__ */ jsx(ErrorListChip, { errors: form.errors }),
          /* @__PURE__ */ jsx(
            SaveButton,
            {
              size: "sm",
              isDisabled: apiResource.nonEditable || !form.isValid,
              onPress: () => services.apiResources.updateApiResource(apiResourceId, {
                ...form.data
              }),
              isLoading: monitors.updateApiResource.executing
            }
          )
        ] }),
        onClose: goToList
      }
    );
  },
  {
    name: "ApiResourceUpdateView",
    sync: (services, { apiResourceId }) => [
      {
        execute: () => services.apiResources.getApiResource(Number(apiResourceId)),
        deps: [apiResourceId]
      }
    ],
    queries: ({ apiResourceId }, services) => [
      {
        name: "apiResource",
        execute: (db) => db.apiResources.get(Number(apiResourceId)),
        defaultValue: {},
        deps: [apiResourceId]
      }
    ],
    monitors: (services) => [
      services.apiResources.apiResourceOperation,
      services.apiResources.updateApiResource,
      services.apiResources.deleteApiResource
    ]
  }
);
const ApiResourceCreate = ({ onChange, validApiResource, onValidate }) => /* @__PURE__ */ jsx(
  ApiResourceDetailsForm,
  {
    onChange,
    validApiResource,
    onValidate
  }
);
ApiResourceCreate.Drawer = withDrawerController(ApiResourceCreate);
const ApiResourceCreateView = reactive.component(
  ({ services, monitors }) => {
    const navigate = useNavigate();
    const [form, setForm] = useState({});
    const [hasApiResource, setHasApiResource] = useState(null);
    const goToList = () => navigate(RoutePaths().apiResources.list());
    useSuccessReactor(services.apiResources.apiResourceOperation, (payload) => {
      setHasApiResource(payload);
    });
    useSuccessReactor(services.apiResources.createApiResource, goToList);
    return /* @__PURE__ */ jsx(
      ApiResourceCreate.Drawer,
      {
        isOpen: true,
        size: "3xl",
        onChange: setForm,
        validApiResource: hasApiResource,
        onValidate: (value) => services.apiResources.apiResourceOperation("validate-resource-name", value),
        headerContent: () => "Create Api Resource",
        footerContent: () => /* @__PURE__ */ jsxs("div", { className: "flex w-full justify-end gap-x-2", children: [
          /* @__PURE__ */ jsx(ErrorListChip, { errors: form.errors }),
          /* @__PURE__ */ jsx(
            SaveButton,
            {
              size: "sm",
              color: "primary",
              isDisabled: !form.isValid,
              onPress: () => services.apiResources.createApiResource({
                ...form.data
              }),
              isLoading: monitors.createApiResource.executing
            }
          )
        ] }),
        onClose: goToList
      }
    );
  },
  {
    name: "ApiResourceCreateView",
    monitors: (services) => [
      services.apiResources.createApiResource,
      services.apiResources.apiResourceOperation
    ]
  }
);
const IdentityResourceDetailsForm = ({
  identityResource,
  onChange,
  validIdentityResource,
  onValidate
}) => {
  const isValidName = !identityResource?.name ? validIdentityResource?.status : validIdentityResource?.status && !!validIdentityResource?.identityResourceName && identityResource?.name !== validIdentityResource?.identityResourceName;
  const errorMessageName = !identityResource?.name ? validIdentityResource?.status ? "Identity Resource Name already exists" : null : validIdentityResource?.status && !!validIdentityResource?.identityResourceName && identityResource?.name !== validIdentityResource?.identityResourceName ? "Identity Resource Name already exists" : null;
  return /* @__PURE__ */ jsxs(
    Uniformly,
    {
      className: "flex flex-col gap-2",
      name: "identityResourceForm",
      onChange: (form) => {
        form.isValid = form.isValid && !isValidName;
        onChange(form);
      },
      deps: [isValidName],
      children: [
        /* @__PURE__ */ jsx(
          TextField$1,
          {
            name: "name",
            label: "Name",
            value: identityResource?.name,
            isInvalid: isValidName,
            onBlur: ({ target }) => onValidate(target.value),
            errorMessage: errorMessageName,
            isRequired: true,
            validation: (x) => x.string().max(100).required("Name is required")
          }
        ),
        /* @__PURE__ */ jsx(
          TextField$1,
          {
            name: "description",
            label: "Description",
            value: identityResource?.description,
            validation: (x) => x.string().max(200)
          }
        ),
        /* @__PURE__ */ jsx(
          TextField$1,
          {
            name: "displayName",
            label: "Display Name",
            value: identityResource?.displayName,
            validation: (x) => x.string().max(100)
          }
        ),
        /* @__PURE__ */ jsx(SwitchField, { name: "emphasize", value: identityResource?.emphasize || false, children: "Emphasize" }),
        /* @__PURE__ */ jsx(SwitchField, { name: "required", value: identityResource?.required || false, children: "Required" }),
        /* @__PURE__ */ jsx(SwitchField, { name: "enabled", value: identityResource?.enabled || false, children: "Enabled" }),
        /* @__PURE__ */ jsx(
          SwitchField,
          {
            name: "showInDiscoveryDocument",
            value: identityResource?.showInDiscoveryDocument || false,
            children: "Show in Document"
          }
        ),
        /* @__PURE__ */ jsx(SwitchField, { name: "nonEditable", value: identityResource?.nonEditable || false, children: "Read-Only" }),
        /* @__PURE__ */ jsx(
          ArrayField,
          {
            name: "userClaims",
            label: "User Claims",
            value: identityResource?.userClaims ?? [],
            validation: (x) => x.array().min(1, "User Claims require at least 1")
          }
        ),
        /* @__PURE__ */ jsx(
          DictionaryField,
          {
            name: "properties",
            label: "Properties",
            value: identityResource?.properties ?? {}
          }
        )
      ]
    }
  );
};
const IdentityResourceCreate = ({ onChange, validIdentityResource, onValidate }) => /* @__PURE__ */ jsx(
  IdentityResourceDetailsForm,
  {
    onChange,
    validIdentityResource,
    onValidate
  }
);
IdentityResourceCreate.Drawer = withDrawerController(IdentityResourceCreate);
const IdentityResourceCreateView = reactive.component(
  ({ services, monitors }) => {
    const navigate = useNavigate();
    const [form, setForm] = useState({});
    const [hasIdentityResource, setHasIdentityResource] = useState(null);
    const goToList = () => navigate(RoutePaths().identityResources.list());
    useSuccessReactor(services.identityResources.identityResourceOperation, (payload) => {
      setHasIdentityResource(payload);
    });
    useSuccessReactor(services.identityResources.createIdentityResource, goToList);
    return /* @__PURE__ */ jsx(
      IdentityResourceCreate.Drawer,
      {
        isOpen: true,
        size: "3xl",
        onChange: setForm,
        validIdentityResource: hasIdentityResource,
        onValidate: (value) => services.identityResources.identityResourceOperation(
          "validate-identity-resource-name",
          value
        ),
        headerContent: () => "Create Identity Resource",
        footerContent: () => /* @__PURE__ */ jsxs("div", { className: "flex w-full justify-end gap-x-2", children: [
          /* @__PURE__ */ jsx(ErrorListChip, { errors: form.errors }),
          /* @__PURE__ */ jsx(
            SaveButton,
            {
              size: "sm",
              color: "primary",
              isDisabled: !form.isValid,
              onPress: () => services.identityResources.createIdentityResource({
                ...form.data
              }),
              isLoading: monitors.createIdentityResource.executing
            }
          )
        ] }),
        onClose: goToList
      }
    );
  },
  {
    name: "IdentityResourceCreateView",
    monitors: (services) => [
      services.identityResources.createIdentityResource,
      services.identityResources.identityResourceOperation
    ]
  }
);
const IdentityResourceUpdate = ({
  identityResource,
  onChange,
  validIdentityResource,
  onValidate
}) => /* @__PURE__ */ jsx(
  IdentityResourceDetailsForm,
  {
    identityResource,
    onChange,
    validIdentityResource,
    onValidate
  }
);
IdentityResourceUpdate.Drawer = withDrawerController(IdentityResourceUpdate);
const IdentityResourceUpdateView = reactive.component(
  ({ services, monitors, data, identityResourceId }) => {
    identityResourceId = Number(identityResourceId);
    const navigate = useNavigate();
    const { identityResource } = data;
    const [form, setForm] = useState({});
    const [hasIdentityResource, setHasIdentityResource] = useState(null);
    const title = "Update Identity Resource";
    const goToList = () => navigate(RoutePaths().identityResources.list());
    useSuccessReactor(services.identityResources.identityResourceOperation, (payload) => {
      setHasIdentityResource(payload);
    });
    useSuccessReactor(services.identityResources.updateIdentityResource, goToList);
    useSuccessReactor(services.identityResources.deleteIdentityResource, goToList);
    return /* @__PURE__ */ jsx(
      IdentityResourceUpdate.Drawer,
      {
        isOpen: true,
        size: "3xl",
        identityResource,
        onChange: setForm,
        validIdentityResource: hasIdentityResource,
        onValidate: (value) => services.identityResources.identityResourceOperation(
          "validate-identity-resource-name",
          value
        ),
        headerContent: () => identityResource?.nonEditable ? /* @__PURE__ */ jsxs("div", { className: "flex flex-row items-center gap-x-2", children: [
          /* @__PURE__ */ jsx(Chip, { color: "danger", children: "Read Only" }),
          title
        ] }) : title,
        footerContent: () => /* @__PURE__ */ jsxs("div", { className: "flex w-full justify-between", children: [
          /* @__PURE__ */ jsxs("div", { children: [
            !identityResource.nonEditable && /* @__PURE__ */ jsx(
              DeleteButton,
              {
                size: "sm",
                color: "danger",
                isDisabled: identityResource.nonEditable,
                onPress: () => services.identityResources.deleteIdentityResource(identityResourceId),
                isLoading: monitors.deleteIdentityResource.executing
              }
            ),
            identityResource.nonEditable && /* @__PURE__ */ jsxs(Tooltip, { delay: 0, children: [
              /* @__PURE__ */ jsx(
                Button,
                {
                  isIconOnly: true,
                  size: "sm",
                  variant: "primary",
                  onPress: () => services.identityResources.identityResourceOperation(
                    "unlock",
                    identityResource.id
                  ),
                  isPending: monitors.identityResourceOperation.executing,
                  children: /* @__PURE__ */ jsx("i", { className: "ri-lock-unlock-fill" })
                }
              ),
              /* @__PURE__ */ jsx(Tooltip.Content, { children: "Unlock" })
            ] })
          ] }),
          /* @__PURE__ */ jsx(ErrorListChip, { errors: form.errors }),
          /* @__PURE__ */ jsx(
            SaveButton,
            {
              size: "sm",
              color: "primary",
              isDisabled: identityResource.nonEditable || !form.isValid,
              onPress: () => services.identityResources.updateIdentityResource(identityResourceId, {
                ...form.data
              }),
              isLoading: monitors.updateIdentityResource.executing
            }
          )
        ] }),
        onClose: goToList
      }
    );
  },
  {
    name: "IdentityResourceUpdateView",
    sync: (services, { identityResourceId }) => [
      {
        execute: () => services.identityResources.getIdentityResource(Number(identityResourceId)),
        deps: [identityResourceId]
      }
    ],
    queries: ({ identityResourceId }, services) => [
      {
        name: "identityResource",
        execute: (db) => db.identityResources.get(Number(identityResourceId)),
        defaultValue: {},
        deps: [identityResourceId]
      }
    ],
    monitors: (services) => [
      services.identityResources.identityResourceOperation,
      services.identityResources.updateIdentityResource,
      services.identityResources.deleteIdentityResource
    ]
  }
);
const IdentityResourcesFilters = new Filters([
  new FilterInput({
    name: "Name",
    title: "Name",
    defaultValue: ""
  }),
  new FilterInput({
    name: "DisplayName",
    title: "Display Name",
    defaultValue: ""
  }),
  new FilterToggle({
    name: "Required",
    title: "Required",
    defaultValue: true
  })
]);
const IdentityResourcesColumns = [
  { propertyName: "name", name: "Name", isSortable: true, isResizable: true, minWidth: 100 },
  {
    propertyName: "displayName",
    name: "Display Name",
    isSortable: true,
    isResizable: true,
    minWidth: 100
  },
  { propertyName: "nonEditable", name: "Locked", isSortable: false },
  { propertyName: "enabled", name: "Enabled", isSortable: false },
  { propertyName: "required", name: "Required", isSortable: false }
];
const IdentityResourcesList = ({ identityResources = [], onSelect }) => {
  const renderCell = useCallback((row, columnKey) => {
    const cellValue = row[columnKey];
    switch (columnKey) {
      case "nonEditable":
      case "enabled":
      case "required":
      case "showInDiscoveryDocument":
        return /* @__PURE__ */ jsxs(Chip, { size: "sm", color: cellValue ? "success" : "danger", children: [
          /* @__PURE__ */ jsx(RiCircleFill, { size: "8" }),
          /* @__PURE__ */ jsx(Chip.Label, { className: "capitalize", children: cellValue ? "Yes" : "No" })
        ] });
      default:
        return cellValue;
    }
  }, []);
  const columns2 = useMemo(
    () => IdentityResourcesColumns.map((column) => ({
      id: column.propertyName,
      header: column.name,
      // `name` is the row's accessible identifier; sorting flows through the
      // request broker via each column's `allowsSorting` + matching `id`.
      isRowHeader: column.propertyName === "name",
      allowsSorting: column.isSortable,
      allowsResizing: column.isResizable,
      minWidth: column.minWidth,
      cell: (row) => renderCell(row, column.propertyName)
    })),
    [renderCell]
  );
  return /* @__PURE__ */ jsx(
    RequestBrokerDataGrid,
    {
      "aria-label": "Identity resources table",
      chipScope: "global",
      columns: columns2,
      data: identityResources,
      getRowId: (row) => row.id,
      allowsColumnResize: true,
      onRowAction: (key) => {
        const selected = identityResources.find((item) => item.id === key);
        if (selected) onSelect(selected);
      }
    }
  );
};
const IdentityResourcesListController = reactive.component(
  ({ data, services, monitors, db, onSelect }) => {
    const { identityResources } = data;
    const handleExecute = useCallback((action, metadata) => {
      action(metadata);
    }, []);
    return /* @__PURE__ */ jsx(
      RequestBroker,
      {
        actionKey: "queryIdentityResources",
        action: services.identityResources.queryIdentityResources,
        filters: IdentityResourcesFilters,
        isExecuting: monitors.queryIdentityResources.executing,
        onExecute: handleExecute,
        skipInitial: () => db.identityResources.count(),
        children: /* @__PURE__ */ jsx(
          IdentityResourcesList,
          {
            identityResources,
            onSelect: (item) => onSelect && onSelect(item)
          }
        )
      }
    );
  },
  {
    name: "IdentityResourcesListController",
    queries: (_, services) => [
      {
        name: "identityResources",
        execute: (db) => db.withRequestBroker(
          services.identityResources.queryIdentityResources.action,
          () => db.identityResources,
          {
            sorting: true
          }
        )
      }
    ],
    monitors: (services) => [services.identityResources.queryIdentityResources]
  }
);
const IdentityResourcesListView = () => {
  const navigate = useNavigate();
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("div", { className: "flex flex-row justify-between mb-4", children: /* @__PURE__ */ jsxs(Breadcrumbs, { className: "content-center", children: [
      /* @__PURE__ */ jsx(BreadcrumbsItem, { isDisabled: true, children: "Auth" }),
      /* @__PURE__ */ jsx(BreadcrumbsItem, { isDisabled: true, children: "Identity Server" }),
      /* @__PURE__ */ jsx(BreadcrumbsItem, { children: "Identity Resources" })
    ] }) }),
    /* @__PURE__ */ jsx(
      IdentityResourcesListController,
      {
        onSelect: (item) => navigate(RoutePaths().identityResources.details(item.id))
      }
    ),
    /* @__PURE__ */ jsx(Outlet, {})
  ] });
};
const ClientsColumns = [
  { propertyName: "clientName", name: "Name", isSortable: true, isResizable: true, minWidth: 120 },
  {
    propertyName: "clientId",
    name: "Client ID",
    isSortable: false,
    isResizable: true,
    minWidth: 120
  },
  { propertyName: "applicationName", name: "Application", isSortable: true },
  { propertyName: "applicationEnvironment", name: "Environment", isSortable: true },
  { propertyName: "nonEditable", name: "Locked", isSortable: false },
  { propertyName: "enabled", name: "Enabled", isSortable: false }
];
const ClientsList = ({ clients = [], onSelect, ...rest }) => {
  const renderCell = useCallback((row, columnKey) => {
    const cellValue = row[columnKey];
    switch (columnKey) {
      case "nonEditable":
      case "enabled":
        return /* @__PURE__ */ jsxs(Chip, { size: "sm", color: cellValue ? "success" : "danger", children: [
          /* @__PURE__ */ jsx(RiCircleFill, { size: "8" }),
          /* @__PURE__ */ jsx(Chip.Label, { className: "capitalize", children: cellValue ? "Yes" : "No" })
        ] });
      case "clientId":
        return cellValue ? /* @__PURE__ */ jsxs("div", { className: "flex items-center", children: [
          /* @__PURE__ */ jsx("span", { className: "me-2", children: cellValue }),
          /* @__PURE__ */ jsx(CopyToClipboard, { text: cellValue })
        ] }) : null;
      case "applicationName":
        return cellValue ? /* @__PURE__ */ jsx(Chip, { color: "accent", size: "sm", children: cellValue }) : null;
      case "applicationEnvironment":
        return cellValue ? /* @__PURE__ */ jsx(EnvironmentChip, { environment: cellValue }) : null;
      default:
        return cellValue;
    }
  }, []);
  const columns2 = useMemo(
    () => ClientsColumns.map((column) => ({
      id: column.propertyName,
      header: column.name,
      // `clientName` is the row's accessible identifier; sorting flows through
      // the request broker via each column's `allowsSorting` + matching `id`.
      isRowHeader: column.propertyName === "clientName",
      allowsSorting: column.isSortable,
      allowsResizing: column.isResizable,
      minWidth: column.minWidth,
      cell: (row) => renderCell(row, column.propertyName)
    })),
    [renderCell]
  );
  return /* @__PURE__ */ jsx(
    RequestBrokerDataGrid,
    {
      "aria-label": "Clients table",
      chipScope: "global",
      columns: columns2,
      data: clients,
      getRowId: (row) => row.id,
      allowsColumnResize: true,
      onRowAction: (key) => {
        const selected = clients.find((item) => item.id === key);
        if (selected) onSelect(selected);
      },
      ...rest
    }
  );
};
const ClientsFilters = new Filters([
  new FilterInput({
    name: "ClientId",
    title: "Client ID",
    defaultValue: ""
  }),
  new FilterInput({
    name: "ClientName",
    title: "Name",
    defaultValue: ""
  }),
  new FilterSelect({
    name: "ApplicationEnvironment",
    title: "Environment",
    loader: () => Promise.resolve(EnvironmentTypeOptions),
    defaultValue: ""
  }),
  new FilterToggle({
    name: "Enabled",
    title: "Enabled",
    defaultValue: true
  })
]);
const ClientsListController = reactive.component(
  ({ data, services, monitors, db, onSelect }) => {
    const { clients } = data;
    const handleExecute = useCallback((action, metadata) => {
      action(metadata);
    }, []);
    return /* @__PURE__ */ jsx(
      RequestBroker,
      {
        actionKey: "queryClients",
        action: services.clients.queryClients,
        filters: ClientsFilters,
        isExecuting: monitors.queryClients.executing,
        onExecute: handleExecute,
        skipInitial: () => db.clients.count(),
        children: /* @__PURE__ */ jsx(ClientsList, { clients, onSelect: (item) => onSelect && onSelect(item) })
      }
    );
  },
  {
    name: "ClientsListController",
    queries: (_, services) => [
      {
        name: "clients",
        execute: (db) => db.withRequestBroker(services.clients.queryClients.action, () => db.clients, {
          sorting: true
        })
      }
    ],
    monitors: (services) => [services.clients.queryClients]
  }
);
const ClientsListView = () => {
  const navigate = useNavigate();
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("div", { className: "flex flex-row justify-between mb-4", children: /* @__PURE__ */ jsxs(Breadcrumbs, { className: "content-center", children: [
      /* @__PURE__ */ jsx(BreadcrumbsItem, { isDisabled: true, children: "Auth" }),
      /* @__PURE__ */ jsx(BreadcrumbsItem, { isDisabled: true, children: "Identity Server" }),
      /* @__PURE__ */ jsx(BreadcrumbsItem, { onPress: () => navigate(RoutePaths().clients.list()), children: "Clients" })
    ] }) }),
    /* @__PURE__ */ jsx(ClientsListController, { onSelect: (item) => navigate(RoutePaths().clients.details(item.id)) })
  ] });
};
const FlagsFormDefinition = {
  name: "flags",
  title: "Flags",
  fields: [
    {
      name: "enabled",
      label: "Enabled",
      description: "Specifies if client is enabled",
      render: (props) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/2", children: /* @__PURE__ */ jsx(
        ExtendedSwitchField,
        {
          name: "enabled",
          label: "Enabled",
          description: "Specifies if client is enabled",
          defaultValue: true,
          ...props
        }
      ) })
    },
    {
      name: "requireClientSecret",
      label: "Require Client Secret",
      description: "If set to false, no client secret is needed to request tokens at the token endpoint",
      render: (props) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/2", children: /* @__PURE__ */ jsx(
        ExtendedSwitchField,
        {
          name: "requireClientSecret",
          label: "Require Client Secret",
          description: "If set to false, no client secret is needed to request tokens from the token endpoint",
          defaultValue: true,
          ...props
        }
      ) })
    },
    {
      name: "requireConsent",
      label: "Require Consent",
      description: "Specifies whether a consent screen is required",
      render: (props) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/2", children: /* @__PURE__ */ jsx(
        ExtendedSwitchField,
        {
          name: "requireConsent",
          label: "Require Consent",
          description: "Specifies whether a consent screen is required",
          defaultValue: false,
          ...props
        }
      ) })
    },
    {
      name: "allowRememberConsent",
      label: "Allow Remember Consent",
      description: "Specifies whether user can choose to store consent decisions",
      render: (props) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/2", children: /* @__PURE__ */ jsx(
        ExtendedSwitchField,
        {
          name: "allowRememberConsent",
          label: "Allow Remember Consent",
          description: "Specifies whether a consent screen is required",
          defaultValue: false,
          ...props
        }
      ) })
    },
    {
      name: "requirePkce",
      label: "Require Pkce",
      description: "Specifies whether PKCE is required for authorization code flow",
      render: (props) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/2", children: /* @__PURE__ */ jsx(
        ExtendedSwitchField,
        {
          name: "requirePkce",
          label: "Require PKCE",
          description: "Specifies whether PKCE is required for authorization code flow",
          defaultValue: true,
          ...props
        }
      ) })
    },
    {
      name: "allowPlainTextPkce",
      label: "Allow Plain Text Pkce",
      description: "Specifies whether a proof key can be sent using plain method",
      render: (props) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/2", children: /* @__PURE__ */ jsx(
        ExtendedSwitchField,
        {
          name: "allowPlainTextPkce",
          label: "Allow Plain Text PKCE",
          description: "Specifies whether a proof key can be sent using plain text",
          defaultValue: true,
          ...props
        }
      ) })
    },
    {
      name: "requireRequestObject",
      label: "Require Request Object",
      description: "Specifies whether the client must use a request object on authorize requests",
      render: (props) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/2", children: /* @__PURE__ */ jsx(
        ExtendedSwitchField,
        {
          name: "requireRequestObject",
          label: "Require Request Object",
          description: "Specifies whether the client must use a request object on authorize requests",
          defaultValue: false,
          ...props
        }
      ) })
    },
    {
      name: "allowAccessTokensViaBrowser",
      label: "Allow Access Tokens Via Browser",
      description: "Controls whether access tokens are transmitted via the browser for this client",
      render: (props) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/2", children: /* @__PURE__ */ jsx(
        ExtendedSwitchField,
        {
          name: "allowAccessTokensViaBrowser",
          label: "Allow Access Tokens via Browser",
          description: "Controls whether access tokens are transmitted via the browser for this client",
          defaultValue: false,
          ...props
        }
      ) })
    },
    {
      name: "frontChannelLogoutSessionRequired",
      label: "Front Channel Logout Session Required",
      description: "Specifies if the user's session id should be sent to the FrontChannelLogoutUri.",
      render: (props) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/2", children: /* @__PURE__ */ jsx(
        ExtendedSwitchField,
        {
          name: "frontChannelLogoutSessionRequired",
          label: "Front Channel Logout Session Required",
          description: "Specifies if the user's session id should be sent to the Front Channel Logout URI",
          defaultValue: true,
          ...props
        }
      ) })
    },
    {
      name: "backChannelLogoutSessionRequired",
      label: "Back Channel Logout Session Required",
      description: "Specifies if the user's session id should be sent to the BackChannelLogoutUri.",
      render: (props) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/2", children: /* @__PURE__ */ jsx(
        ExtendedSwitchField,
        {
          name: "backChannelLogoutSessionRequired",
          label: "Back Channel Logout Session Required",
          description: "Specifies if the user's session id should be sent to the Back Channel Logout URI",
          defaultValue: true,
          ...props
        }
      ) })
    },
    {
      name: "allowOfflineAccess",
      label: "Allow Offline Access",
      description: "Gets or sets a value indicating whether to allow refresh tokens.",
      render: (props) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/2", children: /* @__PURE__ */ jsx(
        ExtendedSwitchField,
        {
          name: "allowOfflineAccess",
          label: "Allow Offline Access",
          description: "Gets or sets a value indicating whether to allow refresh tokens",
          defaultValue: false,
          ...props
        }
      ) })
    },
    {
      name: "alwaysIncludeUserClaimsInIdToken",
      label: "Always Include User Claims In Id Token",
      description: "When requesting both an id token and access token, should the user claims always be added to the id token instead of requiring the client to use the userinfo endpoint.",
      render: (props) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/2", children: /* @__PURE__ */ jsx(
        ExtendedSwitchField,
        {
          name: "alwaysIncludeUserClaimsInIdToken",
          label: "Always Include User Claims in ID Token",
          description: "When requesting both an id token and access token, should the user claims always be added to the id token instead of requiring the client to use the userinfo endpoint.",
          defaultValue: false,
          ...props
        }
      ) })
    },
    {
      name: "updateAccessTokenClaimsOnRefresh",
      label: "Update Access Token Claims On Refresh",
      description: "Gets or sets a value indicating whether the access token (and its claims) should be updated on a refresh token request.",
      render: (props) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/2", children: /* @__PURE__ */ jsx(
        ExtendedSwitchField,
        {
          name: "updateAccessTokenClaimsOnRefresh",
          label: "Update Access Token Claims on Refresh",
          description: "Gets or sets a value indicating whether the access token (and its claims) should be updated on a refresh token request.",
          defaultValue: false,
          ...props
        }
      ) })
    },
    {
      name: "enableLocalLogin",
      label: "Enable Local Login",
      description: "Gets or sets a value indicating whether the local login is allowed for this client.",
      render: (props) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/2", children: /* @__PURE__ */ jsx(
        ExtendedSwitchField,
        {
          name: "enableLocalLogin",
          label: "Enable Local Login",
          description: "Gets or sets a value indicating whether the local login is allowed for this client.",
          defaultValue: true,
          ...props
        }
      ) })
    },
    {
      name: "includeJwtId",
      label: "Include Jwt Id",
      description: "Gets or sets a value indicating whether JWT access tokens should include an identifier.",
      render: (props) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/2", children: /* @__PURE__ */ jsx(
        ExtendedSwitchField,
        {
          name: "includeJwtId",
          label: "Include JWT Id",
          description: "Gets or sets a value indicating whether JWT access tokens should include an identifier.",
          defaultValue: true,
          ...props
        }
      ) })
    },
    {
      name: "alwaysSendClientClaims",
      label: "Always Send Client Claims",
      description: "Gets or sets a value indicating whether client claims should be always included in the access tokens - or only for client credentials flow.",
      render: (props) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/2", children: /* @__PURE__ */ jsx(
        ExtendedSwitchField,
        {
          name: "alwaysSendClientClaims",
          label: "Always Send Client Claims",
          description: "Gets or sets a value indicating whether client claims should be always included in the access tokens - or only for client credentials flow.",
          defaultValue: false,
          ...props
        }
      ) })
    },
    {
      name: "nonEditable",
      label: "Read-Only",
      description: "Gets or sets a value indicating whether this client is not editable.",
      render: (props) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/2", children: /* @__PURE__ */ jsx(
        ExtendedSwitchField,
        {
          name: "nonEditable",
          label: "Read-Only",
          description: "Gets or sets a value indicating whether this client is not editable.",
          defaultValue: false,
          ...props
        }
      ) })
    }
  ]
};
const DefaultClient = {
  nonEditable: false,
  applicationId: 0,
  applicationName: "",
  applicationEnvironment: "",
  clientSecrets: [],
  enabled: true,
  protocolType: "oidc",
  requireClientSecret: false,
  clientName: "",
  description: "",
  clientUri: null,
  logoUri: null,
  requireConsent: false,
  allowRememberConsent: true,
  allowedGrantTypes: ["authorization_code", "refresh_token"],
  requirePkce: true,
  allowPlainTextPkce: false,
  requireRequestObject: false,
  allowAccessTokensViaBrowser: true,
  redirectUris: [],
  postLogoutRedirectUris: [],
  frontChannelLogoutUri: null,
  frontChannelLogoutSessionRequired: true,
  backChannelLogoutUri: null,
  backChannelLogoutSessionRequired: true,
  allowOfflineAccess: true,
  allowedScopes: ["openid", "email", "profile", "phone", "IdentityServerApi", "offline_access"],
  alwaysIncludeUserClaimsInIdToken: true,
  identityTokenLifetime: 300,
  allowedIdentityTokenSigningAlgorithms: [],
  accessTokenLifetime: 3600,
  authorizationCodeLifetime: 300,
  absoluteRefreshTokenLifetime: 2592e3,
  slidingRefreshTokenLifetime: 1296e3,
  consentLifetime: null,
  refreshTokenUsage: TokenUsageType.OneTimeOnly,
  updateAccessTokenClaimsOnRefresh: false,
  refreshTokenExpiration: TokenExpirationType.Absolute,
  accessTokenType: AccessTokenType.Jwt,
  enableLocalLogin: true,
  identityProviderRestrictions: [],
  includeJwtId: true,
  claims: [],
  alwaysSendClientClaims: true,
  clientClaimsPrefix: "client_",
  pairWiseSubjectSalt: null,
  userSsoLifetime: null,
  userCodeType: null,
  deviceCodeLifetime: 300,
  allowedCorsOrigins: [],
  properties: {}
};
const GeneralFormDefinition = {
  name: "general",
  title: "General",
  fields: [
    {
      name: "logo",
      label: "Logo",
      type: "file",
      description: "Logo",
      render: (_, values2) => /* @__PURE__ */ jsx("div", { className: "flex w-full float-start xl:w-1/3 justify-center p-2 m-2", children: /* @__PURE__ */ jsx(ImageInputField, { name: "logo", label: "Logo", defaultValue: values2.logoUri, hasDropzone: true, children: /* @__PURE__ */ jsx(ImageInputField.Preview, { children: (image) => /* @__PURE__ */ jsx("div", { className: "flex items-center justify-center", children: /* @__PURE__ */ jsx("img", { src: image, alt: "Logo", className: "w-3/4" }) }) }) }) })
    },
    {
      name: "applicationId",
      label: "Application",
      description: "Application",
      render: ({ value }) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/3", children: /* @__PURE__ */ jsx(
        ApplicationsLookup,
        {
          name: "applicationId",
          label: "Application",
          description: "The application associated with this client",
          value,
          validation: (x) => x.number().moreThan(0, "Application is required").required("Application is required")
        }
      ) })
    },
    {
      name: "clientId",
      label: "Client ID",
      description: "Client ID",
      render: ({ value }) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/3", children: /* @__PURE__ */ jsx(
        TextFieldWithCopy,
        {
          label: "Client ID",
          isReadOnly: true,
          value,
          description: "This client's unique ID. This value cannot be changed."
        }
      ) })
    },
    {
      name: "clientName",
      label: "Client Name",
      description: "Client display name (used for logging and consent screen)",
      render: (props) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/2", children: /* @__PURE__ */ jsx(
        TextField$1,
        {
          name: "clientName",
          label: "Client Name",
          description: "Client display name (used for login and consent screen)",
          isRequired: true,
          validation: (x) => x.string().max(100).required("Client name is required"),
          ...props
        }
      ) })
    },
    {
      name: "description",
      label: "Description",
      description: "Description of the client",
      render: (props) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/2", children: /* @__PURE__ */ jsx(
        TextAreaField,
        {
          name: "description",
          label: "Description",
          description: "Description of the client",
          validation: (x) => x.string().max(300),
          ...props
        }
      ) })
    },
    {
      name: "protocolType",
      label: "Protocol Type",
      description: "The protocol type of the client (defaults to oidc)",
      render: (props) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/2", required: true, children: /* @__PURE__ */ jsx(
        TextField$1,
        {
          name: "protocolType",
          label: "Protocol Type",
          description: "The protocol type of the client (defaults to oidc)",
          defaultValue: "oidc",
          isRequired: true,
          validation: (x) => x.string().max(100).required("Protocol Type"),
          ...props
        }
      ) })
    },
    {
      name: "identityTokenLifetime",
      label: "Identity Token Lifetime",
      description: "Lifetime of identity token in seconds (defaults to 5 minutes)",
      render: (props) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/2", children: /* @__PURE__ */ jsx(
        TimeCalculatorField,
        {
          name: "identityTokenLifetime",
          label: "Identity Token Lifetime",
          type: "number",
          defaultValue: 300,
          description: "Lifetime of identity token in seconds (default to 5 minutes)",
          validation: (x) => x.number().integer().required("Identity Token Lifetime"),
          variant: "secondary",
          isRequired: true,
          ...props
        }
      ) })
    },
    {
      name: "accessTokenLifetime",
      label: "Access Token Lifetime",
      description: "Lifetime of access token in seconds (defaults to 1 hour)",
      render: (props) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/2", children: /* @__PURE__ */ jsx(
        TimeCalculatorField,
        {
          name: "accessTokenLifetime",
          label: "Access Token Lifetime",
          type: "number",
          description: "Lifetime of access token in seconds (defaults to 1 hour)",
          isRequired: true,
          validation: (x) => x.number().integer().required("Access Token Lifetime"),
          ...props
        }
      ) })
    },
    {
      name: "authorizationCodeLifetime",
      label: "Authorization Code Lifetime",
      description: "Lifetime of authorization code in seconds (defaults to 5 minutes)",
      render: (props) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/2", children: /* @__PURE__ */ jsx(
        TimeCalculatorField,
        {
          name: "authorizationCodeLifetime",
          label: "Authorization Code Lifetime",
          type: "number",
          defaultValue: 300,
          description: "Lifetime of authorization code in seconds (defaults to 5 minutes)",
          isRequired: true,
          validation: (x) => x.number().integer().required("Authorization Code Lifetime"),
          ...props
        }
      ) })
    },
    {
      name: "absoluteRefreshTokenLifetime",
      label: "Absolute Refresh Token Lifetime",
      description: "Maximum lifetime of a refresh token in seconds. Defaults to 2592000 seconds / 30 days",
      render: (props) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/2", children: /* @__PURE__ */ jsx(
        TimeCalculatorField,
        {
          name: "absoluteRefreshTokenLifetime",
          label: "Absolute Refresh Token Lifetime",
          type: "number",
          defaultValue: 2592e3,
          description: "Maximum lifetime of a refresh token in seconds. (defaults to 30 days)",
          isRequired: true,
          validation: (x) => x.number().integer().required("Absolute Refresh Token Lifetime"),
          ...props
        }
      ) })
    },
    {
      name: "slidingRefreshTokenLifetime",
      label: "Sliding Refresh Token Lifetime",
      description: "Sliding lifetime of a refresh token in seconds. Defaults to 1296000 seconds / 15 days",
      render: (props) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/2", children: /* @__PURE__ */ jsx(
        TimeCalculatorField,
        {
          name: "slidingRefreshTokenLifetime",
          label: "Sliding Refresh Token Lifetime",
          type: "number",
          defaultValue: 1296e3,
          description: "Sliding lifetime of a refresh token in seconds. (defaults to 15 days)",
          isRequired: true,
          validation: (x) => x.number().integer().required("Sliding Refresh Token Lifetime"),
          ...props
        }
      ) })
    },
    {
      name: "deviceCodeLifetime",
      label: "Device Code Lifetime",
      description: "Device Code Lifetime.",
      render: (props) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/2", children: /* @__PURE__ */ jsx(
        TimeCalculatorField,
        {
          name: "deviceCodeLifetime",
          label: "Device Code Lifetime",
          type: "number",
          defaultValue: 300,
          description: "Device Code Lifetime",
          isRequired: true,
          validation: (x) => x.number().integer().required("Device Code Lifetime"),
          ...props
        }
      ) })
    },
    {
      name: "consentLifetime",
      label: "Consent Lifetime",
      type: "number",
      description: "Lifetime of a user consent in seconds. Defaults to null (no expiration)",
      render: (props) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/2", children: /* @__PURE__ */ jsx(
        TimeCalculatorField,
        {
          name: "consentLifetime",
          label: "Consent Lifetime",
          type: "number",
          description: "User Consent Lifetime in seconds. (default to no expiration)",
          ...props
        }
      ) })
    },
    {
      name: "userSsoLifetime",
      label: "User Sso Lifetime",
      description: "The maximum duration (in seconds) since the last time the user authenticated.",
      render: (props) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/2", children: /* @__PURE__ */ jsx(
        TimeCalculatorField,
        {
          name: "userSsoLifetime",
          label: "User SSO Lifetime",
          type: "number",
          description: "Maximum duration (in seconds) since the last time the user authenticated",
          ...props
        }
      ) })
    },
    {
      name: "refreshTokenUsage",
      label: "Refresh Token Usage Type",
      render: (props) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/2", children: /* @__PURE__ */ jsx(
        TokenUsageTypeLookup,
        {
          name: "refreshTokenUsage",
          label: "Refresh Token Usage Type",
          defaultValue: TokenUsageType.OneTimeOnly,
          isRequired: true,
          validation: (x) => x.number().required(),
          ...props
        }
      ) })
    },
    {
      name: "refreshTokenExpiration",
      label: "Refresh Token Expiration Type",
      render: (props) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/2", children: /* @__PURE__ */ jsx(
        TokenExpirationTypeLookup,
        {
          name: "refreshTokenExpiration",
          label: "Refresh Token Expiration Type",
          type: "number",
          defaultValue: TokenExpirationType.Absolute,
          isRequired: true,
          validation: (x) => x.number().required(),
          ...props
        }
      ) })
    },
    {
      name: "accessTokenType",
      label: "Access Token Type",
      description: "Specifies whether the access token is a reference token or a self contained JWT token (defaults to Jwt).",
      render: (props) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/2", children: /* @__PURE__ */ jsx(
        AccessTokenTypeLookup,
        {
          name: "accessTokenType",
          label: "Access Token Type",
          type: "number",
          defaultValue: AccessTokenType.Jwt,
          description: "Specifies whether the access token is a reference token or a self contained JWT token (defaults to Jwt).",
          isRequired: true,
          validation: (x) => x.number().required(),
          ...props
        }
      ) })
    },
    {
      name: "clientClaimsPrefix",
      label: "Client Claims Prefix",
      description: "Prefix for client claim to prefix it on client claim types (default is client_)",
      render: (props) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/2", children: /* @__PURE__ */ jsx(
        TextField$1,
        {
          name: "clientClaimsPrefix",
          label: "Client Claims Prefix",
          defaultValue: "client_",
          description: "Prefix for client claim to prefix it on client claim types (default is client_)",
          isRequired: true,
          validation: (x) => x.string().max(100).required(),
          ...props
        }
      ) })
    },
    {
      name: "pairWiseSubjectSalt",
      label: "Pair Wise Subject Salt",
      description: "A salt value used in pair-wise subjectId generation for users of this client.",
      render: (props) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/2", children: /* @__PURE__ */ jsx(
        TextField$1,
        {
          name: "pairWiseSubjectSalt",
          label: "Pair Wise Subject Salt",
          description: "A salt value used in pair-wise subjectId generation for users of this client.",
          validation: (x) => x.string().max(100),
          ...props
        }
      ) })
    },
    {
      name: "userCodeType",
      label: "User Code Type",
      description: "The type of the device flow user code.",
      render: (props) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/2", children: /* @__PURE__ */ jsx(
        TextField$1,
        {
          name: "userCodeType",
          label: "User Code Type",
          description: "The type of the device flow user code",
          validation: (x) => x.string().max(100),
          ...props
        }
      ) })
    },
    {
      name: "allowedGrantTypes",
      label: "Allowed Grant Types",
      render: (props) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/2", children: /* @__PURE__ */ jsx(
        ArrayField,
        {
          name: "allowedGrantTypes",
          label: "Allowed Grant Types",
          withAutocomplete: true,
          defaultItems: [
            { value: "authorization_code", label: "authorization_code" },
            { value: "client_credentials", label: "client_credentials" },
            { value: "api_token", label: "api_token" },
            { value: "refresh_token", label: "refresh_token" }
          ],
          validation: (x) => x.array().min(1, "Allowed Grant Types require at least 1"),
          ...props
        }
      ) })
    },
    {
      name: "allowedScopes",
      label: "Allowed Scopes",
      render: (props, values2) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/2", children: /* @__PURE__ */ jsx(
        ApiResourceScopeDynamicField,
        {
          name: "allowedScopes",
          label: "Allowed Scopes",
          withAutocomplete: true,
          applicationId: values2.applicationId,
          defaultItems: DefaultClient.allowedScopes.map((scope) => ({
            value: scope,
            label: scope
          })),
          validation: (x) => x.array().min(1, "A minimum of one scope is required"),
          ...props
        }
      ) })
    },
    {
      name: "allowedIdentityTokenSigningAlgorithms",
      label: "allowed identity token signing algorithms",
      render: (props) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/2", children: /* @__PURE__ */ jsx(
        ArrayField,
        {
          name: "allowedIdentityTokenSigningAlgorithms",
          label: "Allowed Signing Algorithms",
          defaultItems: [],
          ...props
        }
      ) })
    }
  ]
};
const SecretsFormDefinition = {
  name: "secrets",
  title: "Secrets",
  fields: [
    {
      name: "clientSecrets",
      label: "Client Secrets",
      render: (_, values2) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/2", children: /* @__PURE__ */ jsx(ClientSecretsListView, { clientId: values2.id }) })
    }
  ]
};
const UrisFormDefinition = {
  name: "uris",
  title: "Uri's",
  fields: [
    {
      name: "frontChannelLogoutUri",
      label: "Front Channel Logout Uri",
      description: "Specifies logout URI at client for HTTP front-channel based logout.",
      render: (props) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/2", children: /* @__PURE__ */ jsx(
        TextField$1,
        {
          name: "frontChannelLogoutUri",
          label: "Front Channel Logout Uri",
          description: "Specifies logout URI at client for HTTP front-channel based logout.",
          validation: (x) => x.string().max(200),
          ...props
        }
      ) })
    },
    {
      name: "backChannelLogoutUri",
      label: "Back Channel Logout Uri",
      description: "Specifies logout URI at client for HTTP back-channel based logout.",
      render: (props) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/2", children: /* @__PURE__ */ jsx(
        TextField$1,
        {
          name: "backChannelLogoutUri",
          label: "Back Channel Logout Uri",
          description: "Specifies logout URI at client for HTTP back-channel based logout.",
          validation: (x) => x.string().max(200),
          ...props
        }
      ) })
    },
    {
      name: "allowedCorsOrigins",
      label: "Allowed Cors Origins",
      render: (props) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/2", children: /* @__PURE__ */ jsx(
        ArrayField,
        {
          name: "allowedCorsOrigins",
          label: "Allowed Cors Origins",
          validation: (x) => x.array().min(1, "Allowed Cors Origins require at least 1"),
          ...props
        }
      ) })
    },
    {
      name: "redirectUris",
      label: "Redirect Uris",
      render: (props) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/2", children: /* @__PURE__ */ jsx(
        ArrayField,
        {
          name: "redirectUris",
          label: "Redirect Uris",
          validation: (x) => x.array().min(1, "Redirect Uris require at least 1"),
          ...props
        }
      ) })
    },
    {
      name: "postLogoutRedirectUris",
      label: "Post Logout Redirect Uris",
      render: (props) => /* @__PURE__ */ jsx("div", { className: "w-full xl:w-1/2", children: /* @__PURE__ */ jsx(
        ArrayField,
        {
          name: "postLogoutRedirectUris",
          label: "Post Logout Redirect Uris",
          validation: (x) => x.array().min(1, "Post Logout Redirect Uris require at least 1"),
          ...props
        }
      ) })
    }
  ]
};
const ClientCreateFormDefinitions = [
  GeneralFormDefinition,
  FlagsFormDefinition,
  UrisFormDefinition
];
const ClientUpdateFormDefinitions = [...ClientCreateFormDefinitions, SecretsFormDefinition];
const ClientSaveButton = reactive.component(
  ({ isDisabled, isLoading, onSave, monitors }) => {
    const [showTemplate, setShowTemplate] = useState(false);
    const [templateName, setTemplateName] = useState("");
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(
        MultiActionButton,
        {
          selectedKey: "save",
          color: "primary",
          size: "sm",
          options: {
            save: {
              label: "Save",
              description: "Save changes to client",
              iconClass: "ri-save-line"
            },
            template: {
              label: "Save as Template",
              description: "Save client definition as a template",
              iconClass: "ri-save-line"
            }
          },
          isDisabled,
          onPress: (selected) => {
            if (selected === "save") {
              onSave && onSave();
            }
            if (selected === "template") {
              setShowTemplate(true);
            }
          },
          isLoading
        }
      ),
      /* @__PURE__ */ jsx(Modal.Backdrop, { isOpen: showTemplate, onOpenChange: setShowTemplate, children: /* @__PURE__ */ jsx(Modal.Container, { size: "xs", placement: "center", children: /* @__PURE__ */ jsxs(Modal.Dialog, { children: [
        /* @__PURE__ */ jsx(Modal.Header, { children: /* @__PURE__ */ jsx(Modal.Heading, { children: "Save As Template" }) }),
        /* @__PURE__ */ jsx(Modal.Body, { children: /* @__PURE__ */ jsxs(
          TextField,
          {
            isRequired: true,
            defaultValue: templateName,
            onChange: (value) => setTemplateName(value),
            className: "max-w-xs",
            children: [
              /* @__PURE__ */ jsx(Label, { children: "Template Name" }),
              /* @__PURE__ */ jsxs(InputGroup, { variant: "secondary", children: [
                /* @__PURE__ */ jsx(InputGroup.Input, { type: "text" }),
                monitors.createClientTemplate.executing && /* @__PURE__ */ jsx(InputGroup.Suffix, { children: /* @__PURE__ */ jsx(Spinner, { size: "sm", color: "current" }) })
              ] })
            ]
          }
        ) }),
        /* @__PURE__ */ jsx(Modal.Footer, { children: /* @__PURE__ */ jsx(
          SaveButton,
          {
            color: "primary",
            isDisabled,
            onPress: () => {
            }
          }
        ) })
      ] }) }) })
    ] });
  },
  {
    name: "ClientSaveButton",
    monitors: (services) => [services.clientTemplates.createClientTemplate]
  }
);
const ClientCreateView = reactive.component(
  ({ services, monitors, onSuccess }) => {
    const navigate = useNavigate();
    const { data, isValid, errors, setForm } = useAggregateForm();
    const [values2, setValues] = useState(DefaultClient);
    useEffect(() => {
      setValues((x) => ({ ...x, applicationId: data.applicationId }));
    }, [data.applicationId]);
    useSuccessReactor(services.clients.createClient, (payload) => {
      navigate(RoutePaths().clients.details(payload.id));
    });
    return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-4 h-full", children: [
      /* @__PURE__ */ jsxs(Breadcrumbs, { className: "shrink-0", children: [
        /* @__PURE__ */ jsx(BreadcrumbsItem, { isDisabled: true, children: "Auth" }),
        /* @__PURE__ */ jsx(BreadcrumbsItem, { isDisabled: true, children: "Identity Server" }),
        /* @__PURE__ */ jsx(BreadcrumbsItem, { onPress: () => navigate(RoutePaths().clients.list()), children: "Clients" }),
        /* @__PURE__ */ jsx(BreadcrumbsItem, { children: "New" })
      ] }),
      /* @__PURE__ */ jsxs(SplitCard, { className: "grow", children: [
        /* @__PURE__ */ jsx(SplitCard.Header, { className: "items-start w-full", children: "New Client" }),
        /* @__PURE__ */ jsx(FilteredFormsProvider, { forms: ClientCreateFormDefinitions, children: /* @__PURE__ */ jsxs(SplitCard.Body, { children: [
          /* @__PURE__ */ jsx(SplitCard.Sidebar, { className: "pr-4", children: /* @__PURE__ */ jsx(
            FilteredFormsNavigation,
            {
              iconDefinitions: {
                general: "ri-settings-2-line",
                uris: "ri-global-line",
                flags: "ri-flag-line",
                secrets: "ri-spy-fill"
              }
            }
          ) }),
          /* @__PURE__ */ jsx(SplitCard.Main, { className: "pl-4", children: /* @__PURE__ */ jsx(
            FilteredForms,
            {
              values: values2,
              onChange: (x) => {
                const form = x[x.name];
                setForm({ [x.name]: form });
              }
            }
          ) })
        ] }) }),
        /* @__PURE__ */ jsx(SplitCard.Footer, { className: "justify-end", children: /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx(ErrorListChip, { errors }),
          /* @__PURE__ */ jsx(
            ClientSaveButton,
            {
              isLoading: monitors.createClient.executing,
              isDisabled: !isValid,
              onSave: () => services.clients.createClient(data)
            }
          )
        ] }) })
      ] })
    ] });
  },
  {
    name: "ClientCreateView",
    monitors: (services) => [services.clients.createClient]
  }
);
const ClientUpdateHeader = ({ client }) => {
  return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
    client.nonEditable && /* @__PURE__ */ jsx(Chip, { size: "sm", color: "danger", children: "Read-Only" }),
    /* @__PURE__ */ jsxs("h2", { className: "flex items-center gap-2 font-semibold text-nowrap", children: [
      client.clientName,
      /* @__PURE__ */ jsx("i", { className: "ri-arrow-right-line" }),
      /* @__PURE__ */ jsxs(
        Link$1,
        {
          to: RoutePaths().applications.details(client.applicationId),
          className: "flex items-center gap-1",
          children: [
            /* @__PURE__ */ jsx(Chip, { color: "accent", size: "sm", children: client.applicationName }),
            /* @__PURE__ */ jsx(EnvironmentChip, { environment: client.applicationEnvironment })
          ]
        }
      )
    ] })
  ] });
};
const ClientUpdateView = reactive.component(
  ({ data: { client }, monitors, services, clientId }) => {
    clientId = Number(clientId);
    const navigate = useNavigate();
    const { data, isValid, errors, setForm } = useAggregateForm();
    useRecentView({
      title: client?.id ? client.clientName || client.clientId || `Client #${client.id}` : void 0,
      route: client?.id ? RoutePaths().clients.details(client.id) : void 0
    });
    useSuccessReactor(services.clients.deleteClient, () => {
      navigate(RoutePaths().clients.list());
    });
    if (monitors.getClient.executing) return /* @__PURE__ */ jsx(Loading, { label: "Loading...", color: "primary" });
    if (isEmpty(client)) return /* @__PURE__ */ jsx(NotFound, { resourceType: "Client" });
    return /* @__PURE__ */ jsxs("div", { className: "flex flex-col h-full gap-4", children: [
      /* @__PURE__ */ jsxs(Breadcrumbs, { className: "shrink-0", children: [
        /* @__PURE__ */ jsx(BreadcrumbsItem, { isDisabled: true, children: "Auth" }),
        /* @__PURE__ */ jsx(BreadcrumbsItem, { isDisabled: true, children: "Identity Server" }),
        /* @__PURE__ */ jsx(BreadcrumbsItem, { onPress: () => navigate(RoutePaths().clients.list()), children: "Clients" }),
        /* @__PURE__ */ jsx(BreadcrumbsItem, { children: "Update" })
      ] }),
      /* @__PURE__ */ jsxs(SplitCard, { className: "grow", children: [
        /* @__PURE__ */ jsx(SplitCard.Header, { className: "items-start w-full", children: /* @__PURE__ */ jsx(ClientUpdateHeader, { client }) }),
        /* @__PURE__ */ jsx(FilteredFormsProvider, { forms: ClientUpdateFormDefinitions, children: /* @__PURE__ */ jsxs(SplitCard.Body, { children: [
          /* @__PURE__ */ jsx(SplitCard.Sidebar, { className: "pr-4", children: /* @__PURE__ */ jsx(
            FilteredFormsNavigation,
            {
              iconDefinitions: {
                general: "ri-settings-2-line",
                uris: "ri-global-line",
                flags: "ri-flag-line",
                secrets: "ri-spy-fill"
              }
            }
          ) }),
          /* @__PURE__ */ jsx(SplitCard.Main, { className: "pl-4", children: /* @__PURE__ */ jsx(
            FilteredForms,
            {
              values: client,
              onChange: (x) => {
                const form = x[x.name];
                setForm({ [x.name]: form });
              }
            }
          ) })
        ] }) }),
        /* @__PURE__ */ jsxs(SplitCard.Footer, { className: "justify-between", children: [
          !client.nonEditable && /* @__PURE__ */ jsxs(Tooltip, { delay: 0, children: [
            /* @__PURE__ */ jsx(
              DeleteButton,
              {
                size: "sm",
                isDisabled: client.nonEditable,
                isLoading: monitors.deleteClient.executing,
                onPress: () => services.clients.deleteClient(client.id)
              }
            ),
            /* @__PURE__ */ jsx(Tooltip.Content, { children: "Delete" })
          ] }),
          client.nonEditable && /* @__PURE__ */ jsxs(Tooltip, { delay: 0, children: [
            /* @__PURE__ */ jsx(
              Button,
              {
                isIconOnly: true,
                className: "me-2",
                size: "sm",
                variant: "primary",
                onPress: () => services.clients.clientOperation("unlock", client.id),
                isPending: monitors.clientOperation.executing,
                children: ({ isPending }) => isPending ? /* @__PURE__ */ jsx(Spinner, { size: "sm", color: "current" }) : /* @__PURE__ */ jsx("i", { className: "ri-lock-unlock-fill" })
              }
            ),
            /* @__PURE__ */ jsx(Tooltip.Content, { children: "Unlock" })
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
            /* @__PURE__ */ jsx(ErrorListChip, { errors }),
            /* @__PURE__ */ jsx(
              ClientSaveButton,
              {
                isDisabled: !isValid,
                isLoading: monitors.updateClient.executing,
                onSave: () => {
                  services.clients.updateClient(client.id, {
                    id: client.id,
                    clientId: client.clientId,
                    applicationId: client.applicationId,
                    ...data
                  });
                }
              }
            )
          ] })
        ] })
      ] })
    ] });
  },
  {
    name: "ClientUpdateView",
    sync: (services, { clientId }) => [
      {
        execute: () => services.clients.getClient(Number(clientId)),
        deps: [clientId]
      }
    ],
    queries: ({ clientId }) => [
      {
        name: "client",
        execute: (db) => db.clients.get(Number(clientId)),
        defaultValue: {},
        deps: [clientId]
      }
    ],
    monitors: (services) => [
      services.clients.getClient,
      services.clients.deleteClient,
      services.clients.updateClient,
      services.clients.clientOperation
    ]
  }
);
const isClientSecretTypeValid = ({ description, type, value }) => {
  let isValid = !!type && !!description;
  if (isValid && type === ClientSecretType.X509Thumbprint && value?.length !== 40) {
    isValid = false;
  }
  return isValid;
};
const ClientSecretForm = ({ onChange }) => {
  const [_clientSecretType, setClientSecretType] = useState(ClientSecretType.SharedSecret);
  const handleChange = (form) => {
    setClientSecretType(form.data.type);
    onChange({
      ...form,
      isValid: isClientSecretTypeValid(form.data),
      data: {
        ...form.data,
        type: form.data.type ?? _clientSecretType
      }
    });
  };
  return /* @__PURE__ */ jsxs(
    Uniformly,
    {
      className: "flex flex-col flex-grow gap-2",
      name: "clientSecretForm",
      onChange: handleChange,
      children: [
        /* @__PURE__ */ jsx(
          TextField$1,
          {
            name: "description",
            label: "Description",
            isRequired: true,
            validation: (x) => x.string().max(2e3).required("Description is Required")
          }
        ),
        /* @__PURE__ */ jsx(DateField, { name: "expiration", label: "Expiration", showMonthAndYearPickers: true }),
        /* @__PURE__ */ jsx(
          ClientSecretTypeLookup,
          {
            name: "type",
            label: "Type",
            isRequired: true,
            validation: (x) => x.string().required("Secret Type is Required")
          }
        ),
        _clientSecretType === ClientSecretType.X509Thumbprint && /* @__PURE__ */ jsx(
          TextField$1,
          {
            name: "value",
            label: "Thumbprint",
            isRequired: true,
            validation: (x) => x.string().max(4e3).required("Thumbprint is Required")
          }
        )
      ]
    }
  );
};
const ClientSecretCreate = ({ onChange, secret }) => /* @__PURE__ */ jsxs(Fragment, { children: [
  /* @__PURE__ */ jsx(ClientSecretForm, { onChange }),
  secret && /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2 mt-2", children: [
    /* @__PURE__ */ jsxs(Alert, { status: "warning", children: [
      /* @__PURE__ */ jsx(Alert.Indicator, {}),
      /* @__PURE__ */ jsxs(Alert.Content, { children: [
        /* @__PURE__ */ jsx(Alert.Title, { children: "Copy and save this secret" }),
        /* @__PURE__ */ jsx(Alert.Description, { children: "This is the only time the client secret will be shown. Copy it and store it in a secure location — it will not be available again once you close this drawer." })
      ] })
    ] }),
    /* @__PURE__ */ jsx(TextFieldWithCopy, { value: secret, label: "Client Secret" })
  ] })
] });
ClientSecretCreate.Drawer = withDrawerController(ClientSecretCreate);
const ClientSecretCreateView = reactive.component(
  ({ clientId, services, monitors }) => {
    clientId = Number(clientId);
    const navigate = useNavigate();
    const [form, setForm] = useState({});
    const [secret, setSecret] = useState(null);
    useSuccessReactor(services.clientSecrets.createClientSecret, (payload) => {
      setSecret(payload.value);
    });
    return /* @__PURE__ */ jsx(
      ClientSecretCreate.Drawer,
      {
        isOpen: true,
        size: "3xl",
        onChange: setForm,
        secret,
        headerContent: () => "Create Client Secret",
        footerContent: () => /* @__PURE__ */ jsxs("div", { className: "flex w-full justify-end gap-x-2", children: [
          /* @__PURE__ */ jsx(ErrorListChip, { errors: form.errors }),
          /* @__PURE__ */ jsx(
            SaveButton,
            {
              size: "sm",
              isDisabled: !form.isValid || !!secret,
              onPress: () => services.clientSecrets.createClientSecret(clientId, form.data),
              isLoading: monitors.createClientSecret.executing,
              label: "Create"
            }
          )
        ] }),
        onClose: () => navigate(RoutePaths().clients.details(clientId))
      }
    );
  },
  {
    name: "ClientSecretCreateView",
    monitors: (services) => [services.clientSecrets.createClientSecret]
  }
);
const columns$2 = [
  { propertyName: "description", name: "Name", isSortable: true },
  { propertyName: "expiration", name: "Expiration", isSortable: true },
  { propertyName: "type", name: "Type", isSortable: true },
  { propertyName: "actions", name: "" }
];
const ClientSecretsList = ({ clientSecrets = [], onDelete }) => {
  const renderCell = useCallback(
    (row, columnKey) => {
      const cellValue = row[columnKey];
      switch (columnKey) {
        case "actions":
          return /* @__PURE__ */ jsx("div", { className: "flex gap-2 justify-end", children: /* @__PURE__ */ jsx(
            DeleteButton,
            {
              size: "sm",
              isIconOnly: true,
              radius: "full",
              variant: "light",
              className: "min-w-8 max-w-8",
              onPress: () => onDelete(row)
            }
          ) });
        case "expiration":
          return cellValue ? parseAndFormatDate(cellValue) : "N/A";
        default:
          return cellValue;
      }
    },
    [onDelete]
  );
  return /* @__PURE__ */ jsx(Table, { children: /* @__PURE__ */ jsx(Table.ScrollContainer, { children: /* @__PURE__ */ jsxs(Table.Content, { "aria-label": "Client secrets table", children: [
    /* @__PURE__ */ jsx(Table.Header, { columns: columns$2, children: (column) => /* @__PURE__ */ jsx(
      Table.Column,
      {
        isRowHeader: true,
        id: column.propertyName,
        allowsSorting: column?.isSortable,
        children: column.name
      },
      column.propertyName
    ) }),
    /* @__PURE__ */ jsx(Table.Body, { items: clientSecrets, children: (row) => /* @__PURE__ */ jsx(Table.Row, { id: row.id, children: columns$2.map((column) => /* @__PURE__ */ jsx(Table.Cell, { children: renderCell(row, column.propertyName) }, column.propertyName)) }, row.id) })
  ] }) }) });
};
const ClientSecretsListView = reactive.component(
  ({ services, data, clientId }) => {
    const navigate = useNavigate();
    const { clientSecrets } = data;
    return /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsx("div", { className: "text-end pb-2", children: /* @__PURE__ */ jsx(
        NewButton,
        {
          size: "sm",
          onPress: () => navigate(RoutePaths().clients.createSecret(clientId))
        }
      ) }),
      /* @__PURE__ */ jsx(
        RequestBroker,
        {
          actionKey: `getClientSecrets-${clientId}`,
          onExecute: () => {
          },
          deps: [clientId],
          children: /* @__PURE__ */ jsx(
            ClientSecretsList,
            {
              clientSecrets,
              onDelete: (item) => services.clientSecrets.deleteClientSecret(clientId, item.id)
            }
          )
        }
      ),
      /* @__PURE__ */ jsx(Outlet, {})
    ] });
  },
  {
    name: "ClientSecretsListController",
    queries: ({ clientId }) => [
      {
        name: "clientSecrets",
        execute: (db) => db.clientSecrets.where({ clientId }).toArray(),
        deps: [clientId]
      }
    ],
    monitors: (services) => [services.clientSecrets.deleteClientSecret]
  }
);
const ApiResourceRouter = () => /* @__PURE__ */ jsx(Routes, { children: /* @__PURE__ */ jsxs(Route, { path: "/", element: /* @__PURE__ */ jsx(ApiResourcesListView, {}), children: [
  /* @__PURE__ */ jsx(Route, { path: "create", element: /* @__PURE__ */ jsx(ApiResourceCreateView, {}) }),
  /* @__PURE__ */ jsx(Route, { path: ":apiResourceId", element: /* @__PURE__ */ jsx(WithRouteParams, { element: ApiResourceUpdateView }) })
] }) });
const IdentityResourceRouter = () => /* @__PURE__ */ jsx(Routes, { children: /* @__PURE__ */ jsxs(Route, { path: "/", element: /* @__PURE__ */ jsx(IdentityResourcesListView, {}), children: [
  /* @__PURE__ */ jsx(Route, { path: "create", element: /* @__PURE__ */ jsx(IdentityResourceCreateView, {}) }),
  /* @__PURE__ */ jsx(
    Route,
    {
      path: ":identityResourceId",
      element: /* @__PURE__ */ jsx(WithRouteParams, { element: IdentityResourceUpdateView })
    }
  )
] }) });
const ClientRouter = () => /* @__PURE__ */ jsxs(Routes, { children: [
  /* @__PURE__ */ jsx(Route, { path: "/", element: /* @__PURE__ */ jsx(ClientsListView, {}) }),
  /* @__PURE__ */ jsx(Route, { path: "create", element: /* @__PURE__ */ jsx(ClientCreateView, {}) }),
  /* @__PURE__ */ jsx(Route, { path: ":clientId", element: /* @__PURE__ */ jsx(WithRouteParams, { element: ClientUpdateView }), children: /* @__PURE__ */ jsx(Route, { path: "secrets", element: /* @__PURE__ */ jsx(WithRouteParams, { element: ClientSecretCreateView }) }) })
] });
const IdentityServerRouter = () => /* @__PURE__ */ jsxs(Routes, { children: [
  /* @__PURE__ */ jsx(Route, { path: "api-resources/*", element: /* @__PURE__ */ jsx(ApiResourceRouter, {}) }),
  /* @__PURE__ */ jsx(Route, { path: "identity-resources/*", element: /* @__PURE__ */ jsx(IdentityResourceRouter, {}) }),
  /* @__PURE__ */ jsx(Route, { path: "clients/*", element: /* @__PURE__ */ jsx(ClientRouter, {}) })
] });
const RoutePaths = () => {
  const baseUrl = appRuntime.baseUrl;
  const rootPaths = {
    applications: baseUrl + "/applications",
    users: baseUrl + "/users",
    apis: baseUrl + "/api-registrations",
    templates: baseUrl + "/templates",
    identityServer: {
      apiResources: baseUrl + "/identity-server/api-resources",
      clients: baseUrl + "/identity-server/clients",
      clientTemplates: baseUrl + "/identity-server/client-templates",
      identityResources: baseUrl + "/identity-server/identity-resources"
    }
  };
  return {
    applications: {
      list: () => rootPaths.applications,
      create: () => `${rootPaths.applications}/create`,
      details: (id) => `${rootPaths.applications}/${id}`,
      clients: (id) => `${rootPaths.applications}/${id}/clients`
    },
    clients: {
      list: () => rootPaths.identityServer.clients,
      create: () => `${rootPaths.identityServer.clients}/create`,
      details: (id) => `${rootPaths.identityServer.clients}/${id}`,
      createSecret: (id) => `${rootPaths.identityServer.clients}/${id}/secrets`
    },
    clientTemplates: {
      list: () => rootPaths.identityServer.clientTemplates,
      create: () => `${rootPaths.identityServer.clientTemplates}/creates`,
      details: (id) => `${rootPaths.identityServer.clientTemplates}/${id}`
    },
    apiResources: {
      list: () => rootPaths.identityServer.apiResources,
      create: () => `${rootPaths.identityServer.apiResources}/create`,
      details: (id) => `${rootPaths.identityServer.apiResources}/${id}`
    },
    identityResources: {
      list: () => rootPaths.identityServer.identityResources,
      create: () => `${rootPaths.identityServer.identityResources}/create`,
      details: (id) => `${rootPaths.identityServer.identityResources}/${id}`
    },
    apis: {
      list: () => rootPaths.apis,
      create: () => `${rootPaths.apis}/create`,
      details: (id) => `${rootPaths.apis}/${id}`,
      resources: (id) => `${rootPaths.apis}/${id}/resources`,
      roles: (id, roleId) => roleId ? `${rootPaths.apis}/${id}/roles/${roleId}` : `${rootPaths.apis}/${id}/roles`,
      createResource: (id) => `${rootPaths.apis}/${id}/resources/create`,
      resourceDetails: (id, resourceId) => `${rootPaths.apis}/${id}/resources/${resourceId}`,
      callbacks: (id) => `${rootPaths.apis}/${id}/callbacks`
    },
    users: {
      list: () => rootPaths.users,
      create: () => `${rootPaths.users}/create`,
      details: (id) => `${rootPaths.users}/${id}/details`,
      security: (id) => `${rootPaths.users}/${id}/security`,
      applications: (id, applicationId) => applicationId ? `${rootPaths.users}/${id}/applications/${applicationId}` : `${rootPaths.users}/${id}/applications`,
      permissions: (id, applicationId) => applicationId ? `${rootPaths.users}/${id}/permissions/${applicationId}` : `${rootPaths.users}/${id}/permissions`,
      tokens: (id) => `${rootPaths.users}/${id}/tokens`,
      authHistory: (id) => `${rootPaths.users}/${id}/auth-history`
    },
    templates: {
      list: () => rootPaths.templates,
      create: () => `${rootPaths.templates}/create`,
      details: (id) => `${rootPaths.templates}/${id}/details`
    }
  };
};
const TemplateForm = ({
  template = {},
  disabled = false,
  isSaving = false,
  isDeleting = false,
  onSave,
  onDelete
}) => {
  const [form, setForm] = useState({});
  return /* @__PURE__ */ jsxs(Uniformly, { className: "flex flex-col gap-4", onChange: setForm, throttle: 5, children: [
    template.id && /* @__PURE__ */ jsx(TextField$1, { name: "id", type: "number", className: "hidden", value: template.id }),
    template.key && /* @__PURE__ */ jsx(TextField$1, { name: "key", className: "hidden", value: template.key }),
    template.key && /* @__PURE__ */ jsx(KeyDisplay, { keyValue: template.key }),
    /* @__PURE__ */ jsx(
      TextField$1,
      {
        disabled,
        name: "name",
        label: "Name",
        placeholder: "Enter the name of the template",
        value: template.name || "",
        isRequired: true,
        validation: (x) => x.string().max(100).required("The name is required")
      }
    ),
    /* @__PURE__ */ jsx(
      TextField$1,
      {
        disabled,
        name: "description",
        label: "Description",
        placeholder: "Enter the description of the template",
        value: template.description || "",
        isRequired: true,
        validation: (x) => x.string().max(1e3).required("The description is required")
      }
    ),
    template.id && /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-4", children: [
      /* @__PURE__ */ jsx("label", { className: "text-foreground", children: "Enabled" }),
      /* @__PURE__ */ jsx(
        SwitchField,
        {
          disabled,
          label: "Enabled",
          name: "isEnabled",
          value: template.isEnabled || false
        }
      )
    ] }),
    /* @__PURE__ */ jsx(
      RolesSelectorField,
      {
        name: "roles",
        templateId: template.id,
        value: template.roles || [],
        disabled
      }
    ),
    /* @__PURE__ */ jsxs(DrawerActions, { className: "justify-between", children: [
      /* @__PURE__ */ jsx("div", { children: template.id && /* @__PURE__ */ jsx(
        DeleteButton,
        {
          size: "sm",
          color: "danger",
          onPress: () => onDelete(template),
          isLoading: isDeleting
        }
      ) }),
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-x-2", children: [
        /* @__PURE__ */ jsx(ErrorListChip, { errors: form.errors }),
        /* @__PURE__ */ jsx(
          SaveButton,
          {
            size: "sm",
            color: "primary",
            isDisabled: !form.isValid || !form.data?.roles?.length,
            onPress: () => onSave(form),
            isLoading: isSaving
          }
        )
      ] })
    ] })
  ] });
};
TemplateForm.Drawer = withDrawerController(TemplateForm);
const useTemplateActions = (services, onSuccess) => {
  const handleSuccess = (payload) => onSuccess?.(payload);
  useSuccessReactor(services.templates.createTemplate, handleSuccess);
  useSuccessReactor(services.templates.updateTemplate, handleSuccess);
  useSuccessReactor(services.templates.deleteTemplate, handleSuccess);
  return {
    save: (form) => {
      if (form.data.id) {
        return services.templates.updateTemplate(form.data.id, form.data);
      }
      return services.templates.createTemplate(form.data);
    },
    deleteTemplate: (data) => services.templates.deleteTemplate(data.id)
  };
};
const TemplateFormView = reactive.component(
  ({ data, services, monitors, templateId }) => {
    const navigate = useNavigate();
    const { template } = data;
    const goToList = () => navigate(RoutePaths().templates.list());
    const { save, deleteTemplate } = useTemplateActions(services, goToList);
    const isSaving = monitors.createTemplate.executing || monitors.updateTemplate.executing;
    const isDeleting = monitors.deleteTemplate.executing;
    const title = templateId ? "Template Details" : "Create Template";
    return /* @__PURE__ */ jsx(
      TemplateForm.Drawer,
      {
        isOpen: true,
        size: "2xl",
        template,
        disabled: isSaving || isDeleting,
        isSaving,
        isDeleting,
        onSave: save,
        onDelete: deleteTemplate,
        headerContent: () => title,
        onClose: goToList
      }
    );
  },
  {
    name: "TemplateFormView",
    sync: (services, { templateId }) => [
      {
        execute: () => templateId && services.templates.getTemplate(Number(templateId)),
        deps: [templateId]
      }
    ],
    queries: ({ templateId }) => [
      {
        name: "template",
        execute: (db) => templateId && db.templates.get(Number(templateId)),
        defaultValue: {},
        deps: [templateId]
      }
    ],
    monitors: (services) => [
      services.templates.createTemplate,
      services.templates.updateTemplate,
      services.templates.getTemplate,
      services.templates.deleteTemplate
    ]
  }
);
const filters$2 = new Filters([
  new FilterInput({
    name: "Name",
    title: "Name"
  }),
  new FilterInput({
    name: "Key",
    title: "Key"
  }),
  new FilterInput({
    name: "Description",
    title: "Description"
  })
]);
const TemplatesColumns = [
  { propertyName: "name", name: "Name", isSortable: true, isResizable: true, minWidth: 100 },
  { propertyName: "key", name: "Key", isSortable: true, isResizable: true, minWidth: 100 },
  {
    propertyName: "description",
    name: "Description",
    isSortable: true,
    isResizable: true,
    minWidth: 100
  }
];
const TemplatesList = ({ templates = [], onSelect }) => {
  const renderCell = useCallback((row, columnKey) => {
    const cellValue = row[columnKey];
    switch (columnKey) {
      case "key":
        return /* @__PURE__ */ jsx(KeyDisplay, { keyValue: cellValue });
      default:
        return cellValue;
    }
  }, []);
  const columns2 = useMemo(
    () => TemplatesColumns.map((column) => ({
      id: column.propertyName,
      header: column.name,
      // `name` is the row's accessible identifier; sorting flows through the
      // request broker via each column's `allowsSorting` + matching `id`.
      isRowHeader: column.propertyName === "name",
      allowsSorting: column.isSortable,
      allowsResizing: column.isResizable,
      minWidth: column.minWidth,
      cell: (row) => renderCell(row, column.propertyName)
    })),
    [renderCell]
  );
  return /* @__PURE__ */ jsx(
    RequestBrokerDataGrid,
    {
      "aria-label": "Templates table",
      chipScope: "global",
      columns: columns2,
      data: templates,
      getRowId: (row) => row.id,
      allowsColumnResize: true,
      onRowAction: (key) => {
        const selected = templates.find((item) => item.id === key);
        if (selected) onSelect(selected);
      }
    }
  );
};
const TemplatesListController = reactive.component(
  ({ data, services, monitors, onSelect, db }) => {
    const { templates } = data;
    const handleExecute = useCallback((action, metadata) => action(metadata), []);
    return /* @__PURE__ */ jsx(
      RequestBroker,
      {
        actionKey: "queryTemplates",
        action: services.templates.queryTemplates,
        filters: filters$2,
        skipInitial: () => db.templates.count(),
        isExecuting: monitors.queryTemplates.executing,
        onExecute: handleExecute,
        children: /* @__PURE__ */ jsx(TemplatesList, { templates, onSelect: (item) => onSelect && onSelect(item) })
      }
    );
  },
  {
    name: "TemplatesListController",
    queries: (_, services) => [
      {
        name: "templates",
        execute: (db) => db.withRequestBroker(services.templates.queryTemplates.action, () => db.templates, {
          sorting: true
        }),
        deps: []
      }
    ],
    monitors: (services) => [services.templates.queryTemplates]
  }
);
const TemplatesListView = () => {
  const navigate = useNavigate();
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("div", { className: "flex flex-row justify-between mb-4", children: /* @__PURE__ */ jsxs(Breadcrumbs, { className: "content-center", children: [
      /* @__PURE__ */ jsx(BreadcrumbsItem, { isDisabled: true, children: "Auth" }),
      /* @__PURE__ */ jsx(BreadcrumbsItem, { children: "Templates" })
    ] }) }),
    /* @__PURE__ */ jsx(
      TemplatesListController,
      {
        onSelect: (template) => navigate(RoutePaths().templates.details(template.id))
      }
    ),
    /* @__PURE__ */ jsx(Outlet, {})
  ] });
};
const apiAssignments = (roles = [], applicationId, apiRegistrationId) => roles.filter((r) => r.applicationId === applicationId && r.apiRegistrationId === apiRegistrationId);
const setApiRoles = (roles = [], { templateId, application, apiRegistration, chosenRoles }) => [
  ...roles.filter(
    (r) => !(r.applicationId === application.id && r.apiRegistrationId === apiRegistration.id)
  ),
  ...chosenRoles.map((role) => ({
    templateId,
    applicationId: application.id,
    apiRegistrationId: apiRegistration.id,
    roleId: role.id,
    applicationName: application.name,
    environment: application.environment,
    apiRegistrationName: apiRegistration.name,
    roleName: role.name
  }))
];
const removeApplication = (roles = [], applicationId) => roles.filter((r) => r.applicationId !== applicationId);
const summarizeByApplication = (roles = []) => sortBy(
  values(groupBy(roles, "applicationId")).map((appRoles) => {
    const { applicationId, applicationName, environment } = appRoles[0];
    return {
      applicationId,
      applicationName,
      environment,
      apiCount: new Set(appRoles.map((r) => r.apiRegistrationId)).size,
      roleCount: appRoles.length
    };
  }),
  "applicationName"
);
const ApplicationPicker = reactive.component(
  ({ data, monitors, value, onChange }) => /* @__PURE__ */ jsxs(
    Select,
    {
      variant: "secondary",
      placeholder: "Select an application...",
      isDisabled: monitors.queryApplications.executing,
      value,
      onChange: (key) => onChange(key == null ? null : Number(key)),
      children: [
        /* @__PURE__ */ jsx(Label, { children: "Application" }),
        /* @__PURE__ */ jsxs(Select.Trigger, { children: [
          /* @__PURE__ */ jsx(Select.Value, {}),
          /* @__PURE__ */ jsx(Select.Indicator, {})
        ] }),
        /* @__PURE__ */ jsx(Select.Popover, { children: /* @__PURE__ */ jsx(ListBox, { items: data.applications, children: (app) => /* @__PURE__ */ jsxs(ListBox.Item, { id: app.id, textValue: `${app.name} ${app.environment}`, children: [
          /* @__PURE__ */ jsxs("div", { className: "inline-flex items-center gap-2", children: [
            app.name,
            " ",
            /* @__PURE__ */ jsx(EnvironmentChip, { environment: app.environment })
          ] }),
          /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
        ] }) }) })
      ]
    }
  ),
  {
    name: "ApplicationPicker",
    sync: (services) => [{ execute: () => services.applications.queryApplications(), deps: [] }],
    queries: () => [
      {
        name: "applications",
        execute: (db) => db.applications.toCollection().sortBy("name"),
        defaultValue: []
      }
    ],
    monitors: (services) => [services.applications.queryApplications]
  }
);
const ApiAssignmentRow = ({
  apiRegistration,
  roles,
  assigned,
  isEnabled,
  isDisabled,
  onToggle,
  onRolesChange
}) => /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4", children: [
  /* @__PURE__ */ jsx(
    Checkbox,
    {
      variant: "secondary",
      isSelected: isEnabled,
      isDisabled,
      onChange: onToggle,
      className: "sm:flex-1",
      children: /* @__PURE__ */ jsxs(Checkbox.Content, { children: [
        /* @__PURE__ */ jsx(Checkbox.Control, { children: /* @__PURE__ */ jsx(Checkbox.Indicator, {}) }),
        /* @__PURE__ */ jsxs("span", { className: "flex flex-wrap items-baseline gap-x-2", children: [
          /* @__PURE__ */ jsx("span", { className: "font-medium", children: apiRegistration.name }),
          apiRegistration.description && /* @__PURE__ */ jsx("span", { className: "text-xs text-muted", children: apiRegistration.description })
        ] })
      ] })
    }
  ),
  /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1 sm:w-64", children: [
    /* @__PURE__ */ jsxs(
      Select,
      {
        "aria-label": `Roles for ${apiRegistration.name}`,
        variant: "secondary",
        placeholder: "Select roles...",
        selectionMode: "multiple",
        isDisabled: isDisabled || !isEnabled,
        isInvalid: isEnabled && assigned.length === 0,
        value: assigned.map((r) => r.roleId),
        onChange: (keys) => onRolesChange(roles.filter((r) => keys.map(Number).includes(r.id))),
        children: [
          /* @__PURE__ */ jsxs(Select.Trigger, { children: [
            /* @__PURE__ */ jsx(Select.Value, {}),
            /* @__PURE__ */ jsx(Select.Indicator, {})
          ] }),
          /* @__PURE__ */ jsx(Select.Popover, { children: /* @__PURE__ */ jsx(ListBox, { selectionMode: "multiple", items: roles, children: (role) => /* @__PURE__ */ jsxs(ListBox.Item, { id: role.id, textValue: role.name, children: [
            role.name,
            /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
          ] }) }) })
        ]
      }
    ),
    isEnabled && assigned.length === 0 && /* @__PURE__ */ jsx("span", { className: "text-xs text-danger", children: "Select a role, or this API is not saved." })
  ] })
] });
const ApplicationApiAssignments = reactive.component(
  ({ data, monitors, applicationId, value, isEnabled, isDisabled, onToggle, onRolesChange }) => {
    const { application, applicationRoles } = data;
    if (monitors.getApplication.executing) {
      return /* @__PURE__ */ jsx("div", { className: "flex justify-center py-6", children: /* @__PURE__ */ jsx(Spinner, { size: "sm" }) });
    }
    const apiRegistrations = application?.apiRegistrations ?? [];
    if (apiRegistrations.length === 0) {
      return /* @__PURE__ */ jsx("p", { className: "py-3 text-sm text-muted", children: "This application has no APIs." });
    }
    return /* @__PURE__ */ jsx("div", { className: "flex flex-col divide-y divide-separator", children: apiRegistrations.map((apiRegistration) => /* @__PURE__ */ jsx(
      ApiAssignmentRow,
      {
        apiRegistration,
        roles: applicationRoles.filter((r) => r.apiRegistrationId === apiRegistration.id),
        assigned: apiAssignments(value, applicationId, apiRegistration.id),
        isEnabled: isEnabled(apiRegistration.id),
        isDisabled,
        onToggle: (checked) => onToggle(application, apiRegistration, checked),
        onRolesChange: (chosenRoles) => onRolesChange(application, apiRegistration, chosenRoles)
      },
      apiRegistration.id
    )) });
  },
  {
    name: "ApplicationApiAssignments",
    sync: (services, { applicationId }) => [
      { execute: () => services.applications.getApplication(applicationId), deps: [applicationId] },
      {
        execute: () => services.applications.getApplicationRoles(applicationId),
        deps: [applicationId]
      }
    ],
    queries: ({ applicationId }) => [
      {
        name: "application",
        execute: (db) => db.applications.get(applicationId),
        deps: [applicationId]
      },
      {
        name: "applicationRoles",
        execute: (db) => db.applicationRoles.where({ applicationId }).toArray(),
        defaultValue: [],
        deps: [applicationId]
      }
    ],
    monitors: (services) => [services.applications.getApplication]
  }
);
const apiKey = (applicationId, apiRegistrationId) => `${applicationId}:${apiRegistrationId}`;
const RolesSelectorField = FormField(({ templateId, value = [], onChange, disabled }) => {
  const [applicationId, setApplicationId] = useState(null);
  const [pending, setPending] = useState(() => /* @__PURE__ */ new Set());
  const summary = summarizeByApplication(value);
  const isEnabled = (apiRegistrationId) => apiAssignments(value, applicationId, apiRegistrationId).length > 0 || pending.has(apiKey(applicationId, apiRegistrationId));
  const setPendingKey = (key, on) => setPending((current) => {
    const next = new Set(current);
    if (on) next.add(key);
    else next.delete(key);
    return next;
  });
  const handleToggle = (application, apiRegistration, checked) => {
    setPendingKey(apiKey(application.id, apiRegistration.id), checked);
    if (!checked) {
      onChange(setApiRoles(value, { templateId, application, apiRegistration, chosenRoles: [] }));
    }
  };
  const handleRolesChange = (application, apiRegistration, chosenRoles) => {
    setPendingKey(apiKey(application.id, apiRegistration.id), chosenRoles.length === 0);
    onChange(setApiRoles(value, { templateId, application, apiRegistration, chosenRoles }));
  };
  const handleClearApplication = (id) => {
    setPending((current) => new Set([...current].filter((key) => !key.startsWith(`${id}:`))));
    onChange(removeApplication(value, id));
  };
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-4", children: [
    /* @__PURE__ */ jsxs("h3", { className: "text-lg font-semibold", children: [
      "Roles assigned (",
      value.length,
      ")"
    ] }),
    summary.length > 0 && /* @__PURE__ */ jsx("div", { className: "flex flex-wrap gap-2", children: summary.map((app) => /* @__PURE__ */ jsxs(
      "div",
      {
        className: "flex items-center gap-1 rounded-full border border-default pl-1",
        children: [
          /* @__PURE__ */ jsxs(
            Button,
            {
              size: "sm",
              variant: app.applicationId === applicationId ? "secondary" : "ghost",
              isDisabled: disabled,
              onPress: () => setApplicationId(app.applicationId),
              children: [
                app.applicationName,
                app.environment && /* @__PURE__ */ jsx(EnvironmentChip, { environment: app.environment }),
                /* @__PURE__ */ jsxs("span", { className: "text-muted", children: [
                  app.apiCount,
                  " ",
                  app.apiCount === 1 ? "API" : "APIs",
                  " · ",
                  app.roleCount,
                  " ",
                  app.roleCount === 1 ? "role" : "roles"
                ] })
              ]
            }
          ),
          /* @__PURE__ */ jsx(
            CloseButton,
            {
              "aria-label": `Remove all assignments for ${app.applicationName}`,
              isDisabled: disabled,
              onPress: () => handleClearApplication(app.applicationId)
            }
          )
        ]
      },
      app.applicationId
    )) }),
    /* @__PURE__ */ jsx(ApplicationPicker, { value: applicationId, onChange: setApplicationId }),
    applicationId != null && /* @__PURE__ */ jsx(
      ApplicationApiAssignments,
      {
        applicationId,
        value,
        isEnabled,
        isDisabled: disabled,
        onToggle: handleToggle,
        onRolesChange: handleRolesChange
      }
    )
  ] });
});
const ApplyTemplateForm = reactive.component(
  ({ services, monitors, userId, onSuccess }) => {
    const [form, setForm] = useState({});
    useSuccessReactor(services.templates.applyTemplate, () => onSuccess?.());
    return /* @__PURE__ */ jsxs(FlatCard, { className: "p-1 rounded-none", children: [
      /* @__PURE__ */ jsx(FlatCard.Main, { children: /* @__PURE__ */ jsxs(Uniformly, { className: "flex flex-col gap-4", onChange: setForm, children: [
        /* @__PURE__ */ jsx(TextField$1, { name: "userId", value: userId, className: "hidden" }),
        /* @__PURE__ */ jsx(
          TemplatesLookup,
          {
            name: "templateId",
            label: "Select Template",
            placeholder: "Select a template",
            isRequired: true
          }
        )
      ] }) }),
      /* @__PURE__ */ jsx(FlatCard.Footer, { className: "flex flex-row justify-end", children: /* @__PURE__ */ jsx(
        SaveButton,
        {
          color: "primary",
          onPress: () => services.templates.applyTemplate(
            Number(form.data.templateId),
            Number(form.data.userId)
          ),
          isLoading: monitors.applyTemplate.executing,
          isDisabled: !form.isValid || !form.data.templateId,
          label: "Apply",
          iconClass: "ri-check-double-line"
        }
      ) })
    ] });
  },
  {
    name: "ApplyTemplateController",
    monitors: (services) => [services.templates.applyTemplate]
  }
);
ApplyTemplateForm.Modal = withModalController(({ onSuccess, ...props }) => /* @__PURE__ */ jsx(ApplyTemplateForm, { onSuccess, ...props }));
const TemplatesRouter = () => /* @__PURE__ */ jsx(Routes, { children: /* @__PURE__ */ jsxs(Route, { path: "/", element: /* @__PURE__ */ jsx(TemplatesListView, {}), children: [
  /* @__PURE__ */ jsx(Route, { path: "create", element: /* @__PURE__ */ jsx(TemplateFormView, {}) }),
  /* @__PURE__ */ jsx(Route, { path: ":templateId", children: /* @__PURE__ */ jsx(Route, { path: "details", element: /* @__PURE__ */ jsx(WithRouteParams, { element: TemplateFormView }) }) })
] }) });
const UserApplicationNavigationTabs = ({ userId, userApplications, applications }) => {
  const location = useLocation();
  const currentApplicationId = location.pathname.split("/").filter(Boolean).pop();
  const selectedKeys = applications.map((x) => x.id.toString()).filter((x) => x === currentApplicationId);
  return /* @__PURE__ */ jsx(Card, { className: "grow", variant: "secondary", children: /* @__PURE__ */ jsx(ScrollShadow, { hideScrollBar: true, offset: 1, children: /* @__PURE__ */ jsx(
    ListBox,
    {
      "aria-label": "options",
      disallowEmptySelection: true,
      selectionMode: "single",
      selectedKeys: new Set(selectedKeys),
      children: applications?.map((application) => {
        return /* @__PURE__ */ jsxs(
          ListBox.Item,
          {
            id: `${application.id}`,
            href: RoutePaths().users.applications(userId, application.id),
            "aria-label": application.name,
            textValue: application.name,
            children: [
              /* @__PURE__ */ jsx(EnvironmentChip, { environment: application.environment, mode: "abbr" }),
              /* @__PURE__ */ jsx(Label, { className: "truncate", children: application.name }),
              /* @__PURE__ */ jsx("div", { className: "flex items-center gap-1", children: /* @__PURE__ */ jsx(
                "i",
                {
                  className: `${userApplications.some((x) => x.id === application.id) ? "ri-checkbox-circle-fill text-green-500" : "ri-checkbox-blank-circle-line"}`
                }
              ) })
            ]
          },
          `${application.id}`
        );
      })
    }
  ) }) });
};
const UserForm = ({ user, onChange, onValidate, validUsername }) => {
  const [username, setUsername] = useState(user?.username ?? "");
  const [showPassword, togglePassword] = useState(false);
  const handleFormChanged = useCallback(
    (form) => {
      form.isValid = form.data.togglePassword ? form.isValid && form.data.password?.isValid : form.isValid;
      togglePassword(form.data.togglePassword);
      onChange(form);
    },
    [onChange]
  );
  return /* @__PURE__ */ jsxs(
    Uniformly,
    {
      className: "flex flex-col flex-grow gap-4",
      name: "user",
      onChange: handleFormChanged,
      deps: [user?.id],
      children: [
        /* @__PURE__ */ jsx(SwitchField, { name: "enabled", value: user?.enabled || true, children: "Enabled" }),
        /* @__PURE__ */ jsx(
          UsersLookup,
          {
            name: "parentUserId",
            label: "Parent User",
            userId: user?.id,
            value: user?.parentUserId,
            displayValue: user?.parentUser
          }
        ),
        /* @__PURE__ */ jsx(
          TextField$1,
          {
            name: "firstName",
            label: "First Name",
            value: user?.firstName,
            isRequired: true,
            validation: (x) => x.string().max(50).required("First Name is required")
          }
        ),
        /* @__PURE__ */ jsx(
          TextField$1,
          {
            name: "lastName",
            label: "Last Name",
            value: user?.lastName,
            isRequired: true,
            validation: (x) => x.string().max(50).required("Last Name is required")
          }
        ),
        /* @__PURE__ */ jsx(
          TextField$1,
          {
            name: "displayName",
            label: "Display Name",
            value: user?.displayName,
            isRequired: true,
            validation: (x) => x.string().max(100).required("Display Name is required")
          }
        ),
        /* @__PURE__ */ jsx(
          EmailField,
          {
            name: "email",
            label: "Email",
            value: user?.email,
            onBlur: (e) => {
              if (!username) {
                setUsername(e.target.value);
                onValidate(e.target.value);
              }
            },
            isRequired: true,
            validation: (x) => x.string().max(200).required("Email is required")
          }
        ),
        /* @__PURE__ */ jsx(
          TextField$1,
          {
            name: "mobilePhone",
            label: "Phone Number",
            value: user?.mobilePhone,
            validation: (x) => x.string().max(25).notRequired()
          }
        ),
        /* @__PURE__ */ jsx(SwitchField, { name: "requiresPasswordReset", value: user?.requiresPasswordReset ?? false, children: "Require user to change password on next login" }),
        !user?.id && /* @__PURE__ */ jsxs(Fragment, { children: [
          !validUsername && /* @__PURE__ */ jsxs(Alert, { status: "danger", children: [
            /* @__PURE__ */ jsx(Alert.Indicator, {}),
            /* @__PURE__ */ jsxs(Alert.Content, { children: [
              /* @__PURE__ */ jsx(Alert.Title, { children: "A unique username is required" }),
              /* @__PURE__ */ jsx(Alert.Description, { children: "Username is required and must not be in use by another user." })
            ] })
          ] }),
          /* @__PURE__ */ jsx(
            TextField$1,
            {
              name: "username",
              value: username,
              isInvalid: !validUsername,
              label: "User Name",
              onBlur: (e) => onValidate(e.target.value),
              isRequired: true,
              validation: (x) => x.string().max(200).required("Username is required")
            }
          ),
          /* @__PURE__ */ jsx(SwitchField, { name: "togglePassword", value: false, children: "Set Password" }),
          showPassword && /* @__PURE__ */ jsx(ValidatePassword, { name: "password", label: "Password" })
        ] })
      ]
    }
  );
};
const UserCreateController = reactive.component(
  ({ services, monitors, onSuccess }) => {
    const [form, setForm] = useState({});
    const [isValidUsername, setIsValidUsername] = useState(false);
    useSuccessReactor(services.users.createUser, (payload) => {
      onSuccess && onSuccess(payload);
    });
    useSuccessReactor(services.users.userOperation, (payload) => {
      const { status } = payload;
      setIsValidUsername(!status);
    });
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(
        UserForm,
        {
          onChange: setForm,
          validUsername: isValidUsername,
          onValidate: (value) => services.users.userOperation("validate-username", value)
        }
      ),
      /* @__PURE__ */ jsxs("div", { className: "pb-2 pt-2 flex gap-2 justify-end", children: [
        /* @__PURE__ */ jsx(Button, { size: "sm", slot: "close", variant: "secondary", children: "Cancel" }),
        /* @__PURE__ */ jsx(
          Button,
          {
            size: "sm",
            variant: "primary",
            isDisabled: !(form.isValid && isValidUsername),
            onPress: () => services.users.createUser({
              user: form.data,
              credentials: {
                username: form.data.username,
                hashedPassword: form.data.password?.value
              }
            }),
            isPending: monitors.createUser.executing,
            children: "Create"
          }
        )
      ] })
    ] });
  },
  {
    name: "UserCreateController",
    monitors: (services) => [services.users.createUser, services.users.userOperation]
  }
);
UserCreateController.Modal = withModalController(({ onSuccess, ...props }) => /* @__PURE__ */ jsx(UserCreateController, { onSuccess, ...props }));
const UserCreateView = () => {
  const navigate = useNavigate();
  return /* @__PURE__ */ jsx(
    UserCreateController.Modal,
    {
      isOpen: true,
      isDismissable: false,
      title: "Create User",
      onSuccess: (payload) => navigate(RoutePaths().users.details(payload.id)),
      onClose: () => navigate(RoutePaths().users.list())
    }
  );
};
const UserContext = createContext();
const UserProvider = reactive.component(
  ({ data, monitors, children }) => {
    const { user, credentials, userApplications, applications } = data;
    useRecentView({
      title: user?.displayName,
      route: user?.id ? RoutePaths().users.details(user.id) : void 0
    });
    if (monitors.getUser.executing) return /* @__PURE__ */ jsx(Loading, { label: "Loading...", color: "primary" });
    if (monitors.getUser.complete && isEmpty(user)) return /* @__PURE__ */ jsx(NotFound, { resourceType: "User" });
    return /* @__PURE__ */ jsx(UserContext.Provider, { value: { user, credentials, userApplications, applications }, children });
  },
  {
    name: "UserProvider",
    sync: (services, { userId }) => [
      {
        execute: () => services.users.getUser(userId),
        bypass: (db) => db.users.get(userId),
        deps: [userId]
      },
      {
        execute: () => services.users.getUserApplications(userId),
        bypass: (db) => db.userApplications.where({ userId }).count(),
        deps: [userId]
      },
      {
        execute: () => services.applications.queryApplications(),
        bypass: (db) => db.applications.count(),
        deps: [userId]
      }
    ],
    queries: ({ userId }) => [
      {
        name: "user",
        execute: (db) => db.users.get(userId),
        defaultValue: {},
        deps: [userId]
      },
      {
        name: "credentials",
        execute: (db) => db.credentials.get(userId),
        defaultValue: {},
        deps: [userId]
      },
      {
        name: "userApplications",
        execute: (db) => db.userApplications.where({ userId }).toArray(),
        deps: [userId]
      },
      {
        name: "applications",
        execute: (db) => db.applications.toCollection().sortBy("name")
      }
    ],
    monitors: (services) => [
      services.users.getUser,
      services.users.getUserApplications,
      services.applications.queryApplications
    ]
  }
);
const withUser = (WrappedComponent) => {
  const Wrapper = ({ userId, ...props }) => {
    const context = React.useContext(UserContext);
    if (!context || !context?.user?.id)
      return /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx("p", { children: /* @__PURE__ */ jsx("strong", { children: "Error" }) }),
        /* @__PURE__ */ jsxs("p", { children: [
          "A component using ",
          /* @__PURE__ */ jsx("strong", { children: "withUser" }),
          " must be wrapped with",
          " ",
          /* @__PURE__ */ jsx("strong", { children: "UserProvider" }),
          "."
        ] })
      ] });
    return /* @__PURE__ */ jsx(WrappedComponent, { ...props, userId, ...context });
  };
  return Wrapper;
};
const UserDetailsProfile = withUser(({ user }) => {
  const initials = (user.displayName || "").split(" ").filter(Boolean).map((word) => word[0]).join("").toUpperCase().slice(0, 2);
  return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
    /* @__PURE__ */ jsxs(Avatar, { size: "sm", className: "min-w-8", children: [
      /* @__PURE__ */ jsx(Avatar.Image, { src: user.avatarUrl, alt: user.displayName }),
      /* @__PURE__ */ jsx(Avatar.Fallback, { children: initials })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "flex flex-col", children: [
      /* @__PURE__ */ jsx("span", { className: "text-sm font-medium", children: user.displayName }),
      /* @__PURE__ */ jsx("span", { className: "text-muted text-xs", children: user.email })
    ] })
  ] });
});
const UserDetailsNavigationTabs = ({ userId }) => {
  const location = useLocation();
  const selectedKeys = ["details", "security", "applications", "permissions", "templates"].filter(
    (x) => location.pathname.includes(x)
  );
  const config = useConfig();
  const user = useUser();
  return /* @__PURE__ */ jsxs(
    ListBox,
    {
      "aria-label": "options",
      disallowEmptySelection: true,
      selectionMode: "single",
      selectedKeys,
      children: [
        /* @__PURE__ */ jsxs(
          ListBox.Item,
          {
            id: "details",
            textValue: "General",
            href: RoutePaths().users.details(userId),
            "aria-label": "General",
            children: [
              /* @__PURE__ */ jsx("i", { className: "ri-user-3-line" }),
              /* @__PURE__ */ jsx(Label, { children: "General" })
            ]
          },
          "details"
        ),
        /* @__PURE__ */ jsxs(
          ListBox.Item,
          {
            id: "security",
            textValue: "Security",
            href: RoutePaths().users.security(userId),
            "aria-label": "Security",
            children: [
              /* @__PURE__ */ jsx("i", { className: "ri-shield-keyhole-line" }),
              /* @__PURE__ */ jsx(Label, { children: "Security" })
            ]
          },
          "security"
        ),
        /* @__PURE__ */ jsxs(
          ListBox.Item,
          {
            id: "applications",
            textValue: "Permissions",
            href: RoutePaths().users.applications(userId),
            "aria-label": "applications",
            children: [
              /* @__PURE__ */ jsx("i", { className: "ri-apps-2-line" }),
              /* @__PURE__ */ jsx(Label, { children: "Permissions" })
            ]
          },
          "applications"
        ),
        user.hasPolicy(config.api.apiKey, "apitoken", "all") && /* @__PURE__ */ jsxs(
          ListBox.Item,
          {
            id: "tokens",
            textValue: "API Tokens",
            href: RoutePaths().users.tokens(userId),
            "aria-label": "tokens",
            children: [
              /* @__PURE__ */ jsx("i", { className: "ri-key-line" }),
              /* @__PURE__ */ jsx(Label, { children: "API Tokens" })
            ]
          },
          "tokens"
        ),
        /* @__PURE__ */ jsxs(
          ListBox.Item,
          {
            id: "auth-history",
            textValue: "Login History",
            href: RoutePaths().users.authHistory(userId),
            "aria-label": "auth-history",
            children: [
              /* @__PURE__ */ jsx("i", { className: "ri-history-line" }),
              /* @__PURE__ */ jsx(Label, { children: "Login History" })
            ]
          },
          "auth-history"
        )
      ]
    }
  );
};
const UserDetailsView = ({ userId }) => {
  userId = Number(userId);
  const navigate = useNavigate();
  return /* @__PURE__ */ jsxs(UserProvider, { userId, children: [
    /* @__PURE__ */ jsx("div", { className: "flex flex-row justify-between mb-4 items-center", children: /* @__PURE__ */ jsxs(Breadcrumbs, { className: "content-center", children: [
      /* @__PURE__ */ jsx(BreadcrumbsItem, { isDisabled: true, children: "Auth" }),
      /* @__PURE__ */ jsx(BreadcrumbsItem, { onPress: () => navigate(RoutePaths().users.list()), children: "Users" }),
      /* @__PURE__ */ jsx(BreadcrumbsItem, { children: "Details" })
    ] }) }),
    /* @__PURE__ */ jsxs(SplitCard, { children: [
      /* @__PURE__ */ jsx(SplitCard.Header, { children: /* @__PURE__ */ jsx(UserDetailsProfile, { userId }) }),
      /* @__PURE__ */ jsxs(SplitCard.Body, { children: [
        /* @__PURE__ */ jsx(SplitCard.Sidebar, { children: /* @__PURE__ */ jsx(UserDetailsNavigationTabs, { userId }) }),
        /* @__PURE__ */ jsx(SplitCard.Main, { children: /* @__PURE__ */ jsx(Outlet, {}) })
      ] })
    ] })
  ] });
};
const UsersColumns = [
  { propertyName: "displayName", name: "Name", isSortable: true, isResizable: true, minWidth: 200 },
  { propertyName: "email", name: "Email", isSortable: false, isResizable: true, minWidth: 220 },
  { propertyName: "username", name: "Username", isSortable: false, isResizable: true, minWidth: 150 },
  { propertyName: "enabled", name: "Enabled", isSortable: false },
  {
    propertyName: "lastLoginUtc",
    name: "Last Login",
    isSortable: false,
    isResizable: true,
    minWidth: 150
  }
];
const UserActionMenu = reactive.component(
  ({ user, services, monitors }) => {
    const [triggerIcon, setTriggerIcon] = useState("ri-more-2-fill");
    const [active, setActive] = useState(false);
    const items = useMemo(
      () => [
        {
          key: "toggle-enabled",
          title: user.enabled ? "Disable User" : "Enable User"
        },
        {
          key: "password-setup",
          title: "Resend Password Setup Email"
        }
      ],
      [user.enabled]
    );
    const handleAction = useCallback(
      (actionKey) => {
        switch (actionKey) {
          case "toggle-enabled":
            services.users.updateUser(user.id, { ...user, enabled: !user.enabled });
            break;
          case "password-setup":
            setActive(true);
            services.users.userOperation("setup-password", user.id);
            break;
        }
      },
      [services, user]
    );
    useSuccessReactor(services.users.userOperation, () => {
      if (active) {
        setActive(false);
        setTriggerIcon("ri-checkbox-circle-fill text-success");
        setTimeout(() => {
          setTriggerIcon("ri-more-2-fill");
        }, 3e3);
      }
    });
    return /* @__PURE__ */ jsxs(Dropdown, { isDisabled: monitors.userOperation.executing, children: [
      /* @__PURE__ */ jsx(
        Button,
        {
          "aria-label": "User actions",
          isIconOnly: true,
          size: "sm",
          variant: "tertiary",
          className: "rounded-full",
          children: monitors.userOperation.executing && active ? /* @__PURE__ */ jsx(Spinner, { size: "sm" }) : /* @__PURE__ */ jsx("i", { className: triggerIcon })
        }
      ),
      /* @__PURE__ */ jsx(Dropdown.Popover, { children: /* @__PURE__ */ jsx(Dropdown.Menu, { items, onAction: (key) => handleAction(key), children: (item) => /* @__PURE__ */ jsx(Dropdown.Item, { id: item.key, textValue: item.title, children: /* @__PURE__ */ jsx(Label, { children: item.title }) }, item.key) }) })
    ] });
  },
  {
    name: "UserActionMenu",
    monitors: (services) => [services.users.userOperation]
  }
);
const getInitials = (name) => (name || "").split(" ").filter(Boolean).map((word) => word[0]).join("").toUpperCase().slice(0, 2);
const UsersList = ({ users = [], onSelect }) => {
  const renderCell = useCallback((row, columnKey) => {
    const cellValue = row[columnKey];
    switch (columnKey) {
      case "displayName":
        return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsxs(Avatar, { className: "size-7 rounded-full", children: [
            /* @__PURE__ */ jsx(Avatar.Image, { src: row.avatarUrl, alt: row.displayName }),
            /* @__PURE__ */ jsx(Avatar.Fallback, { children: getInitials(row.displayName) })
          ] }),
          /* @__PURE__ */ jsx("span", { className: "truncate", children: row.displayName })
        ] });
      case "email":
        return cellValue ? /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1", children: [
          /* @__PURE__ */ jsx("span", { className: "truncate", children: cellValue }),
          /* @__PURE__ */ jsx(CopyToClipboard, { text: cellValue })
        ] }) : null;
      case "username":
        return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1", children: [
          /* @__PURE__ */ jsx("span", { className: "truncate", children: cellValue || "[external login]" }),
          cellValue && /* @__PURE__ */ jsx(CopyToClipboard, { text: cellValue })
        ] });
      case "enabled":
        return /* @__PURE__ */ jsxs(Chip, { size: "sm", color: cellValue ? "success" : "danger", children: [
          /* @__PURE__ */ jsx(RiCircleFill, { size: "8" }),
          /* @__PURE__ */ jsx(Chip.Label, { className: "capitalize", children: cellValue ? "Yes" : "No" })
        ] });
      case "lastLoginUtc":
        return cellValue ? parseAndFormatDateTime(cellValue) : "Never";
      default:
        return cellValue;
    }
  }, []);
  const columns2 = useMemo(
    () => [
      ...UsersColumns.map((column) => ({
        id: column.propertyName,
        header: column.name,
        isRowHeader: column.propertyName === "displayName",
        allowsSorting: column.isSortable,
        allowsResizing: column.isResizable,
        minWidth: column.minWidth,
        cell: (row) => renderCell(row, column.propertyName)
      })),
      // Pinned actions column: enable/disable + resend password setup email.
      {
        id: "actions",
        header: "",
        pinned: "end",
        width: 64,
        minWidth: 64,
        align: "center",
        cell: (row) => /* @__PURE__ */ jsx(UserActionMenu, { user: row })
      }
    ],
    [renderCell]
  );
  return /* @__PURE__ */ jsx(
    RequestBrokerDataGrid,
    {
      "aria-label": "Users table",
      hideChips: true,
      topContent: /* @__PURE__ */ jsx("div", { className: "w-3/4", children: /* @__PURE__ */ jsx(HorizontalFilters, { scope: "custom" }) }),
      columns: columns2,
      data: users,
      getRowId: (row) => row.id,
      allowsColumnResize: true,
      onRowAction: (key) => {
        const selected = users.find((item) => item.id === key);
        if (selected) onSelect(selected);
      }
    }
  );
};
const filters$1 = new Filters([
  new FilterInput({
    name: "DisplayName",
    title: "Display Name",
    defaultValue: ""
  }),
  new FilterInput({
    name: "FirstName",
    title: "First Name",
    defaultValue: "",
    options: {
      scope: "custom"
    },
    render: {
      hideContainer: true
    }
  }),
  new FilterInput({
    name: "LastName",
    title: "Last Name",
    defaultValue: "",
    options: {
      scope: "custom"
    },
    render: {
      hideContainer: true
    }
  }),
  new FilterInput({
    name: "Email",
    title: "Email",
    defaultValue: "",
    options: {
      scope: "custom"
    },
    render: {
      hideContainer: true
    }
  }),
  new FilterInput({
    name: "Username",
    title: "Username",
    defaultValue: "",
    options: {
      scope: "custom"
    },
    render: {
      hideContainer: true
    }
  }),
  new FilterToggle({
    name: "Enabled",
    title: "Enabled",
    defaultValue: true
  })
]);
const UsersListController = reactive.component(
  ({ data, services, monitors, db, onSelect }) => {
    const { users } = data;
    const handleExecute = useCallback((action, metadata) => {
      action(metadata);
    }, []);
    return /* @__PURE__ */ jsx(
      RequestBroker,
      {
        actionKey: "queryUsers",
        action: services.users.queryUsers,
        filters: filters$1,
        isExecuting: monitors.queryUsers.executing,
        onExecute: handleExecute,
        skipInitial: () => db.users.count(),
        children: /* @__PURE__ */ jsx(UsersList, { users, onSelect: (item) => onSelect && onSelect(item) })
      }
    );
  },
  {
    name: "UsersListController",
    queries: (_, services) => [
      {
        name: "users",
        execute: (db) => db.withRequestBroker(services.users.queryUsers.action, () => db.users, {
          sorting: true
        })
      }
    ],
    monitors: (services) => [services.users.queryUsers]
  }
);
const UsersListView = () => {
  const navigate = useNavigate();
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("div", { className: "flex flex-row justify-between mb-2", children: /* @__PURE__ */ jsxs(Breadcrumbs, { className: "content-center", children: [
      /* @__PURE__ */ jsx(BreadcrumbsItem, { isDisabled: true, children: "Auth" }),
      /* @__PURE__ */ jsx(BreadcrumbsItem, { children: "Users" })
    ] }) }),
    /* @__PURE__ */ jsx(UsersListController, { onSelect: (item) => navigate(RoutePaths().users.details(item.id)) }),
    /* @__PURE__ */ jsx(Outlet, {})
  ] });
};
const UserUpdateController = withUser(
  reactive.component(
    ({ services, monitors, user, onDelete }) => {
      const [form, setForm] = useState({});
      useSuccessReactor(services.users.deleteUser, () => {
        onDelete && onDelete();
      });
      if (!user?.id) return /* @__PURE__ */ jsx(Spinner, { color: "accent" });
      return /* @__PURE__ */ jsxs(FlatCard, { children: [
        /* @__PURE__ */ jsx(FlatCard.Header, { children: /* @__PURE__ */ jsx("p", { className: "text-lg", children: "General" }) }),
        /* @__PURE__ */ jsx(FlatCard.Main, { children: /* @__PURE__ */ jsx(UserForm, { user, onChange: setForm, validUsername: true }) }),
        /* @__PURE__ */ jsx(FlatCard.Footer, { children: /* @__PURE__ */ jsx(
          ResourceActions,
          {
            isSaveEnabled: form.isValid,
            isLoading: monitors.updateUser.executing,
            onArchive: () => services.users.deleteUser(user.id),
            onSave: () => services.users.updateUser(user.id, {
              ...user,
              ...form.data
            })
          }
        ) })
      ] });
    },
    {
      name: "UserUpdateController",
      monitors: (services) => [
        services.users.updateUser,
        services.users.deleteUser,
        services.users.getUser
      ]
    }
  )
);
const UserUpdateView = ({ userId }) => {
  userId = Number(userId);
  const navigate = useNavigate();
  return /* @__PURE__ */ jsx(UserUpdateController, { userId, onDelete: () => navigate(RoutePaths().users.list()) });
};
const useUserContext = () => {
  const context = useContext(UserContext);
  return {
    user: context?.user || {},
    credentials: context?.credentials || {},
    userApplications: context?.userApplications || [],
    applications: context?.applications || []
  };
};
const UserApplicationsView = withUser(
  reactive.component(
    ({ userId, userApplications, applications, applicationId, services, monitors }) => {
      userId = Number(userId);
      const [applyModal, ApplyTemplateModal] = useModalController(ApplyTemplateForm.Modal, {
        title: "Apply Template",
        isOpen: false
      });
      return /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsxs(FlatCard, { children: [
        /* @__PURE__ */ jsxs(FlatCard.Header, { className: "flex flex-row items-center justify-between gap-3", children: [
          /* @__PURE__ */ jsx(FlatCard.Title, { children: "Applications" }),
          /* @__PURE__ */ jsxs("div", { className: "flex flex-row gap-2", children: [
            /* @__PURE__ */ jsx(
              ReloadButton,
              {
                size: "sm",
                variant: "secondary",
                isIconOnly: true,
                onPress: () => {
                  services.applications.queryApplications();
                  services.users.getUserApplications(userId);
                },
                isLoading: monitors.getUserApplications.executing || monitors.queryApplications.executing,
                className: "text-default-500 hover:text-default-700 rounded-full"
              }
            ),
            /* @__PURE__ */ jsx(ApplyTemplateModal, { userId, applicationId }),
            /* @__PURE__ */ jsxs(
              Button,
              {
                onPress: applyModal.open,
                variant: "tertiary",
                className: "font-medium",
                size: "sm",
                children: [
                  /* @__PURE__ */ jsx("i", { className: "ri-download-cloud-line text-lg" }),
                  "Apply Template"
                ]
              }
            )
          ] })
        ] }),
        /* @__PURE__ */ jsx(FlatCard.Main, { className: "overflow-hidden", children: /* @__PURE__ */ jsxs(
          Resizable,
          {
            orientation: "horizontal",
            autoSaveId: "user-applications:panels",
            className: "h-full",
            children: [
              /* @__PURE__ */ jsx(
                Resizable.Panel,
                {
                  id: "sidebar",
                  defaultSize: "240px",
                  minSize: "180px",
                  maxSize: "360px",
                  groupResizeBehavior: "preserve-pixel-size",
                  children: /* @__PURE__ */ jsx("div", { className: "h-full overflow-y-auto pr-4", children: /* @__PURE__ */ jsx(
                    UserApplicationNavigationTabs,
                    {
                      userId,
                      applicationId,
                      userApplications,
                      applications
                    }
                  ) })
                }
              ),
              /* @__PURE__ */ jsx(Resizable.Handle, { withIndicator: true }),
              /* @__PURE__ */ jsx(Resizable.Panel, { id: "content", minSize: 30, children: /* @__PURE__ */ jsx("div", { className: "h-full overflow-y-auto pl-4", children: /* @__PURE__ */ jsx(Outlet, {}) }) })
            ]
          }
        ) })
      ] }) });
    },
    {
      name: "UserApplicationsView",
      monitors: (services) => [
        services.users.getUserApplications,
        services.applications.queryApplications
      ]
    }
  )
);
const UserApplicationContext = createContext();
const UserApplicationProvider = reactive.component(
  ({ data, monitors, children }) => {
    const { applicationRoles, userRoles } = data;
    if (monitors.getUserRoles.executing || monitors.getApplicationRoles.executing)
      return /* @__PURE__ */ jsx(Loading, { label: "Loading...", color: "primary" });
    return /* @__PURE__ */ jsx(UserApplicationContext.Provider, { value: { applicationRoles, userRoles }, children });
  },
  {
    name: "UserApplicationProvider",
    sync: (services, { userId, applicationId }) => [
      {
        execute: () => services.applications.getApplicationRoles(applicationId),
        deps: [applicationId]
      },
      {
        execute: () => services.users.getUserRoles(userId, applicationId),
        deps: [userId, applicationId]
      }
    ],
    queries: ({ userId, applicationId }) => [
      {
        name: "applicationRoles",
        execute: (db) => db.applicationRoles.where({ applicationId }).toArray(),
        deps: [applicationId]
      },
      {
        name: "userRoles",
        execute: (db) => db.userRoles.where({ userId }).toArray(),
        deps: [userId]
      }
    ],
    monitors: (services) => [services.applications.getApplicationRoles, services.users.getUserRoles]
  }
);
const withUserApplication = (WrappedComponent) => {
  const Wrapper = (props) => {
    const context = React.useContext(UserApplicationContext);
    return /* @__PURE__ */ jsx(WrappedComponent, { ...props, ...context });
  };
  return Wrapper;
};
const useUserApplicationContext = () => {
  const context = useContext(UserApplicationContext);
  return {
    applicationRoles: context?.applicationRoles || [],
    userRoles: context?.userRoles || []
  };
};
const UserApiRolesController = withUserApplication(
  reactive.component(
    ({ services, monitors, userId, apiRegistrationId }) => {
      const { applicationRoles, userRoles } = useUserApplicationContext();
      const roles = applicationRoles.filter((x) => x.apiRegistrationId === apiRegistrationId);
      const selected = userRoles.map((x) => x.id.toString());
      const disabled = monitors.addUserRole.executing || monitors.removeUserRole.executing ? roles.map((x) => x.id.toString()) : [];
      const handleSelectionChanged = useCallback(
        (changed) => {
          const existing = userRoles.map((x) => x.id.toString());
          const diff = xor(existing, changed);
          if (existing.some((x) => x === diff[0])) {
            services.users.removeUserRole(userId, Number(diff[0]));
          } else {
            services.users.addUserRole(userId, Number(diff[0]));
          }
        },
        [userRoles, userId, services]
      );
      return /* @__PURE__ */ jsx(Card, { children: /* @__PURE__ */ jsx(
        ListBox,
        {
          disallowEmptySelection: false,
          selectionMode: "multiple",
          selectedKeys: selected,
          disabledKeys: disabled,
          onSelectionChange: (keys) => handleSelectionChanged(Array.from(keys)),
          children: roles.map((role) => /* @__PURE__ */ jsxs(ListBox.Item, { id: role.id.toString(), textValue: role.name, children: [
            /* @__PURE__ */ jsx(Label, { children: role.name }),
            /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
          ] }, role.id))
        }
      ) });
    },
    {
      name: "UserApiRolesController",
      monitors: (services) => [services.users.addUserRole, services.users.removeUserRole]
    }
  )
);
const columns$1 = [
  { propertyName: "enabled", name: "" },
  { propertyName: "resourceName", name: "Resource" },
  { propertyName: "operationName", name: "Operation" },
  { propertyName: "contextName", name: "Context" },
  { propertyName: "actions", name: "" }
];
const OverrideUserPoliciesList = ({ overrides, onDelete, onCreateOverride, isLoading }) => {
  const renderCell = useCallback(
    (row, columnKey) => {
      const cellValue = row[columnKey];
      switch (columnKey) {
        case "actions":
          return /* @__PURE__ */ jsx("div", { className: "flex gap-2 justify-end", children: /* @__PURE__ */ jsx(
            DeleteButton,
            {
              size: "sm",
              variant: "light",
              radius: "full",
              className: "min-w-8 max-w-8",
              isIconOnly: true,
              onPress: () => onDelete(row)
            }
          ) });
        case "enabled":
          return cellValue ? /* @__PURE__ */ jsx("i", { className: "ri-checkbox-circle-fill text-success" }) : /* @__PURE__ */ jsx("i", { className: "ri-close-circle-fill text-danger" });
        default:
          return cellValue;
      }
    },
    [onDelete]
  );
  return /* @__PURE__ */ jsxs(Table, { children: [
    /* @__PURE__ */ jsxs("div", { className: "flex flex-row items-center justify-end gap-2 p-2", children: [
      isLoading && /* @__PURE__ */ jsx(Spinner, { color: "accent" }),
      /* @__PURE__ */ jsxs(Tooltip, { delay: 0, children: [
        /* @__PURE__ */ jsx(NewButton, { size: "sm", onPress: onCreateOverride, label: "", iconOnly: true }),
        /* @__PURE__ */ jsxs(Tooltip.Content, { showArrow: true, children: [
          /* @__PURE__ */ jsx(Tooltip.Arrow, {}),
          "Add Policy Override"
        ] })
      ] })
    ] }),
    /* @__PURE__ */ jsx(Table.ScrollContainer, { children: /* @__PURE__ */ jsxs(Table.Content, { "aria-label": "User policy overrided", children: [
      /* @__PURE__ */ jsx(Table.Header, { columns: columns$1, children: (column) => /* @__PURE__ */ jsx(Table.Column, { isRowHeader: true, id: column.propertyName, children: column.name }, column.propertyName) }),
      /* @__PURE__ */ jsx(Table.Body, { items: overrides, children: (override) => /* @__PURE__ */ jsx(Table.Row, { id: override.id, children: columns$1.map((column) => /* @__PURE__ */ jsx(Table.Cell, { children: renderCell(override, column.propertyName) }, column.propertyName)) }, override.id) })
    ] }) })
  ] });
};
const ApiRegistrationOverrideController = reactive.component(
  ({ services, data, monitors, userId, onCreateOverride }) => {
    return /* @__PURE__ */ jsx(
      OverrideUserPoliciesList,
      {
        overrides: data.overrides,
        isLoading: monitors.getUserPolicyOverrides.executing,
        onDelete: (item) => services.users.deleteUserPolicyOverride(userId, item.id),
        onCreateOverride
      }
    );
  },
  {
    name: "ApiRegistrationOverrideController",
    sync: (services, { userId, apiRegistrationId }) => [
      {
        execute: () => services.users.getUserPolicyOverrides(userId, apiRegistrationId),
        deps: [userId, apiRegistrationId]
      }
    ],
    queries: ({ userId, apiRegistrationId }) => [
      {
        name: "overrides",
        execute: (db) => db.userOverridePolicies.where({ userId, apiRegistrationId }).toArray(),
        deps: [userId, apiRegistrationId]
      }
    ],
    monitors: (services) => [services.users.getUserPolicyOverrides]
  }
);
const columns = [
  { propertyName: "enabled", name: "" },
  { propertyName: "resourceKey", name: "Resource" },
  { propertyName: "operationKey", name: "Operation" },
  { propertyName: "contextKey", name: "Context" }
];
const EffectivePoliciesList = ({ policies }) => {
  const renderCell = useCallback((row, columnKey) => {
    const cellValue = row[columnKey];
    switch (columnKey) {
      case "enabled":
        return cellValue ? /* @__PURE__ */ jsx("i", { className: "ri-checkbox-circle-fill text-success" }) : /* @__PURE__ */ jsx("i", { className: "ri-close-circle-fill text-danger" });
      default:
        return cellValue;
    }
  }, []);
  return /* @__PURE__ */ jsx(Table, { children: /* @__PURE__ */ jsx(Table.ScrollContainer, { children: /* @__PURE__ */ jsxs(Table.Content, { "aria-label": "Effective policies list", children: [
    /* @__PURE__ */ jsx(Table.Header, { columns, children: (column) => /* @__PURE__ */ jsx(Table.Column, { isRowHeader: true, id: column.propertyName, children: column.name }, column.propertyName) }),
    /* @__PURE__ */ jsx(Table.Body, { items: policies, children: (policy) => /* @__PURE__ */ jsx(Table.Row, { id: policy.id, children: columns.map((column) => /* @__PURE__ */ jsx(Table.Cell, { children: renderCell(policy, column.propertyName) }, column.propertyName)) }, policy.id) })
  ] }) }) });
};
const UserAppPermissionsListController = reactive.component(
  ({ data }) => {
    const { policies } = data;
    return /* @__PURE__ */ jsx(EffectivePoliciesList, { policies });
  },
  {
    name: "UserAppPermissionsListController",
    sync: (services, { userId, apiRegistrationId }) => [
      {
        execute: () => services.users.getUserEffectivePolicies(userId, apiRegistrationId),
        bypass: (db) => db.userEffectivePolicies.where({ userId, apiRegistrationId }).count(),
        deps: [userId, apiRegistrationId]
      }
    ],
    queries: ({ userId, apiRegistrationId }) => [
      {
        name: "policies",
        execute: (db) => db.userEffectivePolicies.where({ userId, apiRegistrationId }).toArray(),
        deps: [userId, apiRegistrationId]
      }
    ]
  }
);
const UserAppPermissionSettingsView = ({ userId, apiRegistrationId }) => {
  userId = Number(userId);
  return /* @__PURE__ */ jsx("div", { className: "flex flex-col", children: /* @__PURE__ */ jsx("div", { className: "mb-2", children: /* @__PURE__ */ jsx(UserAppPermissionsListController, { userId, apiRegistrationId }) }) });
};
const ApiRegistrationsList = ({
  userId,
  applicationId,
  apiRegistrations = [],
  onCreateOverride
}) => {
  return /* @__PURE__ */ jsx(Accordion, { variant: "default", className: "flex flex-col gap-2", children: apiRegistrations.map((apiRegistration) => /* @__PURE__ */ jsxs(
    Accordion.Item,
    {
      id: `${apiRegistration.id}`,
      "aria-label": apiRegistration.name,
      children: [
        /* @__PURE__ */ jsx(Accordion.Heading, { children: /* @__PURE__ */ jsxs(Accordion.Trigger, { children: [
          /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap2", children: [
            /* @__PURE__ */ jsx("p", { children: apiRegistration.name }),
            /* @__PURE__ */ jsx("p", { className: "italic text-xs", children: apiRegistration.description })
          ] }),
          /* @__PURE__ */ jsx(Accordion.Indicator, {})
        ] }) }),
        /* @__PURE__ */ jsx(Accordion.Panel, { children: /* @__PURE__ */ jsx(Accordion.Body, { children: /* @__PURE__ */ jsxs(Tabs, { variant: "secondary", children: [
          /* @__PURE__ */ jsx(Tabs.ListContainer, { children: /* @__PURE__ */ jsxs(Tabs.List, { "aria-label": "options", children: [
            /* @__PURE__ */ jsxs(Tabs.Tab, { id: "roles", children: [
              /* @__PURE__ */ jsxs("div", { className: "flex items-baseline gap-1.5", children: [
                /* @__PURE__ */ jsx("i", { className: "ri-team-line" }),
                " ",
                /* @__PURE__ */ jsx("span", { children: "Roles" })
              ] }),
              /* @__PURE__ */ jsx(Tabs.Indicator, {})
            ] }),
            /* @__PURE__ */ jsxs(Tabs.Tab, { id: "overrides", children: [
              /* @__PURE__ */ jsxs("div", { className: "flex items-baseline gap-1.5", children: [
                /* @__PURE__ */ jsx("i", { className: "ri-swap-line" }),
                " ",
                /* @__PURE__ */ jsx("span", { children: "Overrides" })
              ] }),
              /* @__PURE__ */ jsx(Tabs.Indicator, {})
            ] }),
            /* @__PURE__ */ jsxs(Tabs.Tab, { id: "permissions", children: [
              /* @__PURE__ */ jsxs("div", { className: "flex items-baseline gap-1.5", children: [
                /* @__PURE__ */ jsx("i", { className: "ri-key-line" }),
                " ",
                /* @__PURE__ */ jsx("span", { children: "Effective Permissions" })
              ] }),
              /* @__PURE__ */ jsx(Tabs.Indicator, {})
            ] })
          ] }) }),
          /* @__PURE__ */ jsx(Tabs.Panel, { id: "roles", children: /* @__PURE__ */ jsx(UserApiRolesController, { userId, apiRegistrationId: apiRegistration.id }) }),
          /* @__PURE__ */ jsx(Tabs.Panel, { id: "overrides", children: /* @__PURE__ */ jsx(
            ApiRegistrationOverrideController,
            {
              userId,
              applicationId,
              apiRegistrationId: apiRegistration.id,
              onCreateOverride: () => onCreateOverride(apiRegistration.id)
            }
          ) }),
          /* @__PURE__ */ jsx(Tabs.Panel, { id: "permissions", children: /* @__PURE__ */ jsx(
            UserAppPermissionSettingsView,
            {
              userId,
              apiRegistrationId: apiRegistration.id
            }
          ) })
        ] }) }) })
      ]
    },
    apiRegistration.id
  )) });
};
const UserApplicationToggleController = withUser(
  reactive.component(
    ({ services, monitors, userId, applicationId, isEnabled }) => {
      const [enabled, setEnabled] = useState(isEnabled);
      useEffect(() => {
        setEnabled(isEnabled);
      }, [isEnabled]);
      return /* @__PURE__ */ jsx(
        Toggle,
        {
          name: "enabled",
          isSelected: enabled,
          isLoading: monitors.addUserApplication.executing || monitors.removeUserApplication.executing,
          onChange: (isSelected) => {
            setEnabled(isSelected);
            if (isSelected) services.users.addUserApplication(userId, applicationId);
            else services.users.removeUserApplication(userId, applicationId);
          },
          children: "Enabled"
        }
      );
    },
    {
      name: "UserApplicationToggleController",
      monitors: (services) => [
        services.users.addUserApplication,
        services.users.removeUserApplication
      ]
    }
  )
);
const OverrideForm = ({ apiRegistrationId, onChange }) => {
  const [resourceSelected, setResourceSelected] = useState();
  return /* @__PURE__ */ jsxs(
    Uniformly,
    {
      className: "flex flex-col gap-y-2 w-2/3 mx-auto",
      name: "overrideForm",
      onChange: (form) => {
        onChange({
          ...form,
          data: {
            ...form.data,
            enabled: form.data.enabled !== "" ? form.data.enabled : false
          }
        });
        setResourceSelected(form.data.resourceId);
      },
      children: [
        /* @__PURE__ */ jsx(
          ResourcesLookup,
          {
            type: "number",
            name: "resourceId",
            label: "Resource",
            isRequired: true,
            validation: (x) => x.number().required("Resource is required"),
            apiRegistrationId,
            size: "sm"
          }
        ),
        /* @__PURE__ */ jsx(
          OperationsLookup,
          {
            type: "number",
            name: "operationId",
            label: "Operation",
            isRequired: true,
            validation: (x) => x.number().required("Operation is required"),
            resourceId: resourceSelected,
            forceRequest: !!resourceSelected,
            size: "sm"
          }
        ),
        /* @__PURE__ */ jsx(
          ContextsLookup,
          {
            type: "number",
            name: "contextId",
            label: "Context",
            isRequired: true,
            validation: (x) => x.number().required("Context is required"),
            forceRequest: true
          }
        ),
        /* @__PURE__ */ jsx(SwitchField, { name: "enabled", children: "Enabled" })
      ]
    }
  );
};
const AppResourceOverrideController = reactive.component(
  ({ services, monitors, userId, item: apiRegistrationId, onSuccess }) => {
    const [form, setForm] = useState({});
    useSuccessReactor(services.users.createUserPolicyOverride, (payload) => {
      onSuccess && onSuccess(payload);
    });
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(OverrideForm, { apiRegistrationId, onChange: setForm }),
      /* @__PURE__ */ jsx("div", { className: "text-end pb-2", children: /* @__PURE__ */ jsx(
        SaveButton,
        {
          size: "sm",
          isDisabled: !form.isValid,
          onPress: () => services.users.createUserPolicyOverride(userId, apiRegistrationId, {
            userId,
            ...form.data
          }),
          isLoading: monitors.createUserPolicyOverride.executing
        }
      ) })
    ] });
  },
  {
    name: "AppResourceOverrideController",
    monitors: (services) => [services.users.createUserPolicyOverride]
  }
);
AppResourceOverrideController.Modal = withModalController(({ onSuccess, ...props }) => /* @__PURE__ */ jsx(AppResourceOverrideController, { onSuccess, ...props }));
const _UserApplicationSettingsView = ({ userId, applicationId }) => {
  const { application } = useApplicationContext();
  const { userApplications } = useUserContext();
  const applicationEnabled = userApplications.some((x) => x.id === applicationId);
  const [createModal, CreateOverrideModal] = useModalController(
    AppResourceOverrideController.Modal,
    {
      title: "Add Override",
      isOpen: false
    }
  );
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
    /* @__PURE__ */ jsx(CreateOverrideModal, { userId, applicationId }),
    /* @__PURE__ */ jsx("div", { className: "flex justify-between items-center", children: /* @__PURE__ */ jsx(
      UserApplicationToggleController,
      {
        userId,
        applicationId,
        isEnabled: applicationEnabled
      }
    ) }),
    applicationEnabled && /* @__PURE__ */ jsx(
      ApiRegistrationsList,
      {
        userId,
        applicationId,
        apiRegistrations: application.apiRegistrations,
        onCreateOverride: (id) => createModal.open(id)
      }
    )
  ] });
};
const UserApplicationSettingsView = ({ userId, applicationId }) => {
  userId = Number(userId);
  applicationId = Number(applicationId);
  return /* @__PURE__ */ jsx(ApplicationProvider, { applicationId, children: /* @__PURE__ */ jsx(UserApplicationProvider, { applicationId, userId, children: /* @__PURE__ */ jsx(_UserApplicationSettingsView, { applicationId, userId }) }) });
};
const filters = new Filters([
  new FilterConstant({
    name: "UserId",
    title: "User ID",
    defaultValue: 0
  }),
  new FilterInput({
    name: "IpAddress",
    title: "IP Address",
    defaultValue: ""
  }),
  new FilterToggle({
    name: "IsSuccess",
    title: "Login Successful",
    defaultValue: false
  }),
  new FilterDateRange({
    name: "AuthenticatedOn",
    title: "Authenticated On"
  })
]);
const authHistoryColumns = [
  { propertyName: "isSuccess", name: "", isSortable: true, minWidth: 48 },
  {
    propertyName: "authenticatedOn",
    name: "Authenticated On",
    isSortable: true,
    isResizable: true,
    minWidth: 160
  },
  {
    propertyName: "userName",
    name: "User Name",
    isSortable: true,
    isResizable: true,
    minWidth: 120
  },
  {
    propertyName: "ipAddress",
    name: "Ip Address",
    isSortable: true,
    isResizable: true,
    minWidth: 120
  }
];
const UserAuthHistoryList = ({ authHistory = [] }) => {
  const renderCell = useCallback((row, columnKey) => {
    const cellValue = row[columnKey];
    switch (columnKey) {
      case "authenticatedOn":
        return cellValue ? parseAndFormatDateTime(cellValue) : "";
      case "isSuccess":
        return cellValue ? /* @__PURE__ */ jsx("span", { className: "text-success", children: /* @__PURE__ */ jsx("i", { className: "ri-checkbox-circle-fill" }) }) : /* @__PURE__ */ jsx("span", { className: "text-danger", children: /* @__PURE__ */ jsx("i", { className: "ri-forbid-fill" }) });
      default:
        return cellValue || "";
    }
  }, []);
  const columns2 = useMemo(
    () => authHistoryColumns.map((column) => ({
      id: column.propertyName,
      header: column.name,
      // `authenticatedOn` is the row's accessible identifier; sorting flows
      // through the request broker via each column's `allowsSorting` + matching `id`.
      isRowHeader: column.propertyName === "authenticatedOn",
      allowsSorting: column.isSortable,
      allowsResizing: column.isResizable,
      minWidth: column.minWidth,
      cell: (row) => renderCell(row, column.propertyName)
    })),
    [renderCell]
  );
  return /* @__PURE__ */ jsx(
    RequestBrokerDataGrid,
    {
      "aria-label": "User authentication history table",
      chipScope: "global",
      columns: columns2,
      data: authHistory,
      getRowId: (row) => row.id,
      allowsColumnResize: true
    }
  );
};
const UserAuthHistoryController = reactive.component(
  ({ userId, data, services, monitors, db }) => {
    const { authHistory } = data;
    const handleExecute = useCallback((action, metadata) => {
      action(metadata);
    }, []);
    return /* @__PURE__ */ jsx(
      RequestBroker,
      {
        actionKey: "queryAuthenticationHistory",
        action: services.loginHistory.queryAuthenticationHistory,
        filters,
        sorting: new Sorting(
          [{ title: "Authenticated On", value: "authenticatedOn" }],
          "authenticatedOn",
          "desc"
        ),
        staticFilters: {
          handler: (filters2) => {
            filters2.setValue("UserId", userId, true);
          },
          deps: [userId]
        },
        isExecuting: monitors.queryAuthenticationHistory.executing,
        onExecute: handleExecute,
        children: /* @__PURE__ */ jsx(UserAuthHistoryList, { authHistory })
      }
    );
  },
  {
    name: "UserAuthHistoryController",
    queries: ({ userId }, services) => [
      {
        name: "authHistory",
        execute: (db) => db.withRequestBroker(
          services.loginHistory.queryAuthenticationHistory.action,
          () => db.userAuthHistory.where({ userId }),
          { sorting: true }
        )
      }
    ],
    monitors: (services) => [services.loginHistory.queryAuthenticationHistory]
  }
);
const UserAuthHistoryView = ({ userId }) => {
  userId = Number(userId);
  return /* @__PURE__ */ jsx(UserAuthHistoryController, { userId });
};
const UserCredentialsForm = ({ username, onChange }) => /* @__PURE__ */ jsxs(
  Uniformly,
  {
    className: "flex flex-col flex-grow gap-2",
    name: "userCredentialsForm",
    onChange,
    children: [
      /* @__PURE__ */ jsx(TextFieldWithCopy, { variant: "secondary", isReadOnly: true, name: "username", label: "User Name (Read-only)", value: username }),
      /* @__PURE__ */ jsx(
        PasswordConfirmField,
        {
          name: "password",
          validation: (x) => x.string().required("Password required")
        }
      )
    ]
  }
);
const UserCredentialsUpdateController = withUser(
  reactive.component(
    ({ services, monitors, credentials, user, onSuccess }) => {
      const [form, setForm] = useState({});
      useSuccessReactor(services.users.updateCredentials, (payload) => {
        onSuccess && onSuccess(payload);
      });
      return /* @__PURE__ */ jsxs(FlatCard, { children: [
        /* @__PURE__ */ jsx(FlatCard.Header, { className: "text-lg", children: /* @__PURE__ */ jsx(FlatCard.Title, { children: "Security" }) }),
        /* @__PURE__ */ jsx(FlatCard.Main, { className: "flex gap-4", children: /* @__PURE__ */ jsx(UserCredentialsForm, { username: user.username, onChange: setForm }) }),
        /* @__PURE__ */ jsxs(FlatCard.Footer, { className: "flex flex-row justify-between gap-2", children: [
          /* @__PURE__ */ jsx(
            Button,
            {
              size: "sm",
              variant: "primary",
              onPress: () => services.users.userOperation("setup-password", user.id),
              isPending: monitors.userOperation.executing,
              children: "Resend Password Setup Email"
            }
          ),
          /* @__PURE__ */ jsx(
            SaveButton,
            {
              size: "sm",
              isDisabled: !form.isValid,
              onPress: () => services.users.updateCredentials(user.id, {
                id: user.credentialsId,
                userId: user.id,
                username: user.username,
                password: form.data.password
              }),
              label: "Update Password",
              isLoading: monitors.updateCredentials.executing
            }
          )
        ] })
      ] });
    },
    {
      name: "UserCredentialsUpdateController",
      monitors: (services) => [services.users.updateCredentials, services.users.userOperation]
    }
  )
);
const UserSecurityView = ({ userId }) => {
  userId = Number(userId);
  const navigate = useNavigate();
  return /* @__PURE__ */ jsx(
    UserCredentialsUpdateController,
    {
      userId,
      onDelete: () => navigate(RoutePaths().users.list())
    }
  );
};
const TokenHeader = ({ token }) => {
  let label = `${parseAndFormatDate(token.validAfterUtc)} - ${token.validUntilUtc ? parseAndFormatDate(token.validUntilUtc) : "No Expiration"}`;
  return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
    token.validUntilUtc && fromISO(token.validUntilUtc) < DateTime.now() && /* @__PURE__ */ jsx(Chip, { size: "sm", color: "danger", children: "Expired" }),
    /* @__PURE__ */ jsx("p", { children: label }),
    token.validUntilUtc && token.validUntilUtc > DateTime.now() && /* @__PURE__ */ jsx("p", { className: "italic", children: `(${Math.round(fromISO(token.validUntilUtc).diff(DateTime.now(), "days").values.days)} days)` })
  ] });
};
const TokenUpdateForm = withUser(
  reactive.component(
    ({ user, token, services, monitors }) => {
      const [form, setForm] = useState({});
      const [validUntil, setValidUntil] = useState(token.validUntilUtc);
      return /* @__PURE__ */ jsx(
        Uniformly,
        {
          name: "token",
          onChange: (form2) => {
            form2.data.id = token.id;
            form2.data.userId = user.id;
            form2.data.token = token.token;
            form2.data.validUntilUtc = isEmpty(form2.data.validUntilUtc) ? null : form2.data.validUntilUtc;
            setForm(form2);
          },
          deps: [validUntil],
          children: /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
            /* @__PURE__ */ jsx(TextFieldWithCopy, { type: "password", value: token.token }),
            /* @__PURE__ */ jsx(
              DateTimeField,
              {
                name: "validAfterUtc",
                size: "sm",
                value: token.validAfterUtc,
                label: "Valid From",
                validation: (x) => x.string().required("Valid after date is required")
              }
            ),
            /* @__PURE__ */ jsxs("div", { className: "inline-flex items-center gap-2", children: [
              /* @__PURE__ */ jsx(
                DateTimeField,
                {
                  name: "validUntilUtc",
                  size: "sm",
                  value: validUntil,
                  label: "Valid Until"
                }
              ),
              /* @__PURE__ */ jsx(Link, { onPress: () => setValidUntil(null), className: "cursor-pointer", children: /* @__PURE__ */ jsx("i", { className: "ri-eraser-line" }) })
            ] }),
            /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
              /* @__PURE__ */ jsx(
                DeleteButton,
                {
                  size: "sm",
                  isIconOnly: true,
                  onPress: () => services.users.deleteApiToken(user.id, token.id),
                  isLoading: monitors.deleteApiToken.executing
                }
              ),
              /* @__PURE__ */ jsx(
                SaveButton,
                {
                  size: "sm",
                  isDisabled: !form.isValid,
                  onPress: () => services.users.updateApiToken(user.id, token.id, form.data),
                  isLoading: monitors.updateApiToken.executing
                }
              )
            ] })
          ] })
        }
      );
    },
    {
      name: "TokenForm",
      monitors: (services) => [services.users.updateApiToken, services.users.deleteApiToken]
    }
  )
);
const TokenCreateForm = withUser(
  reactive.component(
    ({ user, services, monitors }) => {
      const [form, setForm] = useState({});
      return /* @__PURE__ */ jsx(
        Uniformly,
        {
          name: "token",
          onChange: (form2) => {
            form2.data.userId = user.id;
            form2.data.validUntilUtc = isEmpty(form2.data.validUntilUtc) ? null : form2.data.validUntilUtc;
            setForm(form2);
          },
          children: /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
            /* @__PURE__ */ jsx(
              DateTimeField,
              {
                name: "validAfterUtc",
                size: "sm",
                value: toISO(DateTime.now()),
                label: "Valid From",
                validation: (x) => x.string().required("Valid after date is required")
              }
            ),
            /* @__PURE__ */ jsx(DateTimeField, { name: "validUntilUtc", size: "sm", value: "", label: "Valid Until" }),
            /* @__PURE__ */ jsx("div", { className: "flex items-center justify-end", children: /* @__PURE__ */ jsx(
              SaveButton,
              {
                size: "sm",
                isDisabled: !form.isValid,
                onPress: () => services.users.createApiToken(user.id, form.data),
                isLoading: monitors.createApiToken.executing
              }
            ) })
          ] })
        }
      );
    },
    {
      name: "TokenCreateForm",
      monitors: (services) => [services.users.createApiToken]
    }
  )
);
const ApiTokens = ({ tokens }) => {
  return /* @__PURE__ */ jsxs(Accordion, { variant: "surface", className: "flex flex-col gap-2", children: [
    tokens.map((item) => {
      return /* @__PURE__ */ jsxs(Accordion.Item, { id: `${item.id}`, "aria-label": `${item.id}`, children: [
        /* @__PURE__ */ jsx(Accordion.Heading, { children: /* @__PURE__ */ jsxs(Accordion.Trigger, { children: [
          /* @__PURE__ */ jsx(TokenHeader, { token: item }),
          /* @__PURE__ */ jsx(Accordion.Indicator, {})
        ] }) }),
        /* @__PURE__ */ jsx(Accordion.Panel, { children: /* @__PURE__ */ jsx(Accordion.Body, { children: /* @__PURE__ */ jsx(TokenUpdateForm, { token: item }) }) })
      ] }, item.id);
    }),
    /* @__PURE__ */ jsxs(Accordion.Item, { id: "new", "aria-label": "new-token", children: [
      /* @__PURE__ */ jsx(Accordion.Heading, { children: /* @__PURE__ */ jsxs(Accordion.Trigger, { children: [
        "New Token",
        /* @__PURE__ */ jsx(Accordion.Indicator, {})
      ] }) }),
      /* @__PURE__ */ jsx(Accordion.Panel, { children: /* @__PURE__ */ jsx(Accordion.Body, { children: /* @__PURE__ */ jsx(TokenCreateForm, {}) }) })
    ] }, "new")
  ] });
};
const UserTokensController = withUser(
  reactive.component(
    ({ user, services, data, monitors }) => {
      return /* @__PURE__ */ jsxs("div", { className: "flex flex-col w-full gap-4", children: [
        /* @__PURE__ */ jsx("div", { className: "flex items-center justify-end", children: /* @__PURE__ */ jsx(
          ReloadButton,
          {
            size: "sm",
            isLoading: monitors.getApiTokens.executing,
            onPress: () => services.users.getApiTokens(user.id)
          }
        ) }),
        /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsx(ApiTokens, { tokens: data.tokens }) })
      ] });
    },
    {
      name: "UserTokensController",
      sync: (services, { user }) => [
        {
          execute: () => services.users.getApiTokens(user.id),
          deps: [user.id]
        }
      ],
      queries: ({ user }) => [
        {
          name: "tokens",
          execute: (db) => db.apiTokens.where({ userId: user.id }).toArray(),
          deps: [user.id]
        }
      ],
      monitors: (services) => [services.users.getApiTokens]
    }
  )
);
const UserTokensView = () => {
  return /* @__PURE__ */ jsx(Card, { children: /* @__PURE__ */ jsx(Card.Content, { children: /* @__PURE__ */ jsx(UserTokensController, {}) }) });
};
const UserRouter = () => /* @__PURE__ */ jsxs(Routes, { children: [
  /* @__PURE__ */ jsx(Route, { path: "/", element: /* @__PURE__ */ jsx(UsersListView, {}), children: /* @__PURE__ */ jsx(Route, { path: "create", element: /* @__PURE__ */ jsx(UserCreateView, {}) }) }),
  /* @__PURE__ */ jsxs(Route, { path: ":userId", element: /* @__PURE__ */ jsx(WithRouteParams, { element: UserDetailsView }), children: [
    /* @__PURE__ */ jsx(Route, { path: "details", element: /* @__PURE__ */ jsx(WithRouteParams, { element: UserUpdateView }) }),
    /* @__PURE__ */ jsx(Route, { path: "security", element: /* @__PURE__ */ jsx(WithRouteParams, { element: UserSecurityView }) }),
    /* @__PURE__ */ jsx(Route, { path: "applications", element: /* @__PURE__ */ jsx(WithRouteParams, { element: UserApplicationsView }), children: /* @__PURE__ */ jsx(
      Route,
      {
        path: ":applicationId",
        element: /* @__PURE__ */ jsx(WithRouteParams, { element: UserApplicationSettingsView })
      }
    ) }),
    /* @__PURE__ */ jsx(Route, { path: "tokens", element: /* @__PURE__ */ jsx(UserTokensView, {}) }),
    /* @__PURE__ */ jsx(Route, { path: "auth-history", element: /* @__PURE__ */ jsx(WithRouteParams, { element: UserAuthHistoryView }) })
  ] })
] });
const ApplicationsListView = reactive.component(
  ({ services, monitors }) => {
    const navigate = useNavigate();
    const getCountByEnvironment = (env) => RequestMetadata.get(serviceMap(services)[env].action)?.paging?.count ?? 0;
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx("div", { className: "flex flex-row justify-between mb-4", children: /* @__PURE__ */ jsxs(Breadcrumbs, { className: "content-center", children: [
        /* @__PURE__ */ jsx(BreadcrumbsItem, { isDisabled: true, children: "Auth" }),
        /* @__PURE__ */ jsx(BreadcrumbsItem, { children: "Applications" })
      ] }) }),
      /* @__PURE__ */ jsxs(Tabs, { variant: "secondary", children: [
        /* @__PURE__ */ jsx(Tabs.ListContainer, { children: /* @__PURE__ */ jsx(Tabs.List, { "aria-label": "Applications", children: Object.entries(EnvironmentType).map(([key, value]) => /* @__PURE__ */ jsxs(Tabs.Tab, { id: key, children: [
          /* @__PURE__ */ jsx(
            TabTitle,
            {
              title: key,
              count: getCountByEnvironment(value),
              isLoading: monitorsMap(monitors)[value].executing
            }
          ),
          /* @__PURE__ */ jsx(Tabs.Indicator, {})
        ] }, key)) }) }),
        Object.entries(EnvironmentType).map(([key, value]) => /* @__PURE__ */ jsx(Tabs.Panel, { id: key, className: "flex flex-col flex-1 h-0", children: /* @__PURE__ */ jsx(
          ApplicationsListController,
          {
            environmentType: value,
            onSelect: (item) => navigate(RoutePaths().applications.details(item.id))
          }
        ) }, key))
      ] }),
      /* @__PURE__ */ jsx(Outlet, {})
    ] });
  },
  {
    name: "ApplicationsListView",
    monitors: (services) => [
      services.applications.getDevelopmentApplications,
      services.applications.getIntegrationApplications,
      services.applications.getStagingApplications,
      services.applications.getProductionApplications
    ]
  }
);
const TabTitle = ({ count, title, isLoading }) => {
  return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1", children: [
    /* @__PURE__ */ jsx("span", { children: title }),
    isLoading ? /* @__PURE__ */ jsx(Spinner, { size: "sm" }) : /* @__PURE__ */ jsx(Chip, { size: "sm", color: "accent", children: count })
  ] });
};
const ApplicationNameField = reactive.component(
  ({ applicationId, environment, services, monitors, value, ...rest }) => {
    const [initialEnvironment] = useState(environment);
    const ref = useRef();
    const handleValidate = useCallback(
      (name) => {
        services.applications.applicationOperation("validate-application-name", {
          name,
          environment
        });
      },
      [services, environment]
    );
    useEffect(() => {
      if (environment != initialEnvironment && !isEmpty(value)) {
        handleValidate(value);
      }
    }, [environment, initialEnvironment, value, handleValidate]);
    useSuccessReactor(services.applications.applicationOperation, (response) => {
      if (response.exists && response.applicationId !== applicationId) {
        ref.current.setIsValid(
          false,
          `${response.applicationName} is already used, please choose a different name.`
        );
      } else {
        ref.current.setIsValid(true);
      }
    });
    return /* @__PURE__ */ jsx(
      NameValidationField,
      {
        ref,
        value,
        onValidate: handleValidate,
        isLoading: monitors.applicationOperation.executing,
        isDisabled: isEmpty(environment) || monitors.applicationOperation.executing,
        ...rest
      }
    );
  },
  {
    name: "ApplicationNameField",
    monitors: (services) => [services.applications.applicationOperation]
  }
);
const ApplicationForm = ({ application = {}, onChange }) => {
  const apis = useMemo(
    () => (application.apiRegistrations || []).map((x) => x.id),
    [application.apiRegistrations]
  );
  const [environment, setEnvironment] = useState(application.environment);
  return /* @__PURE__ */ jsxs(
    Uniformly,
    {
      className: "flex flex-col flex-grow gap-2",
      name: "applicationForm",
      onChange: (form) => {
        setEnvironment(form.data.environment);
        onChange(form);
      },
      children: [
        /* @__PURE__ */ jsx(ImageInputField, { name: "logo", label: "Logo", defaultValue: application.logoUri, hasDropzone: true, children: /* @__PURE__ */ jsx(ImageInputField.Preview, { children: (image) => /* @__PURE__ */ jsx("div", { className: "flex items-center justify-center", children: /* @__PURE__ */ jsx("img", { src: image, alt: "Logo", className: "w-3/4" }) }) }) }),
        /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
          /* @__PURE__ */ jsx(
            EnvironmentTypeLookup,
            {
              name: "environment",
              label: "Environment Type",
              placeholder: "Select Environment Type",
              isRequired: true,
              value: application.environment,
              validation: (x) => x.string().required("Environment Type is required")
            }
          ),
          /* @__PURE__ */ jsx(
            ApplicationNameField,
            {
              applicationId: application.id,
              name: "name",
              label: "Name",
              placeholder: "Application Name",
              isRequired: true,
              value: application.name,
              environment,
              validation: (x) => x.string().max(100).required("Name is required")
            }
          ),
          /* @__PURE__ */ jsx(
            TextField$1,
            {
              name: "description",
              label: "Description",
              placeholder: "Application Description",
              isRequired: true,
              value: application.description,
              validation: (x) => x.string().max(1e3).required("Description is required")
            }
          )
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
          /* @__PURE__ */ jsx("h3", { children: "Configuration" }),
          /* @__PURE__ */ jsx(
            UrlField,
            {
              name: "applicationUri",
              label: "Url",
              placeholder: "Application Url",
              value: application.applicationUri
            }
          ),
          /* @__PURE__ */ jsx(
            ApiRegistrationsLookup,
            {
              name: "apiRegistrations",
              label: "APIs",
              type: "number",
              placeholder: "Select APIs",
              isRequired: true,
              value: apis,
              selectionMode: "multiple",
              isMultiline: true,
              validation: (x) => x.array().min(1, "At least one Api is required")
            }
          )
        ] })
      ]
    }
  );
};
const ApplicationContext = createContext();
const ApplicationProvider = reactive.component(
  ({ data, monitors, children }) => {
    if (monitors.getApplication.complete && isEmpty(data.application))
      return /* @__PURE__ */ jsx(NotFound, { resourceType: "Application" });
    if (monitors.getApplication.executing) return /* @__PURE__ */ jsx(Loading, {});
    return monitors.getApplication.complete && data.application && /* @__PURE__ */ jsx(ApplicationContext.Provider, { value: { application: data.application }, children });
  },
  {
    name: "ApplicationProvider",
    sync: (services, { applicationId }) => [
      {
        execute: () => services.applications.getApplication(applicationId),
        deps: [applicationId]
      }
    ],
    queries: ({ applicationId }) => [
      {
        name: "application",
        execute: (db) => db.applications.get(applicationId),
        defaultValue: {},
        deps: [applicationId]
      }
    ],
    monitors: (services) => [services.applications.getApplication]
  }
);
const withApplication = (WrappedComponent) => {
  const Wrapper = (props) => {
    const context = React.useContext(ApplicationContext);
    if (isEmpty(context))
      return /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx("p", { children: /* @__PURE__ */ jsx("strong", { children: "Error" }) }),
        /* @__PURE__ */ jsxs("p", { children: [
          "A component using ",
          /* @__PURE__ */ jsx("strong", { children: "withApplication" }),
          " must be wrapped with",
          " ",
          /* @__PURE__ */ jsx("strong", { children: "ApplicationProvider" }),
          "."
        ] })
      ] });
    return /* @__PURE__ */ jsx(WrappedComponent, { ...props, ...context });
  };
  return Wrapper;
};
const ApplicationUpdateController = withApplication(
  reactive.component(
    ({ services, monitors, application }) => {
      const [form, setForm] = useState({});
      const handleUpdateApplication = () => {
        services.applications.updateApplication(application.id, {
          ...form.data,
          key: application.key,
          id: application.id
        });
      };
      return /* @__PURE__ */ jsxs(FlatCard, { children: [
        /* @__PURE__ */ jsx(FlatCard.Main, { children: /* @__PURE__ */ jsx(ApplicationForm, { application, onChange: setForm }) }),
        /* @__PURE__ */ jsxs(FlatCard.Footer, { className: "flex flex-row items-center justify-between gap-x-2", children: [
          /* @__PURE__ */ jsx(
            DeleteButton,
            {
              size: "sm",
              isLoading: monitors.deleteApplication.executing,
              onPress: () => services.applications.deleteApplication(application.id)
            }
          ),
          /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
            /* @__PURE__ */ jsx(ErrorListChip, { errors: form.errors }),
            /* @__PURE__ */ jsx(
              SaveButton,
              {
                size: "sm",
                isDisabled: !form.isValid,
                onPress: handleUpdateApplication,
                isLoading: monitors.updateApplication.executing
              }
            )
          ] })
        ] })
      ] });
    },
    {
      name: "ApplicationUpdateController",
      monitors: (services) => [
        services.applications.updateApplication,
        services.applications.deleteApplication
      ]
    }
  )
);
const ApplicationUpdateView = () => {
  return /* @__PURE__ */ jsx(ApplicationUpdateController, {});
};
const ApplicationTabs = ({ applicationId }) => {
  const location = useLocation();
  const selectedKeys = ["details", "clients"].filter((x) => location.pathname.endsWith(x));
  if (isEmpty(selectedKeys)) {
    selectedKeys.push("details");
  }
  return /* @__PURE__ */ jsxs(
    ListBox,
    {
      "aria-label": "options",
      disallowEmptySelection: true,
      selectionMode: "single",
      selectedKeys,
      children: [
        /* @__PURE__ */ jsxs(
          ListBox.Item,
          {
            id: "details",
            textValue: "General",
            href: RoutePaths().applications.details(applicationId),
            "aria-label": "Details",
            children: [
              /* @__PURE__ */ jsx("i", { className: "ri-settings-3-line" }),
              /* @__PURE__ */ jsx(Label, { children: "General" })
            ]
          },
          "details"
        ),
        /* @__PURE__ */ jsxs(
          ListBox.Item,
          {
            id: "clients",
            textValue: "Clients",
            href: RoutePaths().applications.clients(applicationId),
            "aria-label": "Resources",
            children: [
              /* @__PURE__ */ jsx("i", { className: "ri-settings-line" }),
              /* @__PURE__ */ jsx(Label, { children: "Clients" })
            ]
          },
          "clients"
        )
      ]
    }
  );
};
const Layout = withApplication(({ application }) => {
  const navigate = useNavigate();
  useRecentView({
    title: application.name || `Application #${application.id}`,
    route: RoutePaths().applications.details(application.id)
  });
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("div", { className: "flex flex-row justify-between mb-4 items-center", children: /* @__PURE__ */ jsxs(Breadcrumbs, { children: [
      /* @__PURE__ */ jsx(BreadcrumbsItem, { isDisabled: true, children: "Auth" }),
      /* @__PURE__ */ jsx(BreadcrumbsItem, { onPress: () => navigate(RoutePaths().applications.list()), children: "Applications" }),
      /* @__PURE__ */ jsx(BreadcrumbsItem, { children: /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1", children: [
        application.name,
        " ",
        /* @__PURE__ */ jsx(EnvironmentChip, { environment: application.environment })
      ] }) })
    ] }) }),
    /* @__PURE__ */ jsx(SplitCard, { children: /* @__PURE__ */ jsxs(SplitCard.Body, { children: [
      /* @__PURE__ */ jsx(SplitCard.Sidebar, { children: /* @__PURE__ */ jsx(ApplicationTabs, { applicationId: application.id }) }),
      /* @__PURE__ */ jsx(SplitCard.Main, { children: /* @__PURE__ */ jsx(Outlet, {}) })
    ] }) })
  ] });
});
const ApplicationUpdateLayout = ({ applicationId }) => {
  applicationId = Number(applicationId);
  return /* @__PURE__ */ jsx(ApplicationProvider, { applicationId, children: /* @__PURE__ */ jsx(Layout, {}) });
};
const ApplicationCreate = ({ onChange }) => /* @__PURE__ */ jsx(ApplicationForm, { onChange });
ApplicationCreate.Drawer = withDrawerController(ApplicationCreate);
const ApplicationCreateView = reactive.component(
  ({ services, monitors }) => {
    const navigate = useNavigate();
    const [form, setForm] = useState({});
    const goToList = () => navigate(RoutePaths().applications.list());
    useSuccessReactor(services.applications.createApplication, goToList);
    return /* @__PURE__ */ jsx(
      ApplicationCreate.Drawer,
      {
        isOpen: true,
        size: "3xl",
        onChange: setForm,
        headerContent: () => "Create Application",
        footerContent: () => /* @__PURE__ */ jsxs("div", { className: "flex w-full items-center justify-end gap-x-2", children: [
          /* @__PURE__ */ jsx(ErrorListChip, { errors: form.errors }),
          /* @__PURE__ */ jsx(
            SaveButton,
            {
              size: "sm",
              color: "primary",
              isDisabled: !form.isValid,
              onPress: () => services.applications.createApplication(form.data),
              isLoading: monitors.createApplication.executing
            }
          )
        ] }),
        onClose: goToList
      }
    );
  },
  {
    name: "ApplicationCreateView",
    monitors: (services) => [services.applications.createApplication]
  }
);
const useApplicationContext = () => {
  return React.useContext(ApplicationContext);
};
const ApplicationClientsFilters = new Filters([
  new FilterConstant({
    name: "ApplicationId",
    title: "Application",
    defaultValue: 0
  })
]);
const ApplicationClientsController = withApplication(
  reactive.component(
    ({ application, data, services, monitors, db }) => {
      const navigate = useNavigate();
      const handleExecute = useCallback((action, metadata) => {
        action(metadata);
      }, []);
      return /* @__PURE__ */ jsx(
        RequestBroker,
        {
          actionKey: "getClientsByApplication",
          action: services.clients.getClientsByApplication,
          filters: ApplicationClientsFilters,
          sorting: new Sorting([{ title: "Name", value: "clientName" }], "clientName", "asc"),
          isExecuting: monitors.getClientsByApplication.executing,
          onExecute: handleExecute,
          staticFilters: {
            handler: (filters2) => filters2.setValue("ApplicationId", application.id, true),
            deps: [application.id]
          },
          skipInitial: () => db.clients.where({ applicationId: application.id }).count(),
          children: /* @__PURE__ */ jsx(
            ClientsList,
            {
              clients: data.clients,
              onSelect: (item) => navigate(RoutePaths().clients.details(item.id)),
              hideFilters: true,
              hideChips: true
            }
          )
        }
      );
    },
    {
      name: "ApplicationClientsController",
      queries: ({ application }, services) => [
        {
          name: "clients",
          execute: (db) => db.withRequestBroker(
            services.clients.getClientsByApplication.action,
            () => db.clients.where({ applicationId: application.id }),
            { sorting: true }
          )
        }
      ],
      monitors: (services) => [services.clients.getClientsByApplication]
    }
  )
);
const ApplicationClientsView = () => {
  return /* @__PURE__ */ jsx(ApplicationClientsController, {});
};
const ApplicationRouter = () => /* @__PURE__ */ jsxs(Routes, { children: [
  /* @__PURE__ */ jsx(Route, { path: "/", element: /* @__PURE__ */ jsx(ApplicationsListView, {}), children: /* @__PURE__ */ jsx(Route, { path: "create", element: /* @__PURE__ */ jsx(ApplicationCreateView, {}) }) }),
  /* @__PURE__ */ jsxs(Route, { path: "/:applicationId", element: /* @__PURE__ */ jsx(WithRouteParams, { element: ApplicationUpdateLayout }), children: [
    /* @__PURE__ */ jsx(Route, { index: true, element: /* @__PURE__ */ jsx(WithRouteParams, { element: ApplicationUpdateView }) }),
    /* @__PURE__ */ jsx(Route, { path: "clients", element: /* @__PURE__ */ jsx(ApplicationClientsView, {}) })
  ] })
] });
const Router = () => /* @__PURE__ */ jsx(BaseLayout, { children: /* @__PURE__ */ jsxs(Routes, { children: [
  /* @__PURE__ */ jsx(
    Route,
    {
      path: "identity-server/*",
      element: /* @__PURE__ */ jsx(RequirePolicy, { resource: "identityserver", operation: "all", placeholder: /* @__PURE__ */ jsx(Forbidden, {}), children: /* @__PURE__ */ jsx(IdentityServerRouter, {}) })
    }
  ),
  /* @__PURE__ */ jsx(
    Route,
    {
      path: "users/*",
      element: /* @__PURE__ */ jsx(RequirePolicy, { resource: "user", operation: "all", placeholder: /* @__PURE__ */ jsx(Forbidden, {}), children: /* @__PURE__ */ jsx(UserRouter, {}) })
    }
  ),
  /* @__PURE__ */ jsxs(
    Route,
    {
      element: /* @__PURE__ */ jsx(RequirePolicy, { resource: "applications", operation: "all", placeholder: /* @__PURE__ */ jsx(Forbidden, {}), children: /* @__PURE__ */ jsx(Outlet, {}) }),
      children: [
        /* @__PURE__ */ jsx(Route, { path: "api-registrations/*", element: /* @__PURE__ */ jsx(ApiRegistrationRouter, {}) }),
        /* @__PURE__ */ jsx(Route, { path: "applications/*", element: /* @__PURE__ */ jsx(ApplicationRouter, {}) }),
        /* @__PURE__ */ jsx(Route, { path: "templates/*", element: /* @__PURE__ */ jsx(TemplatesRouter, {}) })
      ]
    }
  )
] }) });
function getAuthNavigation(runtime) {
  const user = runtime.user;
  const apiKey2 = runtime.config?.api?.apiKey ?? "";
  return {
    isVisible: user.hasApiAccess(apiKey2),
    items: [
      {
        title: "Identity Server",
        isVisible: user.hasPolicy(apiKey2, "identityserver", "all"),
        items: [
          {
            title: "Identity Resources",
            icon: "ri-tools-line",
            route: "/identity-server/identity-resources",
            isVisible: user.hasPolicy(apiKey2, "identityserver", "all")
          },
          {
            title: "API Resources",
            icon: "ri-rss-line",
            route: "/identity-server/api-resources",
            isVisible: user.hasPolicy(apiKey2, "identityserver", "all")
          },
          {
            title: "Clients",
            icon: "ri-settings-line",
            route: "/identity-server/clients",
            isVisible: user.hasPolicy(apiKey2, "identityserver", "all")
          }
        ]
      },
      {
        title: "Users",
        route: "/users",
        icon: "ri-group-line",
        isVisible: user.hasPolicy(apiKey2, "user", "all")
      },
      {
        title: "API's",
        route: "/api-registrations",
        icon: "ri-global-line",
        isVisible: user.hasPolicy(apiKey2, "applications", "all")
      },
      {
        title: "Applications",
        route: "/applications",
        icon: "ri-apps-line",
        isVisible: user.hasPolicy(apiKey2, "applications", "all")
      },
      {
        title: "Templates",
        route: "/templates",
        icon: "ri-file-copy-fill",
        isVisible: user.hasPolicy(apiKey2, "applications", "all")
      }
    ]
  };
}
function getAuthResources(runtime) {
  const user = runtime.user;
  const apiKey2 = runtime.config?.api?.apiKey ?? "";
  const items = [];
  if (user.hasRole(apiKey2, "Administrator")) {
    items.push({
      key: "application",
      label: "New Application",
      route: "/applications",
      description: "Create new application"
    });
    items.push({
      key: "api",
      label: "New API",
      route: "/api-registrations",
      description: "Create new API registration"
    });
  }
  if (user.hasPolicy(apiKey2, "user", "all")) {
    items.push({
      key: "user",
      label: "New User",
      route: "/users",
      description: "Create new user"
    });
    items.push({
      key: "template",
      label: "New Template",
      route: "/templates",
      description: "Create new template"
    });
  }
  if (user.hasRole(apiKey2, "Administrator")) {
    items.push({
      key: "identity-resource",
      label: "New Identity Resource",
      route: "/identity-server/identity-resources",
      description: "Create new identity resource"
    });
    items.push({
      key: "api-resource",
      label: "New API Resource",
      route: "/identity-server/api-resources",
      description: "Create new API resource"
    });
    items.push({
      key: "client",
      label: "New Client",
      route: "/identity-server/clients",
      description: "Create new client"
    });
  }
  return {
    items,
    handlers: ((navigate) => ({
      "application": () => navigate(RoutePaths().applications.create()),
      "api": () => navigate(RoutePaths().apis.create()),
      "user": () => navigate(RoutePaths().users.create()),
      "identity-resource": () => navigate(RoutePaths().identityResources.create()),
      "api-resource": () => navigate(RoutePaths().apiResources.create()),
      "client": () => navigate(RoutePaths().clients.create()),
      "template": () => navigate(RoutePaths().templates.create())
    }))
  };
}
let appRuntime = null;
const initAuthRuntime = (hostRuntime) => {
  appRuntime = hostRuntime.registerApp("auth", {
    description: {
      title: "Security",
      iconClass: "ri-shield-keyhole-line",
      defaultRoute: "/users",
      isVisible: (runtime) => runtime.user.hasApiAccess(runtime.config?.api?.apiKey ?? "")
    },
    routes: Router,
    navigation: getAuthNavigation,
    resources: getAuthResources,
    config: () => AuthConfig.current
  });
};
export {
  initAuthRuntime
};
//# sourceMappingURL=index.js.map
